/**
 * Cloud Database Service using Supabase
 */
import { supabase } from './supabaseClient';
import { normalizeAccountStatus } from '../utils/userLifecycle.js';
import { repairSiteTextRows } from '../utils/repairUtf8Mojibake.js';
import { ensureAuthSession, getClientAuthHints, SESSION_EXPIRED } from '../utils/authSession.js';
import {
    enqueueWordSave,
    peekWordSaveQueue,
    removeWordSave,
} from '../utils/wordSaveQueue.js';
import {
    extractDailyQuestState,
    markQuestInState,
    armStreakFreezeInState,
    applyStreakPrizeMilestones,
    dailyQuestStateNeedsPersist,
    normalizeDailyQuestState,
    rolloverDailyQuestState,
    getTodayDateStr,
    STREAK_FREEZE_COST,
} from '../utils/dailyQuests.js';
import { extractMediaStoragePath } from '../utils/writingCheckerImages.js';
import { getCachedSignedMediaUrl } from '../utils/signedMediaUrlCache.js';
import { normalizeNewWordPayload, withPrepositionFromEnglish, withNormalizedEnglish, normalizeWordApostrophe } from '../utils/normalizeWordApostrophe.js';
import { applyPrepositionFromEnglish } from '../utils/englishPrepositions.js';
import {
    OTHER_COURSE,
    isOtherCourse,
    isUnattributedCourse,
    isStudentAddedWord,
    normalizeWordAttribution,
} from '../utils/wordAttribution.js';
import {
    sameLessonCoord,
    remapExerciseAttributionDelta,
    remapSimpleAttributionDelta,
} from '../utils/lessonAttributionRemap.js';
import { sanitizePlannerBackupRows } from '../utils/plannerBackupSanitize.js';
import { PLANNER_MONEY_BACKUP_ENTRIES } from '../utils/plannerMoneyBackupKeys.js';
import { normalizeUsedIn } from '../utils/wordPlacement.js';
import {
    getWordThemePairs,
    getWordSubtypes,
    normalizeWordTaxonomy,
    unionThemePairs,
    unionSubtypes,
    wordMatchesTheme,
} from '../utils/wordTaxonomy.js';

const tableMap = {
    globalWords: 'global_words',
    siteCourses: 'site_courses',
    sitePriceCategories: 'site_price_categories',
    siteText: 'site_text',
    lessons: 'lessons',
    users: 'profiles',
    admins: 'profiles',
    groups: 'groups',
    courses: 'courses',
    userProgress: 'user_progress',
    starDefinitions: 'star_definitions',
    showGames: 'show_games',
    messages: 'messages',
    rewards: 'rewards',
    rewardDefinitions: 'rewards',
    paymentLedger: 'payment_ledger',
    onlineLessons: 'online_lessons',
    slides: 'slides',
    syllabusItems: 'syllabus_items',
    syllabusConfig: 'syllabus_config',
    storeItems: 'store_items',
    rewardLedger: 'reward_ledger',
    automationRules: 'automation_rules',
    prices: 'prices',
    siteSettings: 'site_settings',
    checklistTemplates: 'checklist_templates',
    checklistAssessments: 'checklist_assessments',
    blocks: 'blocks',
    exercises: 'exercises',
    exerciseAssignments: 'exercise_assignments',
    groupWords: 'group_words',
    publicExercises: 'public_exercises',
    writingCheckerRubrics: 'writing_checker_rubrics',
    writingCheckerChecks: 'writing_checker_checks',
    teacherNotifications: 'teacher_notifications',
    plannerJobs: 'planner_jobs',
    plannerProjects: 'planner_projects',
    plannerTasks: 'planner_tasks',
    plannerPosts: 'planner_posts',
    plannerEvents: 'planner_events',
    plannerEventCategories: 'planner_event_categories',
    plannerLibrary: 'planner_library',
    plannerContentSmm: 'planner_content_smm',
};

const getSBTable = (name) => tableMap[name] || name;

/**
 * Generates a stable ID for a word based on its content (English, Translation, Transcription).
 * This is used to identify words in JSON-based user wordlists that lack a primary DB 'id'.
 */
export const getStableWordId = (w) => {
    if (!w) return 'null-word';
    if (w.id) return String(w.id);
    if (w._guid) return String(w._guid);
    const eng = String(w.english || w.word || w.English || '').trim().toLowerCase();
    const tra = String(w.russian || w.translation || w.Translation || w.Russian || '').trim().toLowerCase();
    const phn = String(w.ipa || w.transcription || w.Transcription || '').trim().toLowerCase();
    return `hash-${eng}-${tra}-${phn}`;
};

/** Match keys consistently: getStableWordId always returns string; callers may pass numeric ids from JSON. */
const normalizeWordTargetKey = (wordId) => {
    if (wordId === null || wordId === undefined) return '';
    return String(wordId);
};

/** Thrown when code tries to remove words from a student's list (except student account deletion). */
export const USER_WORDS_DELETE_FORBIDDEN =
    'Deleting words from a student word list is forbidden. Words are only removed when the student account is deleted.';

/**
 * Sanitizes a list of user words by removing duplicates and ensuring progress is preserved.
 * If multiple versions of the same word exist, it prioritizes the one with more progress (status weight).
 */
export const sanitizeUserWords = (words) => {
    if (!Array.isArray(words)) return [];

    const statusWeights = {
        'REVISED': 6, '6': 6, 6: 6,
        'LEARNT': 5, '5': 5, 5: 5,
        'FOR TEST': 4, '4': 4, 4: 4,
        'FOR REVISION': 3, '3': 3, 3: 3,
        'LEARNING': 2, '2': 2, 2: 2,
        'NOT STARTED': 1, '1': 1, 1: 1,
        'REMOVE': 0, '0': 0, 0: 0
    };

    const getWeight = (s) => statusWeights[s] || 1;

    const wordGroups = new Map();
    const taxonomyById = new Map();
    
    words.forEach(w => {
        if (!w) return;
        const id = getStableWordId(w);
        const taxonomy = taxonomyById.get(id) || { themePairs: [], subtypes: [] };
        taxonomyById.set(id, {
            themePairs: unionThemePairs(taxonomy.themePairs, getWordThemePairs(w)),
            subtypes: unionSubtypes(taxonomy.subtypes, getWordSubtypes(w)),
        });
        const existing = wordGroups.get(id);
        
        if (!existing || getWeight(w.status) > getWeight(existing.status)) {
            wordGroups.set(id, existing
                ? {
                    ...w,
                    starred: !!(w.starred || existing.starred),
                    student_added: !!(w.student_added || existing.student_added),
                    source: w.source || existing.source,
                }
                : w);
        } else if (getWeight(w.status) === getWeight(existing.status)) {
            // Tie-breaker: prefer the one with a dateAdded or more fields
            const wDate = new Date(w.statusDate || w.dateAdded || 0).getTime();
            const exDate = new Date(existing.statusDate || existing.dateAdded || 0).getTime();
            if (wDate > exDate) {
                wordGroups.set(id, {
                    ...w,
                    starred: !!(w.starred || existing.starred),
                    student_added: !!(w.student_added || existing.student_added),
                    source: w.source || existing.source,
                });
            } else if (w.starred && !existing.starred) {
                wordGroups.set(id, { ...existing, starred: true });
            } else if ((w.student_added || w.source === 'student') && !existing.student_added) {
                wordGroups.set(id, {
                    ...existing,
                    student_added: true,
                    source: existing.source || w.source || 'student',
                });
            }
        } else if (w.starred && !existing.starred) {
            wordGroups.set(id, { ...existing, starred: true });
        } else if ((w.student_added || w.source === 'student') && !existing.student_added) {
            wordGroups.set(id, {
                ...existing,
                student_added: true,
                source: existing.source || w.source || 'student',
            });
        }
    });

    const finalMap = {
        6: 'REVISED', '6': 'REVISED',
        5: 'LEARNT', '5': 'LEARNT',
        4: 'FOR TEST', '4': 'FOR TEST',
        3: 'FOR REVISION', '3': 'FOR REVISION',
        2: 'LEARNING', '2': 'LEARNING',
        1: 'NOT STARTED', '1': 'NOT STARTED',
        0: 'NOT STARTED', '0': 'NOT STARTED'
    };

    return Array.from(wordGroups.entries()).map(([id, w]) => {
        let next = w;
        if (finalMap[w.status]) {
            next = { ...w, status: finalMap[w.status] };
        }
        const taxonomy = taxonomyById.get(id) || {};
        return normalizeWordTaxonomy(withNormalizedEnglish(applyPrepositionFromEnglish({
            ...next,
            theme_pairs: taxonomy.themePairs,
            subtypes: taxonomy.subtypes,
        })));
    });
};

/**
 * Clean and map UI fields to DB columns
 */
const preparePayload = (tableName, item, excludeId = false) => {
    // 1. Remove virtual system fields
    const {
        isNew: _omitIsNew,
        created_at: _omitCreatedAt,
        _dbIdx: _omitDbIdx,
        isOptimistic: _omitIsOptimistic,
        isOptimisticRow: _omitIsOptimisticRow,
        dateAdded: _omitDateAdded,
        actions: _omitActions,
        totalSpend: _omitTotalSpend,
        total_spend: _omitTotalSpendSnake,
        totalSpendMinusTax: _omitTotalSpendMinusTax,
        balance: _omitBalance,
        payment_history: _omitPaymentHistory,
        password: _omitPassword,
        login: _omitLogin,
        email: _omitEmail,
        _projectionCourse: _omitProjectionCourse,
        _storedHome: _omitStoredHome,
        _projectedAwayFromHome: _omitProjectedAway,
        also_appears_in: _omitLegacyAlsoAppears,
        ...cleanItem
    } = item;

    if (excludeId) {
        delete cleanItem.id;
    }

    // 1b. Safety: Master tables don't have 'status' (it's for student progress only)
    if (['globalWords', 'global_words'].includes(tableName)) {
        delete cleanItem.status;
    }

    // Map and cast fields
    const payload = {};

    // 2. Defaults for specific tables
    if (tableName === 'userProgress' || tableName === 'userWords') {
        // Removed redundant status=1 override that was breaking partial updates
    }

    Object.entries(cleanItem).forEach(([key, val]) => {
        let finalKey = key;
        let finalVal = val;
        
        if (tableName === 'lessons') {
            if (key === 'dayOfWeek' || key === 'day_of_week') finalKey = 'day_of_week';
            if (key === 'mSum' || key === 'm_sum') finalKey = 'm_sum';
            if (key === 'nextDatetime' || key === 'next_datetime') finalKey = 'next_datetime';
            if (key === 'homeworkToCheck' || key === 'homework_to_check') finalKey = 'homework_to_check';
            if (key === 'lesson_words' || key === 'lessonWords') finalKey = 'lesson_words';
            if (key === 'wordlistAddedSnapshot' || key === 'wordlist_added_snapshot') finalKey = 'wordlist_added_snapshot';

            if (key === 'attendance') {
                payload.attendance = val;
                return;
            }

            // Handle virtual composite 'info' field
            if (key === 'info') {
                if (typeof val === 'object' && val !== null) {
                    if (val.group !== undefined) payload.group = val.group;
                    if (val.datetime !== undefined) payload.datetime = val.datetime;
                    if (val.dayOfWeek !== undefined) payload.day_of_week = val.dayOfWeek;
                }
                return;
            }
        }

        // Common mappings across tables
        if (key === 'dateAdded') {
            // dateAdded is usually a virtual field derived from 'created_at'.
            // For tables that had 'date_added', it's better to let Supabase handle created_at
            // or explicitly map it if needed. Removing special mapping to prevent schema cache errors.
            return;
        }
        if (key === 'courseName' && tableName !== 'groups' && tableName !== 'showGames') finalKey = 'course_name';
        if ((key === 'groupId' || key === 'group') && tableName === 'groupWords') finalKey = 'group_id';

        // Checklist Mappings
        if (tableName === 'checklistAssessments') {
            if (key === 'templateId') finalKey = 'template_id';
            if (key === 'studentId') finalKey = 'student_id';
            if (key === 'studentName') finalKey = 'student_name';
            if (key === 'totalScore') finalKey = 'total_score';
            if (key === 'criteriaScores') finalKey = 'criteria_scores';
            if (key === 'overallNote') finalKey = 'overall_note';
        }

        if (tableName === 'storeItems') {
            if (key === 'allowedStudents') finalKey = 'allowed_students';
        }

        // Handle multiselect/arrays in DB (strict Postgres check)
        const arrayFields = ['difficulty_type', 'difficulty_types', 'confused_with', 'word_family', 'tags', 'synonyms', 'antonyms', 'price_history', 'payment_history', 'allowed_students', 'subtypes'];
        const isArrayField = arrayFields.includes(key.toLowerCase());

        // Blocks table specific handling
        if (tableName === 'blocks') {
            // Remove invalid fields that don't exist in blocks table
            if (key === 'type') return; // blocks table doesn't have a type column
        }

        // New fields mapping
        if (key === 'spelling_variation' || key === 'spellingVariation') finalKey = 'spelling_variation';
        if (key === 'themePairs') finalKey = 'theme_pairs';
        if (key === 'taxonomyPair') return;
        if (key === 'collocations') finalKey = 'collocations';
        if (key === 'calculated_difficulty' || key === 'calculatedDifficulty') finalKey = 'calculated_difficulty';

        if (isArrayField) {
            if (typeof finalVal === 'string' && finalVal.trim() !== '') {
                finalVal = finalVal.split(/[,;]/).map(s => s.trim()).filter(Boolean);
            } else if (typeof finalVal === 'string' && finalVal.trim() === '') {
                finalVal = null;
            } else if (!Array.isArray(finalVal)) {
                finalVal = null;
            }
        }

        // used_in must be a jsonb array of {course,module,lesson} — never a chip string
        if (finalKey === 'used_in' || finalKey === 'also_appears_in') {
            finalKey = 'used_in';
            finalVal = normalizeUsedIn(finalVal);
        }

        // Handle Type Casting
        const dateFields = ['datetime', 'next_datetime', 'date', 'time', 'date_added', 'sale_until'];
        const boolFields = ['archived', 'm', 'completed', 'active', 'enrolling_individual', 'enrolling_group', 'hidden', 'planned', 'finished', 'homework_sent'];
        const intFields = ['status', 'module', 'lesson', 'rewards', 'wordCount', 'm_sum', 'stars', 'price', 'price_individual', 'price_group', 'sale_price_individual', 'sale_price_group', 'price_individual_40', 'price_individual_60', 'price_individual_90', 'price_group_40', 'price_group_60', 'price_group_90', 'sale_price_individual_40', 'sale_price_individual_60', 'sale_price_individual_90', 'sale_price_group_40', 'sale_price_group_60', 'sale_price_group_90', 'price_individual_40_block', 'price_individual_60_block', 'price_individual_90_block', 'price_group_40_block', 'price_group_60_block', 'price_group_90_block', 'sale_price_individual_40_block', 'sale_price_individual_60_block', 'sale_price_individual_90_block', 'sale_price_group_40_block', 'sale_price_group_60_block', 'sale_price_group_90_block', 'sort_order', 'coins', 'amount', 'value', 'balance', 'calculated_difficulty'];

        // Strict schema check for tables without status
        if (['globalWords', 'prices'].includes(tableName) && finalKey.toLowerCase() === 'status') {
            return;
        }

        // Prevent calculated_difficulty from leaking to non-global tables
        if (tableName !== 'globalWords' && tableName !== 'global_words' && finalKey === 'calculated_difficulty') {
            return;
        }

        const statusIsInt = !['groups', 'profiles', 'users', 'admins'].includes(tableName);
        // site_settings.value stores JSON/text (e.g. text_rework_prompts) — must not be parseInt'd
        const isTextSettingValue = tableName === 'siteSettings' && finalKey === 'value';
        const isIntField = intFields.includes(finalKey) && (finalKey !== 'status' || statusIsInt) && !isTextSettingValue;

        if (isIntField) {
            if (typeof finalVal === 'string' && finalVal.trim() !== '') {
                const parsed = parseInt(finalVal, 10);
                // status/Diff Level should default to 1, others to 0
                finalVal = isNaN(parsed) ? (finalKey === 'status' ? 1 : 0) : parsed;
                // BUG FIX: Removed forced 1 for status 0, as 0 is used for REMOVE state
                // if (finalKey === 'status' && finalVal === 0) finalVal = 1;
            } else if (finalVal === '' || finalVal === null || finalVal === undefined) {
                // BUG FIX: For partial updates, it's safer to not include status if it's truly missing.
                // However, many callers expect a fallback. We'll return the value as is (null/undef)
                // if it's not present, and only force 1 if it's an explicit empty string for status.
                if (finalVal === '' && finalKey === 'status') {
                   finalVal = 1;
                } else {
                   finalVal = null;
                }
            } else if (typeof finalVal === 'number' && finalKey === 'status') {
                // Ensure number status is preserved as is (especially 0)
                finalVal = Math.round(finalVal);
            }
        } else if (boolFields.includes(finalKey)) {
            if (typeof finalVal === 'string') {
                finalVal = finalVal.toLowerCase() === 'true' || finalVal === '1';
            } else if (!finalVal) {
                finalVal = false;
            }
        } else if (dateFields.includes(finalKey) || isArrayField) {
            if (finalVal === '' || (Array.isArray(finalVal) && finalVal.length === 0 && finalKey !== 'subtypes')) {
                finalVal = null;
            }
        }

        payload[finalKey] = finalVal;
    });

    // Slides table: bundle editor-specific fields into config JSONB
    if (tableName === 'slides') {
        const configKeys = ['style', 'defaultHidden', 'randomOrder', 'allowStudentControl', 'mode'];
        const config = payload.config || {};
        configKeys.forEach(k => {
            if (k in payload) {
                config[k] = payload[k];
                delete payload[k];
            }
        });
        if (Object.keys(config).length > 0) {
            payload.config = config;
        }
    }
    
    if (tableName === 'storeItems') {
        console.log('[DEBUG] storeItems payload keys:', Object.keys(payload));
    }

    // Final safety purge for tables with strict schemas
    if (tableName === 'users' || tableName === 'admins') {
        delete payload.balance;
        delete payload.payment_history;
        delete payload.totalSpend;
        delete payload.total_spend;
        delete payload.totalSpendMinusTax;
        // Keep legacy `active` in sync with account lifecycle `status` on profiles
        const allowed = ['Active', 'Frozen', 'Deactivated'];
        if (payload.status != null && String(payload.status).trim() !== '') {
            const s = String(payload.status).trim();
            if (allowed.includes(s)) {
                payload.active = s !== 'Deactivated';
            }
        } else if (payload.active === false) {
            payload.status = 'Deactivated';
        }
    }

    return payload;
};

const ASSIGNMENT_CACHE_TTL = 60 * 1000; // 1 minute cache for assignments
const ASSIGNMENT_LOCKED_META_KEY = '__locked_map__';
const ASSIGNMENT_GAP_ATTEMPTS_META_KEY = '__gap_attempts__';

const ASSIGNMENT_DISALLOWED_COLUMNS = new Set(['updated_at']);

/** Progress fields stored inside assignment.answers (jsonb), not as table columns. */
const ASSIGNMENT_ANSWERS_INLINE_KEYS = [
    'current',
    'currentSlots',
    'slotPools',
    'unblurred',
    'page',
    'flipped',
    // Test-block fields — never existed on exercise_assignments; nesting avoids PGRST204.
    'test_time_seconds',
    'test_completed',
    // Per-gap teacher/auto rewards (Confirm Auto-Answers) — not a table column on prod.
    'gap_rewards',
];

const prepareAssignmentDelta = (delta) => {
    if (!delta || typeof delta !== 'object') return delta;
    const prepared = { ...delta };
    // Explicit answers: null (reassign) clears the jsonb — do not recreate it for meta keys.
    const clearingAnswers = prepared.answers === null;
    if (prepared.answers && typeof prepared.answers === 'object') {
        prepared.answers = { ...prepared.answers };
    }
    if (!clearingAnswers && prepared.locked && typeof prepared.locked === 'object') {
        prepared.answers = prepared.answers || {};
        prepared.answers[ASSIGNMENT_LOCKED_META_KEY] = prepared.locked;
    }
    delete prepared.locked;
    if (!clearingAnswers && prepared.gap_attempts && typeof prepared.gap_attempts === 'object') {
        prepared.answers = prepared.answers || {};
        prepared.answers[ASSIGNMENT_GAP_ATTEMPTS_META_KEY] = prepared.gap_attempts;
    }
    delete prepared.gap_attempts;

    ASSIGNMENT_ANSWERS_INLINE_KEYS.forEach((key) => {
        if (!(key in prepared)) return;
        if (clearingAnswers) {
            delete prepared[key];
            return;
        }
        prepared.answers = prepared.answers || {};
        prepared.answers[key] = prepared[key];
        delete prepared[key];
    });

    ASSIGNMENT_DISALLOWED_COLUMNS.forEach((key) => {
        if (key in prepared) delete prepared[key];
    });
    return prepared;
};

/** True when `answers` patch only carries nested meta (must merge with existing row). */
const answersPatchIsMetaOnly = (answers) => {
    if (!answers || typeof answers !== 'object') return false;
    const keys = Object.keys(answers);
    if (keys.length === 0) return false;
    const meta = new Set([
        ...ASSIGNMENT_ANSWERS_INLINE_KEYS,
        ASSIGNMENT_LOCKED_META_KEY,
        ASSIGNMENT_GAP_ATTEMPTS_META_KEY,
    ]);
    return keys.every((k) => meta.has(k));
};

const applyAssignmentMeta = (row) => {
    if (!row || typeof row !== 'object') return row;
    const answers = row.answers;
    if (!answers || typeof answers !== 'object') return row;

    const next = { ...row };
    let restAnswers = { ...answers };

    if (ASSIGNMENT_LOCKED_META_KEY in restAnswers) {
        const { [ASSIGNMENT_LOCKED_META_KEY]: locked, ...rest } = restAnswers;
        next.locked = locked || {};
        restAnswers = rest;
    }

    if (ASSIGNMENT_GAP_ATTEMPTS_META_KEY in restAnswers) {
        const { [ASSIGNMENT_GAP_ATTEMPTS_META_KEY]: gapAttempts, ...rest } = restAnswers;
        next.gap_attempts = gapAttempts || {};
        restAnswers = rest;
    }

    ASSIGNMENT_ANSWERS_INLINE_KEYS.forEach((key) => {
        if (key in restAnswers) {
            next[key] = restAnswers[key];
            delete restAnswers[key];
        }
    });

    next.answers = restAnswers;
    return next;
};

/**
 * Increments the calculated difficulty of a global word by its ID.
 */
const incrementGlobalDifficulty = async (targetId, wordObj = null) => {
    if (!targetId) return; 
    try {
        // If targetId is a hash-based ID (legacy words without proper UUID), 
        // it won't match any global_words row. Try UUID lookup first, then fallback.
        let query = supabase
            .from('global_words')
            .select('id, calculated_difficulty')
            .eq('id', targetId)
            .single();
        
        let { data, error: fetchError } = await query;
        
        // Fallback: If not found and it's a hash, query by English text
        if ((fetchError || !data) && typeof targetId === 'string' && targetId.startsWith('hash-')) {
            if (wordObj && wordObj.english) {
                console.log(`[Difficulty Tracking] ID lookup failed for hash ${targetId}, trying English fallback...`);
                let fallbackQuery = supabase
                    .from('global_words')
                    .select('id, calculated_difficulty')
                    .ilike('english', wordObj.english);
                
                if (wordObj.russian) {
                    fallbackQuery = fallbackQuery.ilike('russian', wordObj.russian);
                }
                
                const { data: fallbackData, error: fallbackError } = await fallbackQuery.limit(1).single();
                
                if (!fallbackError && fallbackData) {
                    data = fallbackData;
                    fetchError = null;
                } else {
                    console.warn(`[Difficulty Tracking] Fallback lookup also failed for hash "${targetId}".`);
                    return;
                }
            } else {
                console.warn(`[Difficulty Tracking] Hash-based ID "${targetId}" has no global match and no word data. Skipping.`);
                return;
            }
        }
        
        if (fetchError || !data) return;

        const current = data.calculated_difficulty || 0;
        await supabase
            .from('global_words')
            .update({ calculated_difficulty: current + 1 })
            .eq('id', data.id);
            
        console.log(`[Difficulty Tracking] Word ${data.id} incremented to ${current + 1}`);
    } catch (e) {
        console.error("[Difficulty Tracking] Error:", e);
    }
};

/**
 * Helper to determine if a status change is a regression.
 */
const isStatusRegression = (oldStatus, newStatus) => {
    const advanced = ['FOR TEST', 'FOR REVISION', 'LEARNT', 'REVISED', 3, 4, 5, 6, '3', '4', '5', '6'];
    const base = ['LEARNING', 'NOT STARTED', 1, 2, '1', '2'];
    
    // Regression occurs if moving from advanced back to base
    return advanced.includes(oldStatus) && base.includes(newStatus);
};

/** Apply a status change to an in-memory word list (shared by fetch and session-cache paths). */
const applyWordStatusUpdate = (words, wordId, newStatus) => {
    const targetKey = normalizeWordTargetKey(wordId);
    if (!targetKey || targetKey === 'null-word') {
        throw new Error('Word id is required to update status');
    }
    if (!words.some(w => getStableWordId(w) === targetKey)) {
        throw new Error('Word not found in student vocabulary');
    }
    const updated = words.map(w => {
        const currentId = getStableWordId(w);
        if (currentId !== targetKey) return w;
        const oldStatus = w.status;
        const newData = { ...w, status: newStatus };
        if (oldStatus !== newStatus) {
            newData.statusDate = new Date().toISOString();
        }
        if (isStatusRegression(oldStatus, newStatus)) {
            incrementGlobalDifficulty(targetKey, w);
        }
        return newData;
    });
    return { targetKey, words: sanitizeUserWords(updated) };
};

/** Read authoritative daily_quests from DB (word/training saves must not revert quest progress). */
const fetchPersistedDailyQuests = async ({ userId, recordId }) => {
    try {
        let q = supabase.from('user_progress').select('progress');
        if (recordId) q = q.eq('id', recordId);
        else if (userId) q = q.eq('user_id', userId);
        else return null;
        const { data } = await q.maybeSingle();
        const dq = data?.progress?.daily_quests;
        return dq && typeof dq === 'object' ? dq : null;
    } catch {
        return null;
    }
};

/** Net coin balance from history (+ gifts, − paid mode entries, etc.). */
const sumCoinsHistory = (history) => {
    if (!Array.isArray(history) || history.length === 0) return null;
    return history.reduce((sum, entry) => sum + (parseInt(entry?.amount, 10) || 0), 0);
};

const pickLongerCoinsHistory = (columnHist, blobHist) => {
    const col = Array.isArray(columnHist) ? columnHist : [];
    const blob = Array.isArray(blobHist) ? blobHist : [];
    return col.length >= blob.length ? col : blob;
};

/**
 * Authoritative spendable coins: history sum when we have entries (includes spending),
 * otherwise column/blob. On mismatch, trust the longer history's net sum — not max(column, sum).
 */
const resolveAuthoritativeCoins = (columnCoins, columnHist, blobHist, blobCoins) => {
    const history = pickLongerCoinsHistory(columnHist, blobHist);
    const historySum = sumCoinsHistory(history);
    if (historySum !== null) {
        return { coins: historySum, coins_history: history };
    }
    const colParsed = (columnCoins !== null && columnCoins !== undefined && columnCoins !== '')
        ? parseInt(columnCoins, 10)
        : NaN;
    if (!Number.isNaN(colParsed)) {
        return { coins: colParsed, coins_history: history };
    }
    const blobParsed = parseInt(blobCoins || 0, 10) || 0;
    return { coins: blobParsed, coins_history: history };
};

const sumCourseStatsCurrency = (stats) => {
    if (!stats || typeof stats !== 'object') return 0;
    return Object.values(stats).reduce((sum, group) => sum + (parseFloat(group?.currency) || 0), 0);
};

/** Prefer the richer course_stats object (column vs JSON blob). */
const mergeCourseStats = (columnStats, blobStats) => {
    const col = columnStats && typeof columnStats === 'object' ? columnStats : {};
    const blob = blobStats && typeof blobStats === 'object' ? blobStats : {};
    const colKeys = Object.keys(col).length;
    const blobKeys = Object.keys(blob).length;
    if (colKeys === 0) return blob;
    if (blobKeys === 0) return col;
    const colTotal = sumCourseStatsCurrency(col);
    const blobTotal = sumCourseStatsCurrency(blob);
    if (colKeys > blobKeys) return col;
    if (blobKeys > colKeys) return blob;
    return colTotal >= blobTotal ? col : blob;
};

const mergeStarRewardsById = (a = [], b = []) => {
    const map = new Map();
    for (const entry of [...a, ...b]) {
        if (!entry || typeof entry !== 'object') continue;
        const key = entry.id || `${entry.dateAwarded || ''}:${entry.name || ''}:${entry.stars ?? ''}`;
        map.set(key, entry);
    }
    return [...map.values()];
};

/** Never let a stale saveProgress payload shrink star balances vs DB. */
const resolveCourseStatsForSave = (incoming, persistedColumn, persistedBlob) => {
    const persisted = mergeCourseStats(persistedColumn, persistedBlob);
    if (!incoming || typeof incoming !== 'object' || Object.keys(incoming).length === 0) {
        return persisted;
    }

    const countStarRewards = (rewards = []) => rewards.filter((r) => r?.isStar).length;

    const merged = { ...persisted };
    for (const [key, incomingGroup] of Object.entries(incoming)) {
        if (!incomingGroup || typeof incomingGroup !== 'object') continue;
        const persistedGroup = merged[key] || { currency: 0, rewards: [] };
        const inCurrency = parseFloat(incomingGroup.currency) || 0;
        const perCurrency = parseFloat(persistedGroup.currency) || 0;
        const inRewards = Array.isArray(incomingGroup.rewards) ? incomingGroup.rewards : [];
        const perRewards = Array.isArray(persistedGroup.rewards) ? persistedGroup.rewards : [];
        const inStarCount = countStarRewards(inRewards);
        const perStarCount = countStarRewards(perRewards);

        let currency = inCurrency;
        if (inCurrency < perCurrency && inStarCount < perStarCount) {
            currency = perCurrency;
        }

        merged[key] = {
            ...persistedGroup,
            ...incomingGroup,
            currency,
            rewards: mergeStarRewardsById(perRewards, inRewards),
        };
    }
    return merged;
};

const syncWordGameSessionCacheFromProgress = (cache, progressLike) => {
    if (!cache || !progressLike) return;
    if (progressLike.coins !== undefined) cache.coins = progressLike.coins;
    if (Array.isArray(progressLike.coins_history)) cache.coins_history = progressLike.coins_history;
    if (Array.isArray(progressLike.rewards)) cache.rewards = progressLike.rewards;
    if (progressLike.course_stats && typeof progressLike.course_stats === 'object') {
        cache.course_stats = progressLike.course_stats;
    }
    if (Array.isArray(progressLike.user_words) && progressLike.user_words.length > 0) {
        cache.words = progressLike.user_words;
    }
    const dq = progressLike.daily_quests
        ?? (progressLike.progress?.daily_quests);
    if (dq && typeof dq === 'object') cache.daily_quests = dq;
    cache.progressBlob = {
        ...(cache.progressBlob || {}),
        user_words: cache.words,
        coins: cache.coins,
        coins_history: cache.coins_history,
        rewards: cache.rewards,
        course_stats: cache.course_stats,
        daily_quests: cache.daily_quests ?? cache.progressBlob?.daily_quests,
    };
};

/** Academy login emails are always username@learning.site (legacy rows may store bare username). */
function normalizeAcademyAuthEmail({ login, email } = {}) {
    const rawLogin = typeof login === 'string' ? login.trim().toLowerCase() : '';
    if (rawLogin) return `${rawLogin.replace(/@learning\.site$/i, '')}@learning.site`;
    const rawEmail = typeof email === 'string' ? email.trim().toLowerCase() : '';
    if (!rawEmail) return null;
    if (rawEmail.endsWith('@learning.site')) return rawEmail;
    if (rawEmail.includes('@')) return rawEmail;
    return `${rawEmail}@learning.site`;
}

console.log("DB_SERVICE_V4_ACTIVE");

// Simple in-memory cache to prevent redundant queries
const queryCache = new Map();
const CACHE_TTL = 60000; // 1 minute cache for most tables
const CACHE_TTL_LONG = 300000; // 5 minutes for rarely-changing data

// Track in-flight requests to prevent duplicate simultaneous calls (StrictMode)
const inFlightRequests = new Map();

const getCacheKey = (tableName, userId, fields, filters) => {
    return `${tableName}:${userId || 'none'}:${fields}:${JSON.stringify(filters)}`;
};

const getCachedData = (key) => {
    const cached = queryCache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
        console.log(`[DB Cache] HIT for ${key}`);
        return cached.data;
    }
    return null;
};

const setCachedData = (key, data, ttl = CACHE_TTL) => {
    queryCache.set(key, { data, timestamp: Date.now(), ttl });
    console.log(`[DB Cache] SET for ${key} (TTL: ${ttl}ms)`);
};

// Clear cache for specific table when data is modified
const db = {
    supabase,
    sanitizeUserWords,
    USER_WORDS_DELETE_FORBIDDEN,
    clearCache: (tableName) => {
        if (tableName) {
            for (const key of queryCache.keys()) {
                if (key.startsWith(tableName + ':')) {
                    queryCache.delete(key);
                    console.log(`[DB Cache] CLEARED for ${key}`);
                }
            }
        } else {
            queryCache.clear();
            console.log(`[DB Cache] CLEARED ALL`);
        }
    },
    bypassCache: () => {
        queryCache.clear();
        inFlightRequests.clear();
        console.log(`[DB Cache] BYPASSED - cleared cache and in-flight requests`);
    },

    /** Current Supabase Auth user or throw. */
    async _requireAuthUser() {
        await ensureAuthSession();
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        if (!data?.user?.id) throw new Error('Not authenticated');
        return data.user;
    },

    /** Allow only the signed-in user themselves, or a teacher. */
    async _assertSelfOrTeacher(targetUserId) {
        const authUser = await this._requireAuthUser();
        if (String(authUser.id) === String(targetUserId)) return authUser;
        const { data: profile, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', authUser.id)
            .maybeSingle();
        if (error) throw error;
        if (profile?.role === 'teacher') return authUser;
        throw new Error('Forbidden: you can only access your own data');
    },

    /** Load one row from site_settings by key (value is JSON object or string). */
    async getSiteSetting(settingKey, defaultValue = null) {
        const settings = await this.getTable('siteSettings');
        const row = (settings || []).find((s) => s.key === settingKey);
        if (!row || row.value === null || row.value === undefined || row.value === '') {
            return defaultValue;
        }
        const raw = row.value;
        if (typeof raw === 'object' && raw !== null) return raw;
        if (typeof raw === 'number') {
            console.warn(`[DB] site_settings.${settingKey} has numeric value (corrupt); using default.`);
            return defaultValue;
        }
        try {
            return JSON.parse(String(raw));
        } catch {
            return defaultValue;
        }
    },

    /** Persist one site_settings row (value stored as JSON string in Supabase). */
    async saveSiteSetting(settingKey, valueObj) {
        const serialized =
            typeof valueObj === 'string' ? valueObj : JSON.stringify(valueObj);
        const settings = await this.getTable('siteSettings');
        const row = (settings || []).find((s) => s.key === settingKey);
        if (row?.id) {
            await this.updateItem('siteSettings', row.id, { key: settingKey, value: serialized });
        } else {
            await this.addItem('siteSettings', { key: settingKey, value: serialized });
            this.clearCache('siteSettings');
        }
    },
    // в”Ђв”Ђв”Ђ Student Analytics в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async startStudentSession(payload) {
        const { data, error } = await supabase
            .from('student_sessions')
            .insert([payload])
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async touchStudentSession(sessionId, userId, patch) {
        const update = { ...patch, user_id: userId, last_seen_at: new Date().toISOString() };
        const { error } = await supabase
            .from('student_sessions')
            .update(update)
            .eq('id', sessionId)
            .eq('user_id', userId);
        if (error) throw error;
        return true;
    },

    async endStudentSession(sessionId, userId, patch) {
        const now = new Date().toISOString();
        const update = { ...patch, ended_at: now, last_seen_at: now };
        const { error } = await supabase
            .from('student_sessions')
            .update(update)
            .eq('id', sessionId)
            .eq('user_id', userId);
        if (error) throw error;
        return true;
    },

    TRAINING_MODE_LABELS: {
        MEET: 'Meet new words',
        STUDY: 'Study',
        TEST: 'Spelling',
        REVIEW: 'Test',
    },

    async notifyTrainingModeSession(userId, mode) {
        const labels = this.TRAINING_MODE_LABELS;
        const trainingModes = new Set(Object.keys(labels));
        if (!userId || !trainingModes.has(mode)) return null;
        return this.createTeacherNotification({
            studentId: userId,
            type: 'training_mode',
            message: `Used ${labels[mode]}`,
            payload: { mode, date: getTodayDateStr() },
        });
    },

    _notifyExerciseCompleted(assignment, normalizedDelta) {
        if (!assignment || assignment.status !== 'completed' || normalizedDelta.status !== 'completed') return;
        this.createTeacherNotification({
            studentId: assignment.student_id,
            type: 'exercise_complete',
            message: 'Completed an exercise',
            payload: {
                assignment_id: assignment.id,
                exercise_id: assignment.exercise_id || null,
                block_id: assignment.block_id || null,
                group_id: assignment.group_id || null,
                completed_at: assignment.completed_at || normalizedDelta.completed_at || null,
            },
        }).catch(() => {});
    },

    async createTeacherNotification({ studentId, type, message = null, payload = {} }) {
        if (!studentId || !type) return null;
        try {
            // No .select() — students may insert but RLS only allows teachers to read rows.
            const { error } = await supabase
                .from(getSBTable('teacherNotifications'))
                .insert([{
                    student_id: studentId,
                    type,
                    message,
                    payload,
                }]);
            if (error) {
                console.warn('[DB] createTeacherNotification failed:', error.message);
                return null;
            }
            this.clearCache('teacherNotifications');
            return true;
        } catch (err) {
            console.warn('[DB] createTeacherNotification error:', err?.message || err);
            return null;
        }
    },

    async getTeacherNotifications({ limit = 50, unreadOnly = false } = {}) {
        let q = supabase
            .from(getSBTable('teacherNotifications'))
            .select('*')
            .order('created_at', { ascending: false })
            .limit(limit);
        if (unreadOnly) q = q.is('read_at', null);
        const { data, error } = await q;
        if (error) throw error;
        return data || [];
    },

    async markTeacherNotificationsRead(ids = []) {
        if (!ids?.length) return;
        const { error } = await supabase
            .from(getSBTable('teacherNotifications'))
            .update({ read_at: new Date().toISOString() })
            .in('id', ids);
        if (error) throw error;
        this.clearCache('teacherNotifications');
    },

    async markAllTeacherNotificationsRead() {
        const { error } = await supabase
            .from(getSBTable('teacherNotifications'))
            .update({ read_at: new Date().toISOString() })
            .is('read_at', null);
        if (error) throw error;
        this.clearCache('teacherNotifications');
    },

    async logSiteVisit({
        visitorId,
        path,
        search = null,
        referrer = null,
        utm_source = null,
        utm_medium = null,
        utm_campaign = null,
        userAgent = null,
    }) {
        if (!visitorId || !path) return null;
        try {
            const { error } = await supabase.rpc('log_site_visit', {
                p_visitor_id: visitorId,
                p_path: path,
                p_search: search,
                p_referrer: referrer,
                p_utm_source: utm_source,
                p_utm_medium: utm_medium,
                p_utm_campaign: utm_campaign,
                p_user_agent: userAgent,
            });
            if (error) {
                console.warn('[DB] logSiteVisit failed:', error.message);
                return null;
            }
            return true;
        } catch (err) {
            console.warn('[DB] logSiteVisit error:', err?.message || err);
            return null;
        }
    },

    async getSiteTrafficSummary(days = 7) {
        const { data, error } = await supabase.rpc('get_site_traffic_summary', {
            p_days: days,
        });
        if (error) throw error;
        return data || { days, paths: [], funnel: [], totals: { views: 0, unique_visitors: 0 } };
    },

    /** Per open-exercise slug: views, UTM and referrer breakdown (teachers only). */
    async getOpenExerciseTraffic(slug, days = 30) {
        if (!slug) {
            return {
                days,
                path: null,
                totals: { views: 0, unique_visitors: 0 },
                with_utm: { views: 0, unique_visitors: 0 },
                with_external_referrer: { views: 0, unique_visitors: 0 },
                by_utm: [],
                by_referrer: [],
            };
        }
        const { data, error } = await supabase.rpc('get_open_exercise_traffic', {
            p_slug: slug,
            p_days: days,
        });
        if (error) throw error;
        return data || {
            days,
            path: `/open/${slug}`,
            totals: { views: 0, unique_visitors: 0 },
            with_utm: { views: 0, unique_visitors: 0 },
            with_external_referrer: { views: 0, unique_visitors: 0 },
            by_utm: [],
            by_referrer: [],
        };
    },

    /** Load raw activity sources for Teacher Dashboard → Users → Activity. */
    async getUserActivityForTeacher(userId, { days = 30, limit = 300, userType = 'student' } = {}) {
        if (!userId) return { events: [], sessions: [], ledger: [], checks: [], notifications: [], portalTokens: [], progress: null };

        const since = new Date();
        since.setDate(since.getDate() - Math.max(1, parseInt(days, 10) || 30));
        const sinceIso = since.toISOString();

        if (userType === 'portal') {
            const [ledgerRes, checks] = await Promise.all([
                supabase
                    .from('portal_token_ledger')
                    .select('id, amount, balance_after, reason, reference_id, created_at')
                    .eq('user_id', userId)
                    .gte('created_at', sinceIso)
                    .order('created_at', { ascending: false })
                    .limit(limit),
                this.getWritingCheckerChecks({ userId, source: 'portal', limit }),
            ]);
            if (ledgerRes.error) throw ledgerRes.error;
            return {
                events: [],
                sessions: [],
                ledger: [],
                checks: checks || [],
                notifications: [],
                portalTokens: ledgerRes.data || [],
                progress: null,
            };
        }

        const [
            eventsRes,
            sessionsRes,
            ledgerRes,
            progress,
            checks,
            notificationsRes,
        ] = await Promise.all([
            supabase
                .from('student_events')
                .select('id, session_id, user_id, ts, type, path, payload')
                .eq('user_id', userId)
                .gte('ts', sinceIso)
                .order('ts', { ascending: false })
                .limit(limit),
            supabase
                .from('student_sessions')
                .select('id, started_at, ended_at, duration_sec, initial_path, last_path, device')
                .eq('user_id', userId)
                .gte('started_at', sinceIso)
                .order('started_at', { ascending: false })
                .limit(100),
            supabase
                .from('reward_ledger')
                .select('id, type, amount, reason, source, group_id, created_at')
                .eq('user_id', userId)
                .gte('created_at', sinceIso)
                .order('created_at', { ascending: false })
                .limit(limit),
            this.getUserProgress(userId, true).catch(() => null),
            this.getWritingCheckerChecks({ userId, limit: 80 }),
            supabase
                .from(getSBTable('teacherNotifications'))
                .select('id, type, message, payload, created_at')
                .eq('student_id', userId)
                .gte('created_at', sinceIso)
                .order('created_at', { ascending: false })
                .limit(100),
        ]);

        if (eventsRes.error) throw eventsRes.error;
        if (sessionsRes.error) throw sessionsRes.error;
        if (ledgerRes.error) throw ledgerRes.error;
        if (notificationsRes.error) throw notificationsRes.error;

        return {
            events: eventsRes.data || [],
            sessions: sessionsRes.data || [],
            ledger: ledgerRes.data || [],
            checks: (checks || []).filter((c) => {
                const ts = c.completedAt || c.createdAt;
                return ts && new Date(ts) >= since;
            }),
            notifications: notificationsRes.data || [],
            portalTokens: [],
            progress,
        };
    },

    /**
     * Teacher Users → Stats overlay: attendance / visit / daily-quest calendars.
     * @returns {{
     *   attendanceByDate: Record<string, 'ATTENDED'|'LATE'|'MISSED'>,
     *   visitDates: string[],
     *   questMarkedDates: string[],
     *   summary: object,
     *   streak_days: number,
     * }}
     */
    async getUserStatsForTeacher(userId, { fullName = '', months = 6 } = {}) {
        if (!userId) {
            return {
                attendanceByDate: {},
                visitDates: [],
                questMarkedDates: [],
                summary: { attended: 0, late: 0, missed: 0, visitDays: 0, questDays: 0 },
                streak_days: 0,
            };
        }

        await this._assertSelfOrTeacher(userId);

        const toDateKey = (value) => {
            if (!value) return null;
            const d = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(d.getTime())) {
                const s = String(value);
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
                return null;
            }
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const since = new Date();
        since.setMonth(since.getMonth() - Math.max(1, parseInt(months, 10) || 6));
        since.setHours(0, 0, 0, 0);
        const sinceIso = since.toISOString();
        const uid = String(userId);

        const [groups, sessionsRes, notificationsRes, progress] = await Promise.all([
            this.getGroupsForStudent(uid, fullName || ''),
            supabase
                .from('student_sessions')
                .select('started_at')
                .eq('user_id', uid)
                .gte('started_at', sinceIso)
                .order('started_at', { ascending: false })
                .limit(2000),
            supabase
                .from(getSBTable('teacherNotifications'))
                .select('id, type, payload, created_at')
                .eq('student_id', uid)
                .eq('type', 'daily_streak')
                .gte('created_at', sinceIso)
                .order('created_at', { ascending: false })
                .limit(500),
            this.getUserProgress(uid, true).catch(() => null),
        ]);

        if (sessionsRes.error) throw sessionsRes.error;
        // Soft-fail notifications: older RLS may block student self-read until migration is applied
        if (notificationsRes.error) {
            console.warn('[DB] teacher_notifications for stats:', notificationsRes.error.message);
        }

        const groupIds = (groups || []).map((g) => g.id).filter(Boolean);
        const lessons = groupIds.length ? await this.getLessonsForGroups(groupIds) : [];

        const attendancePriority = { MISSED: 3, LATE: 2, ATTENDED: 1 };
        const attendanceByDate = {};
        const lessonEvents = [];
        (lessons || []).forEach((lesson) => {
            const key = toDateKey(lesson.datetime || lesson.date);
            if (!key) return;
            // Only expose the requested student's status (never peer attendance)
            const status = String(lesson.attendance?.[uid] || '').toUpperCase();
            if (!status || !attendancePriority[status]) return;
            const prev = attendanceByDate[key];
            if (!prev || attendancePriority[status] > attendancePriority[prev]) {
                attendanceByDate[key] = status;
            }
            const dt = lesson.datetime ? new Date(lesson.datetime) : null;
            if (dt && !Number.isNaN(dt.getTime())) {
                lessonEvents.push({ datetime: dt.toISOString(), status, dateKey: key });
            }
        });

        const visitSet = new Set();
        (sessionsRes.data || []).forEach((s) => {
            const key = toDateKey(s.started_at);
            if (key) visitSet.add(key);
        });

        const questSet = new Set();
        (notificationsRes.data || []).forEach((n) => {
            // Prefer payload.date (local calendar day when tasks were completed)
            const payloadDate = n?.payload?.date || n?.payload?.today_date || n?.payload?.marked_date;
            const key = toDateKey(payloadDate);
            if (key) questSet.add(key);
        });

        const dq = progress?.daily_quests || progress?.progress?.daily_quests || null;
        const dqNorm = dq ? normalizeDailyQuestState(dq) : null;
        (dqNorm?.marked_dates || []).forEach((d) => {
            const key = toDateKey(d);
            if (key) questSet.add(key);
        });
        if (dqNorm?.day_marked) {
            const todayKey = toDateKey(dqNorm.today_date) || toDateKey(new Date());
            if (todayKey) questSet.add(todayKey);
        }
        if (dqNorm?.last_marked_date) {
            const lastKey = toDateKey(dqNorm.last_marked_date);
            if (lastKey) questSet.add(lastKey);
        }

        const visitDates = [...visitSet].sort();
        const questMarkedDates = [...questSet].sort();

        let attended = 0;
        let late = 0;
        let missed = 0;
        Object.values(attendanceByDate).forEach((s) => {
            if (s === 'ATTENDED') attended += 1;
            else if (s === 'LATE') late += 1;
            else if (s === 'MISSED') missed += 1;
        });

        const now = Date.now();
        const pastEvents = lessonEvents
            .filter((e) => new Date(e.datetime).getTime() <= now)
            .sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
        const lastLesson = pastEvents[0] || null;

        return {
            attendanceByDate,
            visitDates,
            questMarkedDates,
            summary: {
                attended,
                late,
                missed,
                visitDays: visitDates.length,
                questDays: questMarkedDates.length,
            },
            streak_days: Number(dqNorm?.streak_days) || Number(dq?.streak_days) || 0,
            lastLesson: lastLesson
                ? {
                    datetime: lastLesson.datetime,
                    status: lastLesson.status,
                    missedLast: lastLesson.status === 'MISSED',
                }
                : null,
        };
    },

    /** Student Home: stats calendar for the signed-in user only (ignores any foreign id). */
    async getMyStatsCalendar({ months = 12, fullName = '' } = {}) {
        const authUser = await this._requireAuthUser();
        return this.getUserStatsForTeacher(authUser.id, { fullName, months });
    },

    /**
     * Planned away periods for a user (Stats overlay).
     * @returns {Promise<Array<{ id, user_id, start_date, end_date, reason, created_at }>>}
     */
    async listAwayPeriods(userId) {
        if (!userId) return [];
        await this._assertSelfOrTeacher(userId);
        const { data, error } = await supabase
            .from('user_away_periods')
            .select('id, user_id, start_date, end_date, reason, created_at')
            .eq('user_id', String(userId))
            .order('start_date', { ascending: false });
        if (error) throw error;
        return data || [];
    },

    /**
     * Batch load away periods for Lesson Details (teacher).
     * @returns {Promise<Record<string, Array>>} map userId → periods
     */
    async getAwayPeriodsForUsers(userIds = []) {
        const ids = [...new Set((userIds || []).map((id) => String(id)).filter(Boolean))];
        if (!ids.length) return {};
        await this._requireAuthUser();
        const { data, error } = await supabase
            .from('user_away_periods')
            .select('id, user_id, start_date, end_date, reason, created_at')
            .in('user_id', ids)
            .order('start_date', { ascending: true });
        if (error) throw error;
        const map = {};
        ids.forEach((id) => { map[id] = []; });
        (data || []).forEach((row) => {
            const uid = String(row.user_id);
            if (!map[uid]) map[uid] = [];
            map[uid].push(row);
        });
        return map;
    },

    /**
     * Add an away period (teacher for any user, or self).
     * @param {{ userId: string, startDate: string, endDate: string, reason?: string }}
     */
    async addAwayPeriod({ userId, startDate, endDate, reason = '' } = {}) {
        if (!userId || !startDate || !endDate) {
            throw new Error('userId, startDate and endDate are required');
        }
        const start = String(startDate).slice(0, 10);
        const end = String(endDate).slice(0, 10);
        if (end < start) throw new Error('End date must be on or after start date');

        const authUser = await this._assertSelfOrTeacher(userId);
        const payload = {
            user_id: String(userId),
            start_date: start,
            end_date: end,
            reason: String(reason || '').trim() || null,
            created_by: authUser.id,
        };
        const { data, error } = await supabase
            .from('user_away_periods')
            .insert(payload)
            .select('id, user_id, start_date, end_date, reason, created_at')
            .single();
        if (error) throw error;

        // Student self-save → teacher activity bell (RLS: insert only when student_id = auth.uid())
        if (String(authUser.id) === String(userId)) {
            const fmt = (key) => {
                try {
                    const d = new Date(`${String(key).slice(0, 10)}T12:00:00`);
                    if (Number.isNaN(d.getTime())) return key;
                    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
                } catch {
                    return key;
                }
            };
            const fromLabel = fmt(data.start_date);
            const tillLabel = fmt(data.end_date);
            const rangeLabel = fromLabel === tillLabel ? fromLabel : `${fromLabel} → ${tillLabel}`;
            this.createTeacherNotification({
                studentId: userId,
                type: 'away_period',
                message: 'Marked away from lessons',
                payload: {
                    period_id: data.id,
                    start_date: data.start_date,
                    end_date: data.end_date,
                    reason: data.reason || null,
                    range_label: rangeLabel,
                },
            }).catch(() => {});
        }

        return data;
    },

    async deleteAwayPeriod(periodId, userId) {
        if (!periodId) throw new Error('periodId is required');
        if (userId) await this._assertSelfOrTeacher(userId);
        else await this._requireAuthUser();
        const { error } = await supabase
            .from('user_away_periods')
            .delete()
            .eq('id', String(periodId));
        if (error) throw error;
        return true;
    },

    /**
     * Student Home Money + Attendance summary for the signed-in user only.
     * Payment prefers SECURITY DEFINER RPC; falls back to ledger select (RLS-scoped).
     */
    async getMyHomeDashboard({ fullName = '' } = {}) {
        const authUser = await this._requireAuthUser();
        const uid = authUser.id;

        let money = { balance: 0, lastPayment: null };
        try {
            const { data, error } = await supabase.rpc('get_my_payment_summary');
            if (error) throw error;
            money = {
                balance: Number(data?.balance) || 0,
                lastPayment: data?.lastPayment || null,
            };
        } catch (rpcErr) {
            console.warn('[DB] get_my_payment_summary RPC unavailable, using ledger fallback:', rpcErr?.message || rpcErr);
            const history = await this.getPaymentHistory(uid);
            const balance = (history || [])
                .filter((tx) => String(tx.type || '') !== 'tax')
                .reduce((acc, tx) => acc + (parseFloat(tx.amount) || 0), 0);
            const lastPayment = (history || []).find(
                (tx) => (parseFloat(tx.amount) || 0) > 0 && String(tx.type || '') !== 'tax'
            ) || null;
            money = {
                balance,
                lastPayment: lastPayment
                    ? {
                        amount: lastPayment.amount,
                        created_at: lastPayment.created_at,
                        note: lastPayment.note || '',
                        type: lastPayment.type,
                    }
                    : null,
            };
        }

        const stats = await this.getUserStatsForTeacher(uid, { fullName, months: 12 });
        return {
            money,
            lastLesson: stats.lastLesson || null,
            statsSummary: stats.summary || null,
        };
    },

    async logStudentEvent(payload) {
        const { error } = await supabase
            .from('student_events')
            .insert([payload]);
        if (error) {
            // Log but don't throw for expected errors
            const errorCode = String(error.code || '').toLowerCase();
            const errorMessage = String(error.message || '').toLowerCase();
            
            // Conflict errors (duplicate events)
            if (errorCode === '409' || errorCode === '23505' || 
                errorMessage.includes('duplicate') || 
                errorMessage.includes('conflict') ||
                errorMessage.includes('unique')) {
                console.warn('[DB] Student event already logged (duplicate):', error.message);
                return false;
            }
            
            // Foreign key violation (session_id not found in student_sessions)
            if (errorCode === '23503' || errorMessage.includes('foreign key') || errorMessage.includes('not present in table')) {
                console.warn('[DB] Student event session not found, skipping:', error.message);
                return false;
            }
            
            console.error('[DB] Student event logging failed:', error);
            throw error;
        }
        return true;
    },

    async getTable(tableName, userId = null, fields = '*', filters = {}) {
        // Check cache first (skip for userProgress and userWords as they're user-specific and frequently updated)
        const cacheKey = getCacheKey(tableName, userId, fields, filters);
        const cachedData = getCachedData(cacheKey);
        if (cachedData !== null && tableName !== 'userProgress' && tableName !== 'userWords') {
            return tableName === 'siteText' ? repairSiteTextRows(cachedData) : cachedData;
        }

        // Check if there's an in-flight request for the same query (StrictMode deduplication)
        if (inFlightRequests.has(cacheKey)) {
            console.log(`[DB Dedupe] Returning in-flight request for ${cacheKey}`);
            return inFlightRequests.get(cacheKey);
        }

        // Fallback data for critical tables to use when DB is down/slow
        const getStaticFallback = (name) => {
            if (name === 'siteText') return [];
            if (name === 'automationRules') return {
                testToRevisionStars: 1,
                revisionToLearntStars: 1,
                lookingBackCardsCoins: 1,
                lookingBackSpellCoins: 1,
                paidSurfEntryCoins: 10,
                paidChallengeEntryCoins: 10,
                paidChallengeRefundCoins: 10,
                paidChallengeBonusCoins: 5,
                paidDictationEntryCoins: 10,
                paidDictationPerfectCoins: 15,
                paidDictationOneMistakeCoins: 10,
                paidDictationPenaltyCoins: 10,
                paidSentencesEntryCoins: 10,
                paidSentencesPerfectCoins: 15,
                paidSentencesOneMistakeCoins: 10,
                paidSentencesPenaltyCoins: 10
            };
            return [];
        };

        if (tableName === 'userWords') {
            try {
                let q = supabase.from('user_progress').select(fields === '*' ? 'user_id, user_words' : fields);
                if (userId) q = q.eq('user_id', userId);
                
                const { data, error } = await this._withTimeout(q, 15000, 'getTable:userWords');
                if (error || !data) return (fields === '*') ? {} : [];
                if (fields === '*') {
                    return Object.fromEntries(data.map(d => [d.user_id, d.user_words || []]));
                }
                return data;
            } catch (e) {
                console.warn("[DB] getTable:userWords failed:", e.message);
                return (fields === '*') ? {} : [];
            }
        }

        const sbTable = getSBTable(tableName).trim().replace(/\s+/g, '_');

        const makeFilteredQuery = () => {
            let q = supabase.from(sbTable).select(fields);
            if (tableName === 'users') q = q.eq('role', 'student');
            if (tableName === 'admins') q = q.eq('role', 'teacher');
            if (tableName === 'userProgress' && userId) q = q.eq('user_id', userId);
            if (filters && typeof filters === 'object') {
                Object.entries(filters).forEach(([col, val]) => {
                    if (col === '__searchEnglish') {
                        const term = String(val || '').trim().replace(/[%_]/g, '\\$&');
                        if (term) q = q.ilike('english', `%${term}%`);
                        return;
                    }
                    if (Array.isArray(val)) {
                        q = q.in(col, val);
                    } else {
                        q = q.eq(col, val);
                    }
                });
            }
            return q;
        };

        // Execute with timeout and handle errors immediately
        const executeFetch = async () => {
            let data = [], error = null;
            try {
                console.log(`[DB] getTable:${tableName} starting (fields: ${fields})...`);

                // Ensure JWT is attached before RLS-heavy reads (avoids first-paint fetch as anon on cold load)
                if (tableName === 'globalWords') {
                    try {
                        await supabase.auth.getSession();
                    } catch (_) { /* non-fatal */ }
                }

                // Paginate global_words: self-hosted / custom PostgREST max_rows can truncate a single response (e.g. only first page visible after full reload)
                if (tableName === 'globalWords') {
                    const pageSize = 1000;
                    let from = 0;
                    for (;;) {
                        const pageQuery = makeFilteredQuery()
                            .order('id', { ascending: true })
                            .range(from, from + pageSize - 1);
                        const result = await this._withTimeout(pageQuery, 45000, `getTable:${tableName}:${from}`);
                        const batch = result.data || [];
                        error = result.error;
                        if (error) break;
                        data = data.concat(batch);
                        if (batch.length < pageSize) break;
                        from += pageSize;
                    }
                } else if (tableName === 'paymentLedger') {
                    // Paginate paymentLedger to bypass 1000-row limit
                    const pageSize = 1000;
                    let from = 0;
                    for (;;) {
                        const pageQuery = makeFilteredQuery()
                            .order('created_at', { ascending: false })
                            .range(from, from + pageSize - 1);
                        const result = await this._withTimeout(pageQuery, 60000, `getTable:${tableName}:${from}`);
                        const batch = result.data || [];
                        error = result.error;
                        if (error) break;
                        data = data.concat(batch);
                        if (batch.length < pageSize) break;
                        from += pageSize;
                    }
                } else if (tableName === 'groupWords') {
                    // Paginate group_words: table exceeds PostgREST max_rows; unfiltered loads silently drop later groups (e.g. Bro A2).
                    const pageSize = 1000;
                    let from = 0;
                    for (;;) {
                        const pageQuery = makeFilteredQuery()
                            .order('id', { ascending: true })
                            .range(from, from + pageSize - 1);
                        const result = await this._withTimeout(pageQuery, 45000, `getTable:${tableName}:${from}`);
                        const batch = result.data || [];
                        error = result.error;
                        if (error) break;
                        data = data.concat(batch);
                        if (batch.length < pageSize) break;
                        from += pageSize;
                    }
                } else {
                    const timeout = 30000;
                    const result = await this._withTimeout(makeFilteredQuery(), timeout, `getTable:${tableName}`);
                    data = result.data || [];
                    error = result.error;
                }

                console.log(`[DB] getTable:${tableName} completed. Rows:`, data.length);
            } catch (e) {
                console.warn(`[DB] getTable:${tableName} TIMEOUT or ERROR:`, e.message);
                return getStaticFallback(tableName); // Use fallback on timeout
            }

            if (error) {
                console.error(`Error fetching ${tableName}:`, error.message || error);
                // Even if it's not a timeout, use fallback if the fetch failed (e.g. Protocol Error)
                return getStaticFallback(tableName);
            }

            // SYNC: Enrich student profiles with payment info from user_progress
            if (tableName === 'users' && data && data.length > 0) {
                try {
                    // Fetch all ledger entries to calculate balances and history (paginate to bypass 1000-row limit)
                    let ledgerData = [];
                    const pageSize = 1000;
                    let from = 0;
                    while (true) {
                        const { data: batch } = await this._withTimeout(
                            // Secondary order by id is required: many migrated rows share the same
                            // created_at, and range pagination without a unique tie-breaker skips/duplicates rows
                            // (which corrupted balances, e.g. Varvara −1500 / Danila +1200).
                            supabase.from('payment_ledger').select('id, student_id, amount, type, note, created_at, taxed, after_tax').order('created_at', { ascending: false }).order('id', { ascending: true }).range(from, from + pageSize - 1),
                            60000,
                            `getTable:users:ledger_enrich:${from}`
                        );
                        if (!batch || batch.length === 0) break;
                        ledgerData = ledgerData.concat(batch);
                        if (batch.length < pageSize) break;
                        from += pageSize;
                    }
                    
                    if (ledgerData) {
                        const balanceMap = {};
                        const historyMap = {};
                        const spendMap = {};
                        const taxMap = {};
                        
                        ledgerData.forEach(tx => {
                            const sId = tx.student_id;
                            const amt = parseFloat(tx.amount) || 0;
                            
                            // Tax is the teacher's expense, not the student's вЂ” exclude from balance
                            if (tx.type !== 'tax') {
                                balanceMap[sId] = (balanceMap[sId] || 0) + amt;
                            }
                            
                            if (tx.type === 'lesson_cost') {
                                spendMap[sId] = (spendMap[sId] || 0) + Math.abs(amt);
                            } else if (tx.type === 'refund') {
                                spendMap[sId] = (spendMap[sId] || 0) - amt;
                            } else if (tx.type === 'tax') {
                                taxMap[sId] = (taxMap[sId] || 0) + Math.abs(amt);
                            }

                            if (!historyMap[sId]) historyMap[sId] = [];
                            // Include all entries in history (no limit for tax visibility)
                            historyMap[sId].push({
                                date: new Date(tx.created_at).toLocaleString(),
                                amount: tx.amount,
                                note: tx.note,
                                type: tx.type,
                                id: tx.id,
                                taxed: tx.taxed,
                                after_tax: tx.after_tax
                            });
                        });

                        data.forEach(u => {
                            u.balance = balanceMap[u.id] || 0;
                            u.totalSpend = spendMap[u.id] || 0;
                            u.totalSpendMinusTax = (spendMap[u.id] || 0) - (taxMap[u.id] || 0);
                            u.payment_history = historyMap[u.id] || [];
                        });
                    }
                } catch (e) {
                    console.warn("[DB] ledger enrichment failed:", e.message);
                }
            }

            // Map snake_case back to camelCase for UI consistency
            if (data) {
                const mappedData = data.map(row => {
                    const mapped = { ...row };
                    if (row.created_at) mapped.dateAdded = row.created_at.split('T')[0];
                    if (row.date_added) mapped.dateAdded = row.date_added;
                    if (row.assigned_at) mapped.dateAdded = row.assigned_at.split('T')[0];

                    if (tableName === 'lessons') {
                        mapped.dayOfWeek = row.day_of_week;
                        mapped.mSum = row.m_sum;
                        mapped.nextDatetime = row.next_datetime;
                        mapped.homeworkToCheck = row.homework_to_check;
                        // Also keep snake_case for direct access
                        mapped.day_of_week = row.day_of_week;
                        mapped.m_sum = row.m_sum;
                        mapped.next_datetime = row.next_datetime;
                        mapped.homework_to_check = row.homework_to_check;
                        mapped.lesson_words = row.lesson_words || [];
                        mapped.wordlist_added_snapshot = row.wordlist_added_snapshot ?? null;
                        mapped.wordlistAddedSnapshot = row.wordlist_added_snapshot ?? null;
                    }

                    if (tableName === 'checklistAssessments') {
                        mapped.templateId = row.template_id;
                        mapped.studentId = row.student_id;
                        mapped.studentName = row.student_name;
                        mapped.totalScore = row.total_score;
                        mapped.criteriaScores = row.criteria_scores;
                        mapped.overallNote = row.overall_note;
                    }

                    if (tableName === 'userProgress') {
                        const blob = row.progress || {};
                        mapped.rewards = (row.rewards?.length || 0) >= (blob.rewards?.length || 0)
                            ? (row.rewards || []) : (blob.rewards || []);
                        mapped.user_words = (row.user_words?.length || 0) >= (blob.user_words?.length || 0)
                            ? (row.user_words || []) : (blob.user_words || []);
                        mapped.course_stats = mergeCourseStats(row.course_stats, blob.course_stats);
                        const resolvedCoins = resolveAuthoritativeCoins(
                            row.coins,
                            row.coins_history,
                            blob.coins_history,
                            blob.coins,
                        );
                        mapped.coins = resolvedCoins.coins;
                        mapped.coins_history = resolvedCoins.coins_history;
                        mapped.purchase_history = (row.purchase_history && row.purchase_history.length > 0)
                            ? row.purchase_history : (blob.purchase_history || []);
                    }

                    if (tableName === 'storeItems') {
                        mapped.allowedStudents = row.allowed_students || [];
                    }

                    if (tableName === 'users') {
                        mapped.status = normalizeAccountStatus(mapped);
                    }

                    if (['globalWords', 'groupWords', 'showGames'].includes(tableName)) {
                        return normalizeWordTaxonomy(mapped);
                    }
                    return mapped;
                });

                // Cache the mapped data (skip for userProgress and userWords)
                if (tableName !== 'userProgress' && tableName !== 'userWords') {
                    const ttl = (tableName === 'siteText' || tableName === 'courses' || tableName === 'siteCourses' || tableName === 'sitePriceCategories') 
                        ? CACHE_TTL_LONG 
                        : CACHE_TTL;
                    const toCache = tableName === 'siteText' ? repairSiteTextRows(mappedData) : mappedData;
                    setCachedData(cacheKey, toCache, ttl);
                    return toCache;
                }

                return tableName === 'siteText' ? repairSiteTextRows(mappedData) : mappedData;
            }

            return data || [];
        };

        // Store the promise in inFlightRequests for deduplication
        const requestPromise = executeFetch();
        inFlightRequests.set(cacheKey, requestPromise);

        try {
            const result = await requestPromise;
            return tableName === 'siteText' ? repairSiteTextRows(result) : result;
        } finally {
            // Remove from in-flight requests after completion (success or failure)
            inFlightRequests.delete(cacheKey);
        }
    },

    async getFullBackupData() {
        /**
         * Fetches ALL data from ALL defined tables for complete system archival.
         * Returns an object where keys are UI table names and values are arrays of rows.
         */
        const results = {};
        const uiTables = Object.keys(tableMap);
        
        console.log(`[Backup] Starting full data fetch for ${uiTables.length} entities...`);
        
        for (const tableName of uiTables) {
            try {
                // We use getTable to ensure UI-level consistency (camelCase mapping, profile enrichment, etc.)
                const data = await this.getTable(tableName);
                const dbTable = getSBTable(tableName);
                results[tableName] = sanitizePlannerBackupRows(dbTable, data);
                console.log(`[Backup] Successfully fetched ${tableName} (${Array.isArray(data) ? data.length : 'object'} results)`);
            } catch (err) {
                console.error(`[Backup] Failed to fetch table "${tableName}":`, err);
                results[tableName] = { error: err.message };
            }
        }
        
        return results;
    },

    /**
     * Planner money tools as discrete JSON snapshots (from site_settings keys).
     * Used by Maintenance → Download Full Archive under data/money/.
     */
    async getPlannerMoneyBackupSnapshots() {
        const out = {};
        for (const entry of PLANNER_MONEY_BACKUP_ENTRIES) {
            try {
                const val = await this.getSiteSetting(entry.settingKey, null);
                if (val !== null && val !== undefined) {
                    out[entry.file] = val;
                }
            } catch (err) {
                console.warn(`[Backup] money ${entry.settingKey}:`, err.message);
            }
        }
        console.log(`[Backup] Money tools: ${Object.keys(out).length} snapshot(s)`);
        return out;
    },

    async addItem(tableName, item) {
        const sbTable = getSBTable(tableName);

        // For tables that use UUIDs, we keep the ID if it's a valid UUID to maintain stable state.
        // For ALL other tables, we strip the item.id to let the DB generate its own (e.g. serial integer).
        const uuidTables = ['lessons', 'users', 'admins', 'groups', 'courses', 'messages', 'userProgress', 'checklistTemplates', 'checklistAssessments', 'showGames', 'groupWords', 'globalWords', 'siteCourses', 'sitePriceCategories'];
        const isUUID = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
        const newUuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);

        // messages.id is text with no DB default — always supply a UUID when missing.
        let row = item;
        if (uuidTables.includes(tableName) && !isUUID(item?.id)) {
            row = { ...item, id: newUuid() };
        }

        // Exclude ID if:
        // 1. Table is NOT in uuidTables
        // 2. OR it is in uuidTables but the provided ID is just a temporary non-UUID string
        const shouldExcludeId = row.id && (!uuidTables.includes(tableName) || !isUUID(row.id));

    const payload = normalizeNewWordPayload(tableName, preparePayload(tableName, row, shouldExcludeId));

        // Auto-assign role for profiles
        if (tableName === 'users') payload.role = 'student';
        if (tableName === 'admins') payload.role = 'teacher';

        const { data, error } = await supabase
            .from(sbTable)
            .insert([payload])
            .select();

        if (error) {
            console.error('Add Error:', error);
            const err = new Error(error.message);
            err.details = error.details;
            throw err;
        }

        this.clearCache(tableName);

        const addedItem = data?.[0];
        if (tableName === 'lessons' && addedItem) {
            return {
                ...addedItem,
                dayOfWeek: addedItem.day_of_week,
                mSum: addedItem.m_sum,
                nextDatetime: addedItem.next_datetime,
                homeworkToCheck: addedItem.homework_to_check,
                // Also keep snake_case
                day_of_week: addedItem.day_of_week,
                m_sum: addedItem.m_sum,
                next_datetime: addedItem.next_datetime,
                homework_to_check: addedItem.homework_to_check,
                lesson_words: addedItem.lesson_words || [],
                wordlist_added_snapshot: addedItem.wordlist_added_snapshot ?? null,
                wordlistAddedSnapshot: addedItem.wordlist_added_snapshot ?? null,
            };
        }
        return addedItem || null;
    },

    async updateItem(tableName, id, item, options = {}) {
        const sbTable = getSBTable(tableName);
        let dataToUpdate = preparePayload(tableName, item, true);
        dataToUpdate = normalizeNewWordPayload(tableName, dataToUpdate);
        if (['globalWords', 'groupWords', 'showGames'].includes(tableName) && typeof dataToUpdate.english === 'string') {
            dataToUpdate = withPrepositionFromEnglish(dataToUpdate);
        }
        this.clearCache(tableName);

        // Safety: If after stripping fields, we have nothing to update, just return
        if (Object.keys(dataToUpdate).length === 0) {
            console.log(`[DB Update] Skip for ${tableName}: No valid fields to save.`);
            return;
        }

        const groupWordGroupId = tableName === 'groupWords'
            ? (options.groupId || item.group_id || item.groupId || dataToUpdate.group_id)
            : null;
        if (tableName === 'groupWords' && !groupWordGroupId) {
            throw new Error('groupId is required to update group words');
        }

        // PRE-FETCH: For group words difficulty tracking, get old status before update
        let oldGroupWordStatus = null;
        if (tableName === 'groupWords' && item.status === 2) {
            try {
                const { data: oldRow } = await supabase
                    .from('group_words')
                    .select('status')
                    .eq('id', id)
                    .eq('group_id', groupWordGroupId)
                    .single();
                oldGroupWordStatus = oldRow?.status;
            } catch (e) {
                // Non-critical, proceed with update
            }
        }

        let updateQuery = supabase
            .from(sbTable)
            .update(dataToUpdate, { count: 'exact' })
            .eq('id', id);
        if (tableName === 'groupWords') {
            updateQuery = updateQuery.eq('group_id', groupWordGroupId);
        }

        const { error, count } = await updateQuery;

        if (error) {
            console.error(`Update Error (${tableName}):`, error);
            throw error;
        }

        if (count === 0) {
            throw new Error(`Database reported 0 rows updated for ${tableName}. Possible permission (RLS) issue.`);
        }

        // Regression Tracker: Group Words (Diff Level 2 = KEEP)
        if (tableName === 'groupWords' && item.status === 2 && oldGroupWordStatus !== 2) {
            // We pass dataToUpdate to lookup fallback if needed (though groupWords should have UUIDs)
            incrementGlobalDifficulty(id, dataToUpdate);
        }
        // All financial updates must now use db.logPayment() to ensure ledger consistency.
    },

    async upsertItems(tableName, items, options = {}) {
        const sbTable = getSBTable(tableName);
        const { normalizeNewWords = false } = options;
        const payloads = items.map(item => {
            const prepared = preparePayload(tableName, item);
            const normalized = (normalizeNewWords || ['globalWords', 'groupWords', 'showGames'].includes(tableName))
                ? normalizeNewWordPayload(tableName, prepared)
                : prepared;
            if (['globalWords', 'groupWords', 'showGames'].includes(tableName) && typeof normalized?.english === 'string') {
                return withPrepositionFromEnglish(normalized);
            }
            return normalized;
        });
        const onConflict = tableName === 'groupWords' ? 'group_id,id' : 'id';
        const { error, count } = await supabase
            .from(sbTable)
            .upsert(payloads, { onConflict, count: 'exact' });
        if (error) {
            console.error('Bulk Upsert Error:', error);
            throw error;
        }
        if (count === 0 && items.length > 0) {
            throw new Error(`Database reported 0 rows updated/added. Possible permission (RLS) issue on ${sbTable}.`);
        }
        return count;
    },

    /**
     * Find word rows whose English matches any of the given keys (case-insensitive, exact).
     * Keys should already be normalized (trim + lower + apostrophe).
     */
    async findWordRowsByEnglishKeys(tableName, englishKeys = [], options = {}) {
        const keys = [...new Set(
            (englishKeys || []).map((k) => String(k || '').trim().toLowerCase()).filter(Boolean),
        )];
        if (!keys.length) return [];
        if (!['globalWords', 'groupWords', 'showGames'].includes(tableName)) {
            throw new Error(`findWordRowsByEnglishKeys: unsupported table ${tableName}`);
        }
        const sbTable = getSBTable(tableName);
        const out = [];
        const seen = new Set();
        for (const key of keys) {
            let q = supabase.from(sbTable).select('*').ilike('english', key);
            if (tableName === 'groupWords' && options.groupId) {
                q = q.eq('group_id', options.groupId);
            }
            const { data, error } = await q;
            if (error) {
                console.error('[DB] findWordRowsByEnglishKeys:', error);
                throw error;
            }
            (data || []).forEach((row) => {
                const id = String(row.id);
                if (seen.has(id)) return;
                // Client-side guard: apostrophe / spacing variants
                const rowKey = String(row.english || '').trim().toLowerCase()
                    .replace(/[\u2018\u2019\u201A\u201B\u2032\u02BC\uFF07\u0060\u00B4]/g, "'");
                if (rowKey !== key && rowKey.replace(/'/g, "'") !== key) {
                    // still accept ilike exact hits
                }
                seen.add(id);
                out.push(row);
            });
        }
        return out;
    },

    async deleteItem(tableName, id, options = {}) {
        const sbTable = getSBTable(tableName);
        if (tableName === 'groupWords' && !options.groupId) {
            throw new Error('groupId is required to delete group words');
        }
        console.log(`[DB] DELETE from ${sbTable} where id = ${id}${options.groupId ? ` and group_id = ${options.groupId}` : ''}`);
        let deleteQuery = supabase
            .from(sbTable)
            .delete({ count: 'exact' })
            .eq('id', id);
        if (tableName === 'groupWords') {
            deleteQuery = deleteQuery.eq('group_id', options.groupId);
        }

        const { error, count } = await deleteQuery;
        
        console.log(`[DB] DELETE result for ${id}:`, { error, count });

        if (error) {
            console.error(`Error deleting from ${sbTable}:`, error);
            throw error;
        }

        this.clearCache(tableName);

        // Only legitimate full wipe of a student's words: remove progress when the student profile is deleted.
        if (tableName === 'users' && count > 0) {
            const { error: progressErr } = await supabase
                .from('user_progress')
                .delete()
                .eq('user_id', id);
            if (progressErr) {
                console.error('[DB] Failed to delete user_progress after student delete:', progressErr);
                throw progressErr;
            }
        }

        return count;
    },

    /**
     * Push Global Words (by Word ID) into Show Games, Group Words, and every student
     * user_progress copy. Always reads masters + targets from the DB — never depends on
     * which Teacher Dashboard tab has data loaded in memory.
     * Preserves student status / dateAdded / starred / statusDate / student_added / source,
     * and Group/Show Games Diff Level status (+ group_id).
     */
    async syncWordsFromGlobal(ids, options = {}) {
        const { masterOverrides = null } = options;
        const idList = [...new Set((Array.isArray(ids) ? ids : [])
            .filter(Boolean)
            .map((id) => String(id)))];
        if (idList.length === 0) {
            return { masters: 0, studentsUpdated: 0, showGamesUpdated: 0, groupWordsUpdated: 0 };
        }

        await ensureAuthSession();

        const CHUNK = 100;
        let masterRows = [];
        for (let i = 0; i < idList.length; i += CHUNK) {
            const batch = idList.slice(i, i + CHUNK);
            const { data, error } = await this._withTimeout(
                supabase.from('global_words').select('*').in('id', batch),
                30000,
                'syncWordsFromGlobal:global'
            );
            if (error) throw error;
            masterRows = masterRows.concat(data || []);
        }

        let masterWords = masterRows.map((row) => ({
            ...row,
            dateAdded: row.created_at
                ? String(row.created_at).split('T')[0]
                : (row.date_added || undefined),
        }));

        if (masterOverrides && typeof masterOverrides === 'object') {
            masterWords = masterWords.map((w) => ({
                ...w,
                ...(masterOverrides[w.id] || masterOverrides[String(w.id)] || {}),
            }));
        }

        if (masterWords.length === 0) {
            return {
                masters: 0,
                studentsUpdated: 0,
                showGamesUpdated: 0,
                groupWordsUpdated: 0,
                missing: true,
            };
        }

        const masterById = new Map(masterWords.map((m) => [String(m.id), m]));
        const syncIdSet = new Set(masterWords.map((m) => String(m.id)));

        let showGamesUpdated = 0;
        {
            let games = [];
            for (let i = 0; i < idList.length; i += CHUNK) {
                const batch = idList.slice(i, i + CHUNK);
                const { data, error } = await this._withTimeout(
                    supabase.from('show_games').select('*').in('id', batch),
                    30000,
                    'syncWordsFromGlobal:show_games'
                );
                if (error) throw error;
                games = games.concat(data || []);
            }
            const toUpsert = games
                .filter((g) => syncIdSet.has(String(g.id)))
                .map((gw) => {
                    const master = masterById.get(String(gw.id));
                    return { ...master, status: gw.status, id: gw.id };
                });
            if (toUpsert.length > 0) {
                await this.upsertItems('showGames', toUpsert);
                showGamesUpdated = toUpsert.length;
            }
        }

        let groupWordsUpdated = 0;
        {
            let rows = [];
            for (let i = 0; i < idList.length; i += CHUNK) {
                const batch = idList.slice(i, i + CHUNK);
                const { data, error } = await this._withTimeout(
                    supabase.from('group_words').select('*').in('id', batch),
                    30000,
                    'syncWordsFromGlobal:group_words'
                );
                if (error) throw error;
                rows = rows.concat(data || []);
            }
            const toUpsert = rows
                .filter((g) => syncIdSet.has(String(g.id)))
                .map((gw) => {
                    const master = masterById.get(String(gw.id));
                    return {
                        ...master,
                        group_id: gw.group_id,
                        status: gw.status,
                        id: gw.id,
                    };
                });
            if (toUpsert.length > 0) {
                await this.upsertItems('groupWords', toUpsert);
                groupWordsUpdated = toUpsert.length;
            }
        }

        let studentsUpdated = 0;
        {
            const pageSize = 40;
            let from = 0;
            for (;;) {
                const { data: page, error } = await this._withTimeout(
                    supabase
                        .from('user_progress')
                        .select('user_id, user_words, progress')
                        .order('user_id', { ascending: true })
                        .range(from, from + pageSize - 1),
                    120000,
                    `syncWordsFromGlobal:user_progress:${from}`
                );
                if (error) throw error;
                const rows = page || [];
                if (rows.length === 0) break;

                const touched = await Promise.all(rows.map(async (p) => {
                    const blob = (p.progress && typeof p.progress === 'object') ? p.progress : {};
                    const columnWords = Array.isArray(p.user_words) ? p.user_words : [];
                    const blobWords = Array.isArray(blob.user_words) ? blob.user_words : [];
                    const userWords = sanitizeUserWords([...columnWords, ...blobWords]);
                    let changed = false;
                    const updatedWords = userWords.map((w) => {
                        const wid = String(w?.id || '');
                        if (!syncIdSet.has(wid)) return w;
                        const master = masterById.get(wid);
                        if (!master) return w;
                        changed = true;
                        return {
                            ...master,
                            status: w.status,
                            dateAdded: w.dateAdded,
                            starred: w.starred,
                            statusDate: w.statusDate,
                            student_added: w.student_added,
                            source: w.source,
                            id: w.id,
                        };
                    });
                    if (!changed) return 0;
                    const { error: upErr } = await supabase
                        .from('user_progress')
                        .update({
                            user_words: updatedWords,
                            progress: { ...blob, user_words: updatedWords },
                        })
                        .eq('user_id', p.user_id);
                    if (upErr) throw upErr;
                    return 1;
                }));
                studentsUpdated += touched.reduce((sum, n) => sum + n, 0);

                if (rows.length < pageSize) break;
                from += pageSize;
            }
        }

        this.clearCache('showGames');
        this.clearCache('groupWords');
        this.clearCache('userProgress');

        return {
            masters: masterWords.length,
            studentsUpdated,
            showGamesUpdated,
            groupWordsUpdated,
        };
    },

    async assignWordsToUser(userId, wordsToAssign, options = {}) {
        const { duplicatePolicy = 'skip' } = options;

        const { data: records, error: fetchError } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', userId);

        if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

        const record = records?.[0];
        const blob = record?.progress || {};
        
        // Use our new sanitizer to merge existing sources safely
        const columnWords = record?.user_words || [];
        const blobWords = blob.user_words || [];
        const currentWords = sanitizeUserWords([...columnWords, ...blobWords]);

        const existingIds = new Set(currentWords.map(w => getStableWordId(w)));
        const assignIds = new Set(wordsToAssign.map(w => getStableWordId(w)));
        const nowIso = new Date().toISOString();

        let workingWords = currentWords;

        if (duplicatePolicy === 'update') {
            workingWords = currentWords.map(w => {
                const key = getStableWordId(w);
                if (assignIds.has(key) && existingIds.has(key)) {
                    return {
                        ...w,
                        status: 'NOT STARTED',
                        statusDate: nowIso,
                    };
                }
                return w;
            });
        }

        const newWords = wordsToAssign
            .filter(w => !existingIds.has(getStableWordId(w)))
            .map(w => normalizeWordAttribution({
                // Preserve original ID to enable sync functionality
                id: w.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9)),
                ...w,
                dateAdded: new Date().toISOString().split('T')[0],
                status: (w.status !== undefined && w.status !== null) ? w.status : 'NOT STARTED'
            }));

        if (newWords.length === 0 && duplicatePolicy !== 'update') return;
        if (newWords.length === 0 && duplicatePolicy === 'update' && !wordsToAssign.some(w => existingIds.has(getStableWordId(w)))) {
            return;
        }

        const finalWords = sanitizeUserWords([...workingWords, ...newWords]);

        const payload = {
            user_id: userId,
            user_words: finalWords,
            // SYNC RESTORED: Save to both for safety, but sanitized
            progress: { ...blob, user_words: finalWords }
        };
        if (record?.id) payload.id = record.id;

        const { error } = await supabase
            .from('user_progress')
            .upsert(payload, { onConflict: 'user_id' });

        if (error) throw error;
    },

    /**
     * Student self-add from Wordlist: course Other, marked student_added.
     * Mirrors each new row into global_words (Words database) like teacher Group/User adds.
     * Enter Training modes / Looking Back / Paid pools like other words, but WordGame
     * awards coins only (Stars currency forced to 0).
     * @returns {{ added: number, words: object[] }}
     */
    async addStudentOwnWords(userId, rows = [], options = {}) {
        if (!userId) throw new Error('userId is required');
        const list = (Array.isArray(rows) ? rows : [])
            .map((r) => ({
                english: String(r?.english || '').trim(),
                russian: String(r?.russian || '').trim(),
            }))
            .filter((r) => r.english && r.russian);
        if (list.length === 0) return { added: 0, words: [] };

        await ensureAuthSession();

        let groupId = options.groupId || null;
        if (!groupId) {
            try {
                groupId = await this.resolveDefaultTrainingGroupId(userId);
            } catch {
                groupId = null;
            }
        }

        const { data: records } = await supabase
            .from('user_progress')
            .select('user_words, progress')
            .eq('user_id', userId);
        const record = records?.[0];
        const before = sanitizeUserWords([
            ...(record?.user_words || []),
            ...((record?.progress && record.progress.user_words) || []),
        ]);
        const beforeIds = new Set(before.map((w) => getStableWordId(w)));

        const newUuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);
        const theme = String(options.theme || 'Imported').trim() || 'Imported';

        const wordsToAssign = list.map((r) => {
            const id = newUuid();
            const normalized = normalizeNewWordPayload('globalWords', {
                id,
                english: r.english,
                russian: r.russian,
                theme,
                course: OTHER_COURSE,
                module: null,
                lesson: null,
            });
            return normalizeWordAttribution({
                ...normalized,
                id,
                student_added: true,
                source: 'student',
                status: 'NOT STARTED',
                group_id: groupId || undefined,
                starred: false,
            });
        });

        // Mirror to Words database first (same stable UUID as user_words), matching teacher flow.
        const globalPayloads = wordsToAssign.map((w) => ({
            id: w.id,
            english: w.english,
            russian: w.russian,
            theme: w.theme || theme,
            subtheme: w.subtheme || '',
            subtype: w.subtype || '',
            theme_pairs: getWordThemePairs(w),
            subtypes: getWordSubtypes(w),
            course: OTHER_COURSE,
            module: null,
            lesson: null,
            ...(w.preposition ? { preposition: w.preposition } : {}),
        }));
        try {
            await this.addGlobalWords(globalPayloads);
            this.clearCache('globalWords');
        } catch (globalErr) {
            console.error(
                '[DB] addStudentOwnWords: global_words mirror failed '
                + '(apply migrations/20260809_student_own_words_global_insert.sql if students lack INSERT):',
                globalErr,
            );
            // Still assign to the student; teacher can re-import / sync later if needed.
        }

        await this.assignWordsToUser(userId, wordsToAssign, { duplicatePolicy: 'skip' });

        const words = await this.getUserWords(userId, { failOpen: false });
        const added = words.filter((w) => !beforeIds.has(getStableWordId(w)) && isStudentAddedWord(w)).length;

        return { added, words };
    },

    /**
     * One-shot load for WordGame: words + reward fields so per-word saves skip GET.
     */
    async loadWordGameSessionCache(userId) {
        if (!userId) throw new Error('userId is required');
        await ensureAuthSession();

        const { data: record, error } = await supabase
            .from('user_progress')
            .select('id, user_id, user_words, progress, coins, coins_history, purchase_history, rewards, course_stats')
            .eq('user_id', userId)
            .single();

        if (error) throw error;
        if (!record) throw new Error('No user progress record');

        const blob = record.progress && typeof record.progress === 'object' ? record.progress : {};
        const words = sanitizeUserWords([...(record.user_words || []), ...(blob.user_words || [])]);
        const rewards = (record.rewards && Array.isArray(record.rewards) && record.rewards.length >= (blob.rewards?.length || 0))
            ? record.rewards
            : (blob.rewards || []);

        const resolvedCoins = resolveAuthoritativeCoins(
            record.coins,
            record.coins_history,
            blob.coins_history,
            blob.coins,
        );

        return {
            recordId: record.id,
            userId,
            words,
            progressBlob: { ...blob, user_words: words },
            coins: resolvedCoins.coins,
            coins_history: resolvedCoins.coins_history,
            purchase_history: (record.purchase_history && record.purchase_history.length > 0)
                ? record.purchase_history
                : (blob.purchase_history || []),
            rewards,
            course_stats: mergeCourseStats(record.course_stats, blob.course_stats),
            daily_quests: blob.daily_quests && typeof blob.daily_quests === 'object' ? blob.daily_quests : null,
        };
    },

    async updateUserWordStatus(userId, wordId, newStatus, sessionCache = null) {
        const targetKey = normalizeWordTargetKey(wordId);
        if (!targetKey || targetKey === 'null-word') {
            console.warn('[DB] updateUserWordStatus: invalid wordId', wordId);
            throw new Error('Word id is required to update status');
        }

        await ensureAuthSession();

        const upsertWords = async (recordId, blob, finalWords) => {
            const persistedDailyQuests = await fetchPersistedDailyQuests({ recordId });
            const progressPayload = {
                ...blob,
                user_words: finalWords,
                ...(persistedDailyQuests ? { daily_quests: persistedDailyQuests } : {}),
            };
            const { error, count } = await supabase
                .from('user_progress')
                .upsert({
                    id: recordId,
                    user_id: userId,
                    user_words: finalWords,
                    progress: progressPayload,
                }, { onConflict: 'id', count: 'exact' });

            if (error) throw error;

            if (count === 0) {
                console.error('[DB] updateUserWordStatus: RLS failure - count === 0', {
                    userId,
                    wordId,
                    targetKey,
                    ...getClientAuthHints(),
                    timestamp: new Date().toISOString(),
                });
                throw new Error('Database reported 0 rows updated. Please log out and sign in again, then retry.');
            }

            removeWordSave(userId, targetKey);
            return progressPayload;
        };

        if (sessionCache?.recordId && Array.isArray(sessionCache.words)) {
            try {
                const { words: finalWords } = applyWordStatusUpdate(sessionCache.words, wordId, newStatus);
                sessionCache.words = finalWords;
                const progressPayload = await upsertWords(sessionCache.recordId, sessionCache.progressBlob, finalWords);
                sessionCache.progressBlob = progressPayload;
                if (progressPayload.daily_quests) {
                    sessionCache.daily_quests = progressPayload.daily_quests;
                }
                return;
            } catch (err) {
                if (err?.message === 'Word not found in student vocabulary') {
                    console.warn('[DB] updateUserWordStatus: no user_word row matches id', targetKey);
                }
                throw err;
            }
        }

        const { data: records, error: fetchError } = await supabase
            .from('user_progress')
            .select('id, user_id, user_words, progress')
            .eq('user_id', userId);

        if (fetchError) throw fetchError;
        if (!records || records.length === 0) return;

        const record = records[0];
        const blob = record.progress || {};
        const words = sanitizeUserWords([...(record.user_words || []), ...(blob.user_words || [])]);

        let finalWords;
        try {
            ({ words: finalWords } = applyWordStatusUpdate(words, wordId, newStatus));
        } catch (err) {
            if (err?.message === 'Word not found in student vocabulary') {
                console.warn('[DB] updateUserWordStatus: no user_word row matches id', targetKey);
            }
            throw err;
        }

        await upsertWords(record.id, blob, finalWords);

        if (sessionCache) {
            sessionCache.recordId = record.id;
            sessionCache.words = finalWords;
            const freshDq = await fetchPersistedDailyQuests({ recordId: record.id });
            sessionCache.progressBlob = {
                ...blob,
                user_words: finalWords,
                ...(freshDq ? { daily_quests: freshDq } : {}),
            };
            if (freshDq) sessionCache.daily_quests = freshDq;
        }
    },

    /**
     * Set starred flag on one or many user words (JSONB field — no SQL migration).
     * @param {string} userId
     * @param {string|string[]} wordIds
     * @param {boolean} starred
     * @returns {Promise<object[]>} updated sanitized word list
     */
    async setUserWordsStarred(userId, wordIds, starred) {
        const ids = (Array.isArray(wordIds) ? wordIds : [wordIds])
            .map((id) => normalizeWordTargetKey(id))
            .filter((id) => id && id !== 'null-word');
        if (!userId || !ids.length) return [];

        const wantStarred = !!starred;
        const idSet = new Set(ids);

        await ensureAuthSession();

        const { data: records, error: fetchError } = await supabase
            .from('user_progress')
            .select('id, user_id, user_words, progress')
            .eq('user_id', userId);

        if (fetchError) throw fetchError;
        if (!records || records.length === 0) return [];

        const record = records[0];
        const blob = record.progress || {};
        const words = sanitizeUserWords([...(record.user_words || []), ...(blob.user_words || [])]);

        let changed = 0;
        const updated = words.map((w) => {
            const id = getStableWordId(w);
            if (!idSet.has(id)) return w;
            if (!!w.starred === wantStarred) return w;
            changed += 1;
            return { ...w, starred: wantStarred };
        });

        if (!changed) return sanitizeUserWords(updated);

        const finalWords = sanitizeUserWords(updated);
        const persistedDailyQuests = await fetchPersistedDailyQuests({ recordId: record.id });
        const progressPayload = {
            ...blob,
            user_words: finalWords,
            ...(persistedDailyQuests ? { daily_quests: persistedDailyQuests } : {}),
        };

        const { error, count } = await supabase
            .from('user_progress')
            .upsert({
                id: record.id,
                user_id: userId,
                user_words: finalWords,
                progress: progressPayload,
            }, { onConflict: 'id', count: 'exact' });

        if (error) throw error;
        if (count === 0) {
            throw new Error('Database reported 0 rows updated. Please log out and sign in again, then retry.');
        }

        this.clearCache('userProgress');
        return finalWords;
    },

    /**
     * Persist Wordlist "star new words?" prompt state on progress JSON (no user_words touch).
     * @param {string} userId
     * @param {{ dismissedIds?: string[], activeIds?: string[] | null }} promptState
     */
    async saveNewWordStarPrompt(userId, promptState) {
        if (!userId) return null;
        await ensureAuthSession();

        const { data: records, error: fetchError } = await supabase
            .from('user_progress')
            .select('id, user_id, progress')
            .eq('user_id', userId);

        if (fetchError) throw fetchError;
        if (!records?.length) return null;

        const record = records[0];
        const blob = record.progress && typeof record.progress === 'object' ? { ...record.progress } : {};
        const next = {
            dismissedIds: Array.isArray(promptState?.dismissedIds)
                ? promptState.dismissedIds.map(String).filter(Boolean)
                : [],
            activeIds: Array.isArray(promptState?.activeIds) && promptState.activeIds.length
                ? promptState.activeIds.map(String).filter(Boolean)
                : null,
        };
        blob.new_word_star_prompt = next;

        const { error, count } = await supabase
            .from('user_progress')
            .upsert({
                id: record.id,
                user_id: userId,
                progress: blob,
            }, { onConflict: 'id', count: 'exact' });

        if (error) throw error;
        if (count === 0) {
            throw new Error('Database reported 0 rows updated. Please log out and sign in again, then retry.');
        }

        this.clearCache('userProgress');
        return next;
    },

    async flushPendingWordSaves(userId) {
        if (!userId) return { flushed: 0, failed: 0 };
        const pending = peekWordSaveQueue(userId);
        if (!pending.length) return { flushed: 0, failed: 0 };

        let flushed = 0;
        let failed = 0;
        for (const item of pending) {
            try {
                await this.updateUserWordStatus(userId, item.wordId, item.newStatus);
                flushed++;
            } catch (err) {
                failed++;
                console.warn('[DB] flushPendingWordSaves: item failed', item.wordId, err?.message);
                enqueueWordSave(userId, item);
                break;
            }
        }
        return { flushed, failed, remaining: peekWordSaveQueue(userId).length };
    },

    async updateUserWord(userId, targetId, updatedWord) {
        const uid = userId;

        // Ensure JWT is attached before RLS-heavy write (prevents Safari/iOS auth issues)
        try {
            await supabase.auth.getSession();
        } catch (sessionErr) {
            console.warn('[DB] updateUserWord: getSession failed', sessionErr?.message);
        }

        const { data: records, error: fetchError } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', uid);

        if (fetchError) throw fetchError;
        if (!records || records.length === 0) throw new Error(`No record found for user ${uid}`);

        const record = records[0];
        const blob = record.progress || {};
        const words = sanitizeUserWords([...(record.user_words || []), ...(blob.user_words || [])]);

        const targetKey = normalizeWordTargetKey(targetId);
        if (!targetKey || targetKey === 'null-word') {
            throw new Error('Word id is required to update a word');
        }

        const updated = words.map(w => {
            const currentId = getStableWordId(w);
            if (currentId === targetKey) {
                const { _dbIdx, isOptimistic, ...cleanItem } = updatedWord;
                const oldStatus = w.status;
                const newStatus = cleanItem.status;
                
                if (oldStatus !== newStatus) {
                    cleanItem.statusDate = new Date().toISOString();
                }
                
                if (isStatusRegression(oldStatus, newStatus)) {
                    incrementGlobalDifficulty(targetKey, w);
                }

                return cleanItem;
            }
            return w;
        });

        const finalWords = sanitizeUserWords(updated);

        const { error } = await supabase
            .from('user_progress')
            .upsert({
                id: record.id,
                user_id: uid,
                user_words: finalWords,
                progress: { ...blob, user_words: finalWords }
            }, { onConflict: 'id' });

        if (error) throw error;
    },

    async bulkUpdateUserWords(userId, updates) {
        const uid = userId;

        // Ensure JWT is attached before RLS-heavy write (prevents Safari/iOS auth issues)
        try {
            await supabase.auth.getSession();
        } catch (sessionErr) {
            console.warn('[DB] bulkUpdateUserWords: getSession failed', sessionErr?.message);
        }

        const { data: records, error: fetchError } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', uid);

        if (fetchError) throw fetchError;
        if (!records || records.length === 0) throw new Error(`No record found for user ${uid}`);

        const record = records[0];
        const blob = record.progress || {};
        const words = sanitizeUserWords([...(record.user_words || []), ...(blob.user_words || [])]);

        const updateMap = new Map(updates.map(u => [normalizeWordTargetKey(u.id), u.item]));
        const updated = words.map(w => {
            const currentId = getStableWordId(w);
            if (updateMap.has(currentId)) {
                const { _dbIdx, isOptimistic, ...cleanItem } = updateMap.get(currentId);
                
                if (w.status !== cleanItem.status) {
                    cleanItem.statusDate = new Date().toISOString();
                }
                
                return cleanItem;
            }
            return w;
        });

        const finalWords = sanitizeUserWords(updated);

        const { error } = await supabase
            .from('user_progress')
            .upsert({
                id: record.id,
                user_id: uid,
                user_words: finalWords,
                progress: { ...blob, user_words: finalWords }
            }, { onConflict: 'id' });

        if (error) throw error;
    },

    async bulkDeleteUserWords(_userId, _indices) {
        throw new Error(USER_WORDS_DELETE_FORBIDDEN);
    },

    async deleteUserWord(_userId, _wordIdx) {
        throw new Error(USER_WORDS_DELETE_FORBIDDEN);
    },

    async deleteUserWordsByIds(_userId, _wordIds) {
        throw new Error(USER_WORDS_DELETE_FORBIDDEN);
    },

    async updateUserCredentials(userId, { email, password, role, login } = {}) {
        let newEmail = normalizeAcademyAuthEmail({ login, email });

        if (!newEmail && (password || role)) {
            const { data: profile } = await supabase
                .from('profiles')
                .select('login, email')
                .eq('id', userId)
                .maybeSingle();
            newEmail = normalizeAcademyAuthEmail(profile || {});
        }

        const { error } = await this.supabase.rpc('admin_update_user', {
            target_user_id: userId,
            new_email: newEmail || null,
            new_password: password || null,
            new_role: role || null,
        });
        if (error) throw error;
    },

    async getPortalAccount(userId) {
        if (!userId) return null;
        try {
            const q = supabase
                .from('portal_accounts')
                .select('user_id, token_balance, created_at, updated_at')
                .eq('user_id', userId)
                .single();
            const { data, error } = await this._withTimeout(q, 15000, 'getPortalAccount');
            if (error && error.code !== 'PGRST116') {
                console.error('[DB] getPortalAccount:', error.message);
            }
            return data || null;
        } catch (err) {
            console.error('[DB] getPortalAccount failed:', err.message);
            return null;
        }
    },

    async getPortalUsersForTeacher({ limit = 500 } = {}) {
        const q = supabase
            .from('profiles')
            .select('id, email, name, surname, role, portal_accounts(token_balance)')
            .eq('role', 'portal')
            .order('created_at', { ascending: false })
            .limit(limit);

        const { data, error } = await this._withTimeout(q, 30000, 'getPortalUsersForTeacher');
        if (error) throw error;

        return (data || []).map((row) => ({
            id: row.id,
            email: row.email || '',
            name: row.name || '',
            surname: row.surname || '',
            tokenBalance: row.portal_accounts?.token_balance ?? 0,
        }));
    },

    async teacherSetPortalTokenBalance(targetUserId, newBalance, reason = 'Manual adjustment') {
        const { data, error } = await this.supabase.rpc('teacher_set_portal_token_balance', {
            target_user_id: targetUserId,
            new_balance: parseInt(newBalance, 10) || 0,
            reason,
        });
        if (error) throw error;
        return data;
    },

    async setManualStarsForUser(userId, groupId, newTotal, reason = 'Attendance adjustment') {
        const progress = await this.getUserProgress(userId);
        let course_stats = { ...(progress.course_stats || {}) };
        const current = course_stats[groupId] || { currency: 0, rewards: [] };
        
        const diff = (parseInt(newTotal, 10) || 0) - (current.currency || 0);
        if (diff === 0) return;

        const historyEntry = {
            id: `att_adj_${Date.now()}`,
            name: reason,
            stars: diff,
            isStar: true,
            source: 'teacher',
            dateAwarded: new Date().toISOString().split('T')[0]
        };

        // Add to Achievement Ledger (Append-only audit trail)
        await this.logToLedger(userId, 'stars', diff, reason, 'teacher', groupId);

        course_stats[groupId] = {
            ...current,
            currency: newTotal,
            rewards: [...(current.rewards || []), historyEntry]
        };
        
        // (global currency sum removed)
        
        await this.saveProgress(userId, {
            ...progress,
        // (currency mapping removed)
            course_stats
        });
    },

    async getUserProgress(userId, fast = false) {
        if (!userId) return { rewards: [], user_words: [], course_stats: {} };

        try {
            let q = supabase.from('user_progress');
            
            if (fast) {
                // Light fetch: include progress JSON for daily_quests (words loaded via getUserWords)
                q = q.select('user_id, coins, coins_history, purchase_history, rewards, course_stats, progress');
            } else {
                q = q.select('*');
            }

            q = q.eq('user_id', userId).single();

            const { data, error } = await this._withTimeout(q, 30000, fast ? 'getUserProgress:fast' : 'getUserProgress');

            if (error && error.code !== 'PGRST116') {
                console.error("[DB] getUserProgress fetch error:", error.message);
                throw error; // CRITICAL: Stop execution to prevent empty state from being saved later
            }
            if (!data) return { rewards: [], user_words: [], course_stats: {} };

            // MERGE LOGIC: Prioritize top-level columns for primary values, 
            // but fallback to blob if they are null (likely meaning they weren't saved due to schema issues)
            const progressField = data.progress && typeof data.progress === 'object' ? data.progress : {};
            const blob = fast ? {} : progressField;
            const daily_quests = (data.daily_quests && typeof data.daily_quests === 'object')
                ? data.daily_quests
                : (progressField.daily_quests && typeof progressField.daily_quests === 'object'
                    ? progressField.daily_quests
                    : null);

            // 1. Currency (Number) removed

            // 2. Rewards (Array) - Take the most populated one
            const rewards = (data.rewards && Array.isArray(data.rewards) && data.rewards.length >= (blob.rewards?.length || 0))
                ? data.rewards : (blob.rewards || []);

            // 3. User Words (Array) — skipped in fast mode (loaded via getUserWords)
            const columnWords = Array.isArray(data.user_words) ? data.user_words : [];
            const blobWords = Array.isArray(blob.user_words) ? blob.user_words : [];
            
            let user_words = fast
                ? []
                : sanitizeUserWords([...columnWords, ...blobWords]);

            // AUTO-REVERT REVISED WORDS
            const REVISED_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
            let wordsChanged = false;
            const now = Date.now();
            user_words = user_words.map(w => {
                if ((w.status === 'REVISED' || w.status === '6' || w.status === 6) && w.statusDate) {
                    const statusMs = new Date(w.statusDate).getTime();
                    if (now - statusMs >= REVISED_COOLDOWN_MS) {
                        wordsChanged = true;
                        return { ...w, status: 'LEARNT' };
                    }
                }
                return w;
            });

            if (wordsChanged) {
                // Background update to save reverted states
                supabase.from('user_progress').upsert({
                    id: data.id,
                    user_id: userId,
                    user_words: user_words,
                    progress: { ...blob, user_words },
                }, { onConflict: 'id' }).then(({error}) => {
                    if(error) console.error("[DB] Failed auto-reverting REVISED words", error);
                });
            }

            // 4. Course Stats (Object) — column + blob (fast mode uses progressField, not empty blob)
            const course_stats = mergeCourseStats(data.course_stats, progressField.course_stats);

            // 5–6. Coins — net balance from longer history (includes spending); not "max column vs blob"
            const resolvedCoins = resolveAuthoritativeCoins(
                data.coins,
                data.coins_history,
                progressField.coins_history,
                progressField.coins,
            );
            const coins = resolvedCoins.coins;
            const coins_history = resolvedCoins.coins_history;

            // 7. Purchase History (Array)
            const purchase_history = (data.purchase_history && data.purchase_history.length > 0)
                ? data.purchase_history : (blob.purchase_history || []);

            // 8. Balance & Payment History (Virtual columns stored in blob)
            const balance = (blob.balance !== undefined && blob.balance !== null) ? blob.balance : 0;
            const payment_history = blob.payment_history || [];

            // Stars (Total) is now a computed sum of all stars
            // (Global totalStars calculation removed)

            return {
                // currency removed
                rewards,
                user_words,
                course_stats,
                coins,
                coins_history,
                purchase_history,
                balance,
                payment_history,
                daily_quests,
                new_word_star_prompt: progressField.new_word_star_prompt ?? null,
            };
        } catch (err) {
            console.error("[DB] getUserProgress critical error:", err);
            return { rewards: [], user_words: [], course_stats: {} };
        }
    },
    async getUserWords(userId, { failOpen = true } = {}) {
        if (!userId) return [];
        try {
            // Only select the columns that contain word data
            const q = supabase.from('user_progress').select('user_id, user_words, progress').eq('user_id', userId).single();
            const { data, error } = await this._withTimeout(q, 30000, 'getUserWords');
            
            if (error || !data) {
                if (!failOpen) throw (error || new Error('getUserWords: no data'));
                return [];
            }
            
            const blob = data.progress || {};
            const columnWords = Array.isArray(data.user_words) ? data.user_words : [];
            const blobWords = Array.isArray(blob.user_words) ? blob.user_words : [];
            
            return this.sanitizeUserWords([...columnWords, ...blobWords]);
        } catch (e) {
            console.warn("[DB] getUserWords fail:", e.message);
            if (!failOpen) throw e;
            return [];
        }
    },

    /**
     * Never persist an empty word list when the DB already has words.
     * Empty clears are forbidden — words are only removed by deleting the student account.
     */
    _resolveUserWordsForSave(incoming, persisted) {
        const incomingArr = Array.isArray(incoming) ? incoming : null;
        const persistedArr = Array.isArray(persisted) ? persisted : [];

        if (incomingArr && incomingArr.length > 0) {
            return sanitizeUserWords(incomingArr);
        }
        if (persistedArr.length > 0) {
            if (incomingArr && incomingArr.length === 0) {
                console.warn('[DB] saveProgress: blocked empty user_words overwrite — keeping existing list');
            }
            return sanitizeUserWords(persistedArr);
        }
        return [];
    },

    /** Read authoritative coin totals from columns (not session cache). */
    async _getPersistedCoins(userId) {
        if (!userId) return { coins: 0, coins_history: [], progressBlob: {} };
        try {
            const { data, error } = await supabase
                .from('user_progress')
                .select('id, coins, coins_history, progress')
                .eq('user_id', userId)
                .maybeSingle();
            if (error || !data) return { coins: 0, coins_history: [], progressBlob: {} };
            const blob = data.progress && typeof data.progress === 'object' ? data.progress : {};
            const resolved = resolveAuthoritativeCoins(
                data.coins,
                data.coins_history,
                blob.coins_history,
                blob.coins,
            );
            return {
                coins: resolved.coins,
                coins_history: resolved.coins_history,
                progressBlob: blob,
                recordId: data.id,
            };
        } catch (e) {
            console.warn('[DB] _getPersistedCoins failed:', e?.message);
            return { coins: 0, coins_history: [], progressBlob: {} };
        }
    },

    /** Read authoritative group star totals (column + blob). */
    async _getPersistedCourseStats(userId) {
        if (!userId) return { course_stats: {}, rewards: [], progressBlob: {} };
        try {
            const { data, error } = await supabase
                .from('user_progress')
                .select('id, course_stats, rewards, progress')
                .eq('user_id', userId)
                .maybeSingle();
            if (error || !data) return { course_stats: {}, rewards: [], progressBlob: {} };
            const blob = data.progress && typeof data.progress === 'object' ? data.progress : {};
            const columnRewards = Array.isArray(data.rewards) ? data.rewards : [];
            const blobRewards = Array.isArray(blob.rewards) ? blob.rewards : [];
            const rewards = columnRewards.length >= blobRewards.length ? columnRewards : blobRewards;
            return {
                course_stats: mergeCourseStats(data.course_stats, blob.course_stats),
                rewards,
                progressBlob: blob,
                recordId: data.id,
            };
        } catch (e) {
            console.warn('[DB] _getPersistedCourseStats failed:', e?.message);
            return { course_stats: {}, rewards: [], progressBlob: {} };
        }
    },

    /**
     * Never replace a long coins_history with a shorter stale list (e.g. WordGame session cache).
     * Appends only entries that are not already present by id.
     */
    _resolveCoinsHistoryForSave(incoming, persisted) {
        const per = Array.isArray(persisted) ? persisted : [];
        const inc = Array.isArray(incoming) ? incoming : null;
        if (!inc) return per;
        if (inc.length >= per.length) return inc;
        const perIds = new Set(per.map((e) => e?.id).filter(Boolean));
        const novel = inc.filter((e) => e?.id && !perIds.has(e.id));
        if (novel.length === 0) {
            console.warn('[DB] saveProgress: blocked coins_history shrink — keeping persisted list');
            return per;
        }
        return [...per, ...novel];
    },

    /** Coin-only DB write — avoids loading user_words and prevents stale cache overwrites. */
    async _saveCoinsOnly(userId, { coins, coins_history }) {
        if (!userId) throw new Error('userId is required');
        const { data: row, error: fetchErr } = await supabase
            .from('user_progress')
            .select('id, progress')
            .eq('user_id', userId)
            .maybeSingle();
        if (fetchErr) throw fetchErr;
        if (!row?.id) throw new Error(`No user_progress row for ${userId}`);

        const blob = row.progress && typeof row.progress === 'object' ? row.progress : {};
        const payload = {
            id: row.id,
            user_id: userId,
            coins: parseInt(coins, 10) || 0,
            coins_history,
            progress: { ...blob, coins: parseInt(coins, 10) || 0, coins_history },
        };

        const { error } = await supabase
            .from('user_progress')
            .upsert(payload, { onConflict: 'user_id' });
        if (error) throw error;
        this.clearCache('userProgress');
    },

    /** Star-only DB write — avoids loading user_words or touching coins. */
    async _saveStarsOnly(userId, { course_stats, rewards }) {
        if (!userId) throw new Error('userId is required');
        const { data: row, error: fetchErr } = await supabase
            .from('user_progress')
            .select('id, progress, rewards, course_stats')
            .eq('user_id', userId)
            .maybeSingle();
        if (fetchErr) throw fetchErr;
        if (!row?.id) throw new Error(`No user_progress row for ${userId}`);

        const blob = row.progress && typeof row.progress === 'object' ? row.progress : {};
        const nextCourseStats = resolveCourseStatsForSave(
            course_stats && typeof course_stats === 'object' ? course_stats : {},
            row.course_stats,
            blob.course_stats,
        );
        const persistedRewards = Array.isArray(row.rewards) ? row.rewards : (blob.rewards || []);
        const columnRewards = Array.isArray(rewards)
            ? mergeStarRewardsById(persistedRewards, rewards)
            : persistedRewards;

        const payload = {
            id: row.id,
            user_id: userId,
            course_stats: nextCourseStats,
            rewards: columnRewards,
            progress: {
                ...blob,
                course_stats: nextCourseStats,
                rewards: columnRewards,
            },
        };

        const { error } = await supabase
            .from('user_progress')
            .upsert(payload, { onConflict: 'user_id' });
        if (error) throw error;
        this.clearCache('userProgress');
    },

    /**
     * Internal helper to save user progress safely by stripping system fields
     */
    async saveProgress(userId, progressObject, options = {}) {
        if (!userId) throw new Error("userId is required to save progress");
        this.clearCache('userProgress');

        // Ensure JWT is attached before RLS-heavy write (prevents Safari/iOS auth issues)
        try {
            await supabase.auth.getSession();
        } catch (sessionErr) {
            console.warn('[DB] saveProgress: getSession failed', sessionErr?.message);
        }

        // Clean the data: remove Postgres system fields
        const { id, created_at, updated_at, ...rawPayload } = progressObject;

        // Fail closed on read errors so a timeout cannot look like "0 words" and wipe the list.
        const persistedWords = await this.getUserWords(userId, { failOpen: false });
        const finalWords = this._resolveUserWordsForSave(
            rawPayload.user_words,
            persistedWords,
        );

        let existingProgress = {};
        let persistedCoins = 0;
        let persistedCoinsHistory = [];
        let persistedCourseStatsColumn = {};
        if (!options.skipProgressMerge) {
            try {
                const { data: existingRow } = await supabase
                    .from('user_progress')
                    .select('progress, user_words, coins, coins_history, course_stats')
                    .eq('user_id', userId)
                    .maybeSingle();
                if (existingRow?.progress && typeof existingRow.progress === 'object') {
                    existingProgress = existingRow.progress;
                }
                persistedCourseStatsColumn = existingRow?.course_stats && typeof existingRow.course_stats === 'object'
                    ? existingRow.course_stats
                    : {};
                const columnHist = Array.isArray(existingRow?.coins_history) ? existingRow.coins_history : [];
                const blobHist = Array.isArray(existingProgress.coins_history) ? existingProgress.coins_history : [];
                persistedCoinsHistory = pickLongerCoinsHistory(columnHist, blobHist);
                const resolvedPersistedCoins = resolveAuthoritativeCoins(
                    existingRow?.coins,
                    columnHist,
                    blobHist,
                    existingProgress.coins,
                );
                persistedCoins = resolvedPersistedCoins.coins;
            } catch (mergeErr) {
                console.warn('[DB] saveProgress: could not read existing progress blob', mergeErr?.message);
            }
        } else if (rawPayload.progress && typeof rawPayload.progress === 'object') {
            existingProgress = rawPayload.progress;
        }

        const incomingDailyQuests = rawPayload.daily_quests && typeof rawPayload.daily_quests === 'object'
            ? rawPayload.daily_quests
            : (rawPayload.progress?.daily_quests && typeof rawPayload.progress.daily_quests === 'object'
                ? rawPayload.progress.daily_quests
                : null);

        let daily_quests;
        if (options.updateDailyQuests) {
            daily_quests = incomingDailyQuests ?? existingProgress.daily_quests ?? null;
        } else {
            const persistedDailyQuests = await fetchPersistedDailyQuests({ userId });
            daily_quests = persistedDailyQuests ?? incomingDailyQuests ?? existingProgress.daily_quests ?? null;
        }

        const mergedCoinsHistory = this._resolveCoinsHistoryForSave(
            Array.isArray(rawPayload.coins_history) ? rawPayload.coins_history : null,
            persistedCoinsHistory.length ? persistedCoinsHistory : (existingProgress.coins_history || []),
        );
        const incomingCoins = rawPayload.coins !== undefined ? parseInt(rawPayload.coins, 10) : null;
        let mergedCoins;
        if (incomingCoins !== null && !Number.isNaN(incomingCoins)) {
            if (mergedCoinsHistory.length >= (persistedCoinsHistory.length || 0)) {
                mergedCoins = incomingCoins;
            } else {
                const novelDelta = mergedCoinsHistory
                    .slice(persistedCoinsHistory.length)
                    .reduce((s, e) => s + (parseInt(e.amount, 10) || 0), 0);
                mergedCoins = persistedCoins + novelDelta;
                console.warn('[DB] saveProgress: coins merged from persisted base (stale history blocked)');
            }
        } else {
            mergedCoins = persistedCoins || parseInt(existingProgress.coins || 0, 10) || 0;
        }

        const historySum = sumCoinsHistory(mergedCoinsHistory);
        if (historySum !== null) {
            mergedCoins = historySum;
        }

        const mergedCourseStats = resolveCourseStatsForSave(
            rawPayload.course_stats,
            persistedCourseStatsColumn,
            existingProgress.course_stats,
        );

        const mergedProgress = {
            ...existingProgress,
            coins: mergedCoins,
            coins_history: mergedCoinsHistory,
            purchase_history: Array.isArray(rawPayload.purchase_history) ? rawPayload.purchase_history : (existingProgress.purchase_history || []),
            rewards: Array.isArray(rawPayload.rewards) ? rawPayload.rewards : (existingProgress.rewards || []),
            user_words: finalWords,
            course_stats: mergedCourseStats,
            daily_quests,
            balance: parseInt(rawPayload.balance !== undefined ? rawPayload.balance : (rawPayload.progress?.balance ?? existingProgress.balance ?? 0), 10),
            payment_history: rawPayload.payment_history || rawPayload.progress?.payment_history || existingProgress.payment_history || [],
        };

        const basePayload = {
            user_id: userId,
            coins: mergedProgress.coins,
            coins_history: mergedProgress.coins_history,
            purchase_history: mergedProgress.purchase_history,
            rewards: mergedProgress.rewards,
            user_words: finalWords,
            course_stats: mergedProgress.course_stats,
            progress: mergedProgress,
        };

        try {
            // Attempt full column save (including synced JSON blob)
            const { error } = await supabase
                .from('user_progress')
                .upsert(basePayload, { onConflict: 'user_id' });

            if (error) {
                // If column error found, log it and let the 'catch' block handle JSON backup
                console.warn("[DB] Full column save failed:", error.message);
                throw error; 
            }
        } catch (err) {
            console.warn("[DB] Column save failed, using JSON backup only:", err.message);

            const jsonOnlyPayload = {
                user_id: userId,
                // currency removed
                progress: basePayload.progress
            };

            const { error: jsonError } = await supabase
                .from('user_progress')
                .upsert(jsonOnlyPayload, { onConflict: 'user_id' });

            if (jsonError) throw jsonError;
        }
    },

    /**
     * When a word/exercise has no group_id or course hint, attribute auto training rewards to the student's
     * active group (same rule as legacy "highest course name" fallback): one active enrollment, else pick first by courseName desc.
     */
    async resolveDefaultTrainingGroupId(userId) {
        if (!userId) return null;
        try {
            const { data: user } = await supabase.from(getSBTable('users')).select('name, surname').eq('id', userId).single();
            if (!user) return null;
            const fullName = `${user.name || ''} ${user.surname || ''}`.trim();
            const uidStr = String(userId);
            const { data: allGroups } = await supabase.from('groups').select('*');
            if (!allGroups) return null;
            const myActiveGroups = allGroups.filter(g =>
                !g.completed &&
                (
                    (g.enrollments || []).some(e => (e.userName || '').toLowerCase().trim() === fullName.toLowerCase().trim()) ||
                    (g.enrollments || []).some(e => String(e.userId || '') === uidStr) ||
                    (Array.isArray(g.students) && g.students.map(String).includes(uidStr))
                )
            );
            if (myActiveGroups.length === 0) return null;
            myActiveGroups.sort((a, b) => (b.courseName || '').localeCompare(a.courseName || ''));
            return myActiveGroups[0].id;
        } catch (e) {
            console.warn('[DB] resolveDefaultTrainingGroupId failed:', e?.message);
            return null;
        }
    },

    async resolveGroupKey(userId, targetKey) {
        if (!targetKey) return null;

        const uuidLedgerRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidLedgerRegex.test(String(targetKey).trim())) {
            return String(targetKey).trim();
        }

        try {
            // 1. Get student's name
            const { data: user } = await supabase.from(getSBTable('users')).select('name, surname').eq('id', userId).single();
            if (!user) return targetKey;
            const fullName = `${user.name || ''} ${user.surname || ''}`.trim();
            const uidStr = String(userId);

            // 2. Fetch relevant groups for this course
            const { data: groups } = await supabase.from('groups').select('*').eq('courseName', targetKey);
            
            const studentGroups = groups ? groups.filter(g =>
                (g.enrollments || []).some(e => (e.userName || '').toLowerCase().trim() === fullName.toLowerCase().trim()) ||
                (g.enrollments || []).some(e => String(e.userId || '') === uidStr) ||
                (Array.isArray(g.students) && g.students.map(String).includes(uidStr))
            ) : [];

            if (studentGroups.length > 0) {
                // 4. Resolve: Prefer NOT Finished/Done
                const active = studentGroups.find(g => !g.completed);
                return active ? active.id : studentGroups[0].id;
            }

            // Fallback: Target course does not match any enrolled group. 
            // Find the highest active course group (alphabetically, e.g., L2 over L1) for this student.
            const { data: allGroups } = await supabase.from('groups').select('*');
            if (allGroups) {
                let myActiveGroups = allGroups.filter(g => 
                    !g.completed && 
                    (
                        (g.enrollments || []).some(e => (e.userName || '').toLowerCase().trim() === fullName.toLowerCase().trim()) ||
                        (g.enrollments || []).some(e => String(e.userId || '') === uidStr) ||
                        (Array.isArray(g.students) && g.students.map(String).includes(uidStr))
                    )
                );
                
                if (myActiveGroups.length > 0) {
                    // Sort descending by courseName (L2 > L1)
                    myActiveGroups.sort((a, b) => (b.courseName || "").localeCompare(a.courseName || ""));
                    return myActiveGroups[0].id;
                }
            }

            return targetKey;
        } catch (err) {
            console.error("Resolution error:", err);
            return targetKey;
        }
    },

    async adjustCurrencyForUser(userId, amount, targetKey = null, source = null) {
        const persisted = await this._getPersistedCourseStats(userId);
        const addedAmount = parseInt(amount, 10) || 0;

        let course_stats = { ...(persisted.course_stats || {}) };
        let rewards = [...(persisted.rewards || [])];
        if (targetKey) {
            const finalKey = (source === 'teacher' || source === 'manual') ? targetKey : await this.resolveGroupKey(userId, targetKey);
            console.log(`[DB] Adjusting currency for user ${userId} in group ${finalKey}: ${addedAmount}`);
            
            // CONSOLIDATION: If finalKey is different from targetKey (and targetKey was a name), migrate the data
            if (targetKey && finalKey && targetKey !== finalKey && course_stats[targetKey]) {
                const old = course_stats[targetKey];
                const current = course_stats[finalKey] || { currency: 0, rewards: [] };
                course_stats[finalKey] = {
                    ...current,
                    currency: (current.currency || 0) + (old.currency || 0),
                    rewards: [...(current.rewards || []), ...(old.rewards || [])]
                };
                delete course_stats[targetKey];
            }

            const current = course_stats[finalKey] || { currency: 0, rewards: [] };

            const skipHistory = source === 'auto' || !source;
            const historyEntry = !skipHistory ? {
                id: `adj_manual_${Date.now()}`,
                name: source === 'teacher' ? 'Manual Star Award' : (source || 'Adjustment'),
                description: `${addedAmount >= 0 ? '+' : ''}${addedAmount} stars`,
                stars: addedAmount,
                isStar: true,
                source: source || 'manual',
                dateAwarded: new Date().toISOString().split('T')[0]
            } : null;

            // LOG TO LEDGER
            await this.logToLedger(
                userId, 
                'stars', 
                addedAmount, 
                source === 'teacher' ? 'Manual Star Award' : (source || 'Adjustment'), 
                source || 'manual', 
                finalKey
            );

            course_stats[finalKey] = {
                ...current,
                currency: (current.currency || 0) + addedAmount,
                rewards: historyEntry ? [...(current.rewards || []), historyEntry] : (current.rewards || [])
            };
        }

        await this._saveStarsOnly(userId, { course_stats, rewards });
    },

    async _syncDailyQuestState(userId, { questId = null } = {}) {
        if (!userId) return null;

        const [progress, automationRules] = await Promise.all([
            this.getUserProgress(userId),
            this.getAutomationRules(),
        ]);

        const raw = progress.daily_quests ?? progress.progress?.daily_quests ?? null;
        const persisted = normalizeDailyQuestState(raw);
        let state = rolloverDailyQuestState(persisted, getTodayDateStr());

        let questCoinReward = 0;
        let questResult = { changed: false };
        if (questId) {
            questResult = markQuestInState(state, questId, automationRules);
            state = questResult.state;
            if (questResult.changed) questCoinReward = questResult.coinReward;
        }

        const prizeResult = applyStreakPrizeMilestones(state, automationRules);
        state = prizeResult.state;

        if (questResult.changed || dailyQuestStateNeedsPersist(persisted, state) || prizeResult.changed) {
            await this.saveProgress(userId, { daily_quests: state }, {
                updateDailyQuests: true,
            });
        }

        if (questResult.changed && state.day_marked && !persisted.day_marked) {
            this.createTeacherNotification({
                studentId: userId,
                type: 'daily_streak',
                message: 'Completed daily tasks',
                payload: {
                    streak_days: state.streak_days,
                    tasks_done: Object.keys(state.tasks_done || {}),
                    date: state.today_date,
                },
            }).catch(() => {});
        }

        if (questCoinReward > 0 && questId) {
            const label = questId.replace(/_/g, ' ');
            await this.adjustCoinsForUser(userId, questCoinReward, `Daily quest: ${label}`, 'auto');
        }

        for (const award of prizeResult.awards) {
            await this.adjustCoinsForUser(
                userId,
                award.coins,
                `Streak path: ${award.label} (day ${award.day})`,
                'auto'
            );
        }

        return state;
    },

    async markDailyQuest(userId, questId) {
        if (!userId || !questId) return null;
        return this._syncDailyQuestState(userId, { questId });
    },

    /** Rollover, milestone prizes, and persist without marking a quest (e.g. after load). */
    async syncDailyQuestState(userId) {
        if (!userId) return null;
        return this._syncDailyQuestState(userId, {});
    },

    async purchaseStreakFreeze(userId) {
        if (!userId) return { success: false, error: 'Not signed in' };

        const progress = await this.getUserProgress(userId);
        const state = extractDailyQuestState(progress);

        if ((progress.coins || 0) < STREAK_FREEZE_COST) {
            return { success: false, error: 'insufficient_coins', coins: progress.coins || 0 };
        }

        const { state: next, changed, error } = armStreakFreezeInState(state);
        if (!changed) {
            return { success: false, error: error || 'already_armed' };
        }

        await this.adjustCoinsForUser(
            userId,
            -STREAK_FREEZE_COST,
            'Streak freeze (1 missed day)',
            'auto'
        );

        const progressAfter = await this.getUserProgress(userId);
        await this.saveProgress(userId, { daily_quests: next }, {
            updateDailyQuests: true,
        });

        return { success: true, state: next };
    },

    async adjustCoinsForUser(userId, amount, reason, source = 'manual', targetKey = null) {
        const { coins, coins_history } = await this._getPersistedCoins(userId);
        const addedAmount = parseInt(amount, 10) || 0;

        const historyEntry = {
            id: `coin_${Date.now()}`,
            name: source === 'auto' ? `Automation: ${reason}` : (reason || 'Adjustment'),
            amount: addedAmount,
            source: source,
            dateAwarded: new Date().toISOString().split('T')[0]
        };

        let finalKey = null;
        let rawKey = targetKey;
        if (!rawKey && source === 'auto') {
            rawKey = await this.resolveDefaultTrainingGroupId(userId);
        }
        if (rawKey) {
            finalKey = (source === 'teacher' || source === 'manual') ? rawKey : await this.resolveGroupKey(userId, rawKey);
        }

        // LOG TO LEDGER
        await this.logToLedger(userId, 'coins', addedAmount, reason || 'Adjustment', source, finalKey);

        const nextHistory = [...(coins_history || []), historyEntry];
        const newCoins = sumCoinsHistory(nextHistory) ?? ((coins || 0) + addedAmount);

        await this._saveCoinsOnly(userId, {
            coins: newCoins,
            coins_history: nextHistory,
        });
    },

    /**
     * One read + one write for per-word training rewards (avoids duplicate getUserProgress/saveProgress).
     */
    async applyTrainingAutomation(userId, { stars = 0, coins = 0, actionPrefix, wordLabel, targetKey = null }, sessionCache = null) {
        const starAmount = parseFloat(stars) || 0;
        const coinAmount = parseInt(coins, 10) || 0;
        if (!starAmount && !coinAmount) return null;

        const persistedCoins = await this._getPersistedCoins(userId);
        const persistedStars = await this._getPersistedCourseStats(userId);
        const progress = sessionCache
            ? {
                coins: persistedCoins.coins,
                coins_history: persistedCoins.coins_history,
                rewards: sessionCache.rewards ?? persistedStars.rewards,
                course_stats: sessionCache.course_stats ?? persistedStars.course_stats,
            }
            : {
                coins: persistedCoins.coins,
                coins_history: persistedCoins.coins_history,
                rewards: persistedStars.rewards,
                course_stats: persistedStars.course_stats,
            };
        const date = new Date().toISOString().split('T')[0];
        const label = wordLabel || 'Training';
        const update = {
            coins: progress.coins,
            coins_history: progress.coins_history,
            rewards: progress.rewards,
            course_stats: progress.course_stats,
        };

        const uuidLedgerRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        let finalKey = null;
        let rawKey = targetKey;
        if (!rawKey) {
            rawKey = await this.resolveDefaultTrainingGroupId(userId);
        }
        if (rawKey) {
            finalKey = await this.resolveGroupKey(userId, rawKey);
            if (finalKey && !uuidLedgerRegex.test(String(finalKey))) {
                const fallbackId = await this.resolveDefaultTrainingGroupId(userId);
                if (fallbackId) finalKey = fallbackId;
            }
        }

        if (coinAmount) {
            const coinEntry = {
                id: `coin_${Date.now()}`,
                name: `Automation: ${label}`,
                amount: coinAmount,
                source: 'auto',
                dateAwarded: date,
            };
            const nextHistory = [...(progress.coins_history || []), coinEntry];
            const newCoins = sumCoinsHistory(nextHistory) ?? ((progress.coins || 0) + coinAmount);
            await this.logToLedger(userId, 'coins', coinAmount, `${label}: ${actionPrefix}`, 'auto', finalKey);
            await this._saveCoinsOnly(userId, { coins: newCoins, coins_history: nextHistory });
            update.coins = newCoins;
            update.coins_history = nextHistory;
        }

        if (starAmount) {
            const rewardItem = {
                id: `auto_${actionPrefix}_star_${Date.now()}`,
                name: actionPrefix.includes('test') ? 'Correct spelling in Test mode' :
                    actionPrefix.includes('revision') ? 'Correct spelling in Review Words' :
                        'Training Achievement',
                description: `+${starAmount} star${starAmount === 1 ? '' : 's'} for "${label}"`,
                stars: starAmount,
                isStar: true,
                source: 'auto',
                dateAwarded: date,
            };
            update.rewards = [...(progress.rewards || []), rewardItem];

            let course_stats = { ...(progress.course_stats || {}) };
            if (finalKey) {
                const current = course_stats[finalKey] || { currency: 0, rewards: [] };
                course_stats[finalKey] = {
                    ...current,
                    currency: (current.currency || 0) + starAmount,
                    rewards: [...(current.rewards || []), rewardItem],
                };
                update.course_stats = course_stats;
            }

            await this.logToLedger(userId, 'stars', starAmount, rewardItem.name, 'auto', finalKey);
            await this._saveStarsOnly(userId, {
                course_stats: update.course_stats || progress.course_stats,
                rewards: update.rewards,
            });
        }

        if (sessionCache) {
            syncWordGameSessionCacheFromProgress(sessionCache, update);
            const freshDq = await fetchPersistedDailyQuests({ userId });
            if (freshDq) {
                sessionCache.daily_quests = freshDq;
                sessionCache.progressBlob = { ...(sessionCache.progressBlob || {}), daily_quests: freshDq };
            }
        }
        return update;
    },

    async dbAdjustCoins(userId, adjustment, reason) {
        return this.adjustCoinsForUser(userId, adjustment, reason || 'Manual adjustment', 'teacher');
    },

    async awardExerciseRewards(userId, groupIdent, stars, coins, reason) {
        if (!stars && !coins) return;
        const date = new Date().toISOString().split('T')[0];

        if (coins) {
            const { coins: baseCoins, coins_history } = await this._getPersistedCoins(userId);
            const coinEntry = {
                id: `coin_${Date.now()}`,
                name: reason || 'Exercise completion',
                amount: coins,
                source: 'auto',
                dateAwarded: date,
            };
            const nextHistory = [...(coins_history || []), coinEntry];
            const newCoins = sumCoinsHistory(nextHistory) ?? ((baseCoins || 0) + coins);
            await this.logToLedger(userId, 'coins', coins, reason || 'Exercise completion', 'auto', groupIdent);
            await this._saveCoinsOnly(userId, { coins: newCoins, coins_history: nextHistory });
        }

        if (stars) {
            const persisted = await this._getPersistedCourseStats(userId);
            let course_stats = { ...(persisted.course_stats || {}) };
            
            // CONSOLIDATION: If groupIdent is a name, resolve it to an ID to avoid duplicates
            let targetKey = groupIdent;
            if (groupIdent && groupIdent.length < 20 && !/^[0-9a-fA-F-]+$/.test(groupIdent)) {
               const resolved = await this.resolveGroupKey(userId, groupIdent);
               if (resolved) {
                   targetKey = resolved;
                   // Migrate existing stars from the name-based key if they exist
                   if (course_stats[groupIdent] && targetKey !== groupIdent) {
                       const old = course_stats[groupIdent];
                       const current = course_stats[targetKey] || { currency: 0, rewards: [] };
                       course_stats[targetKey] = {
                           ...current,
                           currency: (current.currency || 0) + (old.currency || 0),
                           rewards: [...(current.rewards || []), ...(old.rewards || [])]
                       };
                       delete course_stats[groupIdent];
                   }
               }
            }

            const current = course_stats[targetKey] || { currency: 0, rewards: [] };
            
            course_stats[targetKey] = {
                ...current,
                currency: (current.currency || 0) + parseFloat(stars),
                rewards: [...(current.rewards || []), {
                    id: `star_${Date.now()}`,
                    name: reason || 'Exercise completion',
                    stars: parseFloat(stars),
                    isStar: true,
                    source: 'auto',
                    dateAwarded: date
                }]
            };
            
            await this.logToLedger(userId, 'stars', stars, reason || 'Exercise completion', 'auto', targetKey);
            await this._saveStarsOnly(userId, { course_stats, rewards: persisted.rewards });
        }
    },

    /**
     * Achievement Ledger: Append-only audit trail
     */
    async logToLedger(userId, type, amount, reason, source, groupId = null) {
        if (!userId) return;

        // reward_ledger.group_id must be a UUID or null; non-UUID keys (course names) must be resolved or history filters miss rows.
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        let validGroupId = null;
        if (groupId) {
            const raw = String(groupId).trim();
            if (uuidRegex.test(raw)) {
                validGroupId = raw;
            } else {
                try {
                    const resolved = await this.resolveGroupKey(userId, raw);
                    if (resolved && uuidRegex.test(String(resolved))) {
                        validGroupId = String(resolved);
                    }
                } catch (e) {
                    console.warn('[LEDGER] resolveGroupKey failed for ledger row:', raw, e?.message);
                }
            }
        }

        const { error } = await supabase
            .from('reward_ledger')
            .insert({
                user_id: userId,
                group_id: validGroupId,
                type,
                amount: parseFloat(amount),
                reason,
                source,
                // created_at removed to let DB handle it
            });

        if (error) {
            console.error("[LEDGER] Error:", error);
            throw new Error(`Ledger Update Failed: ${error.message}`);
        }
    },

    async purchaseStoreItem(userId, item) {
        const progress = await this.getUserProgress(userId);
        const price = parseInt(item.price, 10) || 0;

        if ((progress.coins || 0) < price) {
            throw new Error("Not enough coins!");
        }

        const newCoins = progress.coins - price;
        const date = new Date().toISOString().split('T')[0];

        const historyEntry = {
            id: `purchase_${Date.now()}`,
            name: `Purchase: ${item.name}`,
            amount: -price,
            source: 'store',
            dateAwarded: date
        };

        const purchaseEntry = {
            id: `buy_${Date.now()}`,
            name: item.name,
            price: price,
            date: date
        };

        await this.logToLedger(userId, 'coins', -price, `Purchase: ${item.name}`, 'purchase');

        await this.saveProgress(userId, {
            ...progress,
            coins: newCoins,
            coins_history: [...(progress.coins_history || []), historyEntry],
            purchase_history: [...(progress.purchase_history || []), purchaseEntry]
        });
    },

    async awardRewardToUser(userId, reward, targetKey = null, source = null) {
        const progress = await this.getUserProgress(userId);
        const awardedItem = {
            ...reward,
            dateAwarded: new Date().toISOString().split('T')[0],
            source: source
        };
        const newRewards = [...(progress.rewards || []), awardedItem];

        // If the reward itself carries stars, update the currency too
        // newGlobalCurrency removed

        const uuidLedgerRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        let course_stats = { ...(progress.course_stats || {}) };
        let finalKey = null;

        let rawKey = targetKey;
        if (!rawKey && source !== 'teacher' && source !== 'manual') {
            rawKey = await this.resolveDefaultTrainingGroupId(userId);
        }
        if (rawKey) {
            finalKey = (source === 'teacher' || source === 'manual') ? rawKey : await this.resolveGroupKey(userId, rawKey);
            if (finalKey && !uuidLedgerRegex.test(String(finalKey)) && source !== 'teacher' && source !== 'manual') {
                const fallbackId = await this.resolveDefaultTrainingGroupId(userId);
                if (fallbackId) finalKey = fallbackId;
            }
            const current = course_stats[finalKey] || { currency: 0, rewards: [] };

            const starDelta = reward.isStar
                ? (parseFloat(reward.stars != null ? reward.stars : reward.amount) || 0)
                : 0;
            const newGroupCurrency = (current.currency || 0) + starDelta;

            course_stats[finalKey] = {
                ...current,
                currency: newGroupCurrency,
                rewards: [...(current.rewards || []), awardedItem]
            };
        }

        await this.logToLedger(
            userId, 
            reward.isStar ? 'stars' : 'coins', 
            reward.stars || reward.amount || 0, 
            reward.name || 'Reward', 
            source || 'auto',
            finalKey
        );

        await this.saveProgress(userId, {
            ...progress,
            rewards: newRewards,
            course_stats
        }, { skipProgressMerge: true });
    },

    // Removed redundant adjustCurrencyForUser that modified global currency

    async adjustCourseCurrency(userId, groupIdent, delta, reason) {
        const progress = await this.getUserProgress(userId);
        let course_stats = { ...(progress.course_stats || {}) };
        const current = course_stats[groupIdent] || { currency: 0, rewards: [] };

        const adjustment = parseFloat(delta) || 0;
        const newTotal = (current.currency || 0) + adjustment;

        const historyEntry = {
            id: `cour_adj_${Date.now()}`,
            name: reason || 'Teacher adjustment',
            stars: adjustment,
            isStar: true,
            source: 'teacher',
            dateAwarded: new Date().toISOString().split('T')[0]
        };

        await this.logToLedger(userId, 'stars', adjustment, reason || 'Teacher adjustment', 'teacher', groupIdent);

        course_stats[groupIdent] = {
            ...current,
            currency: newTotal,
            rewards: [...(current.rewards || []), historyEntry]
        };

        await this.saveProgress(userId, {
            course_stats
        });
    },

    async getLedgerHistory(userId, type = 'stars', groupId = null) {
        let q = supabase
            .from('reward_ledger')
            .select('*')
            .eq('user_id', userId)
            .eq('type', type)
            .order('created_at', { ascending: false });

        if (groupId) {
            q = q.eq('group_id', groupId);
        }

        try {
            const { data, error } = await this._withTimeout(q, 15000, `getLedgerHistory:${type}`);
            if (error) {
                console.error("[LEDGER] Fetch history error:", error);
                return [];
            }
            return data || [];
        } catch (e) {
            console.warn("[LEDGER] getLedgerHistory timeout:", e.message);
            return [];
        }
    },

    async getLedgerBalance(userId, type, groupId = null) {
        if (!userId) return 0;
        let query = supabase
            .from('reward_ledger')
            .select('amount')
            .eq('user_id', userId)
            .eq('type', type);
        
        if (groupId) {
            query = query.eq('group_id', groupId);
        }

        try {
            const { data, error } = await this._withTimeout(query, 15000, `getLedgerBalance:${type}`);
            if (error) return 0;
            return data.reduce((sum, row) => sum + row.amount, 0);
        } catch (e) {
            console.warn("[LEDGER] getLedgerBalance timeout:", e.message);
            return 0;
        }
    },

    async forceSyncFromLedger(userId) {
        const progress = await this.getUserProgress(userId);
        
        // Fetch ALL ledger history for this user
        let { data: ledgerEntries, error } = await supabase
            .from('reward_ledger')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: true }); // Chronological rebuild

        if (error) {
            console.error("[DB] Sync failed:", error);
            throw error;
        }
        ledgerEntries = ledgerEntries || [];

        let coins = 0;
        let coins_history = [];
        let courseStats = { ...(progress.course_stats || {}) };

        // Keep existing trophies (non-stars), we will rebuild the star history
        const existingTrophies = (progress.rewards || []).filter(r => !r.isStar);
        let globalRewards = [...existingTrophies];

        // Clear existing accumulated stars in course_stats to avoid duplicates
        for (const groupIdent of Object.keys(courseStats)) {
            courseStats[groupIdent] = {
                ...courseStats[groupIdent],
                currency: 0,
                rewards: (courseStats[groupIdent].rewards || []).filter(r => !r.isStar)
            };
        }

        // Replay history
        for (const entry of ledgerEntries) {
            const dateStr = entry.created_at.split('T')[0];
            const ts = new Date(entry.created_at).getTime();
            
            if (entry.type === 'coins') {
                coins += parseFloat(entry.amount);
                coins_history.push({
                    id: `coin_${ts}_${Math.random().toString(36).substr(2, 5)}`,
                    name: entry.reason || 'Ledger event',
                    amount: parseFloat(entry.amount),
                    source: entry.source || 'auto',
                    dateAwarded: dateStr
                });
            } else if (entry.type === 'stars') {
                const group_id = entry.group_id;
                const amt = parseFloat(entry.amount);
                
                const historyEntry = {
                    id: `star_${ts}_${Math.random().toString(36).substr(2, 5)}`,
                    name: entry.reason || 'Ledger event',
                    stars: amt,
                    isStar: true,
                    source: entry.source || 'auto',
                    dateAwarded: dateStr
                };

                if (group_id) {
                    if (!courseStats[group_id]) {
                        courseStats[group_id] = { currency: 0, rewards: [] };
                    }
                    courseStats[group_id].currency += amt;
                    courseStats[group_id].rewards.push(historyEntry);
                } else {
                    // Fallback for global stars without a group
                    globalRewards.push(historyEntry);
                }
            }
        }

        await this.saveProgress(userId, {
            coins,
            coins_history,
            rewards: globalRewards,
            course_stats: courseStats,
        });
        
        return { coins };
    },

    async clearUserHistory(userId, type) {
        const progress = await this.getUserProgress(userId);
        let update = { ...progress };

        if (!type || type === 'coins') {
            update.coins_history = [];
            update.purchase_history = [];
        }

        if (!type || type === 'stars') {
            // Remove all isStar entries from top-level rewards
            update.rewards = (update.rewards || []).filter(r => !r.isStar);

            // Also clear in course stats
            let course_stats = { ...(update.course_stats || {}) };
            Object.keys(course_stats).forEach(key => {
                const stats = course_stats[key] || {};
                course_stats[key] = {
                    ...stats,
                    rewards: (stats.rewards || []).filter(r => !r.isStar)
                };
            });
            update.course_stats = course_stats;
        }

        if (!type || type === 'trophies' || type === 'rewards') {
            // Remove all non-star entries from top-level rewards
            update.rewards = (update.rewards || []).filter(r => r.isStar);

            // Also clear in course stats
            let course_stats = { ...(update.course_stats || {}) };
            Object.keys(course_stats).forEach(key => {
                const stats = course_stats[key] || {};
                course_stats[key] = {
                    ...stats,
                    rewards: (stats.rewards || []).filter(r => r.isStar)
                };
            });
            update.course_stats = course_stats;
        }

        await this.saveProgress(userId, update);
    },

    async getUserMessages(userId) {
        if (!userId) return [];
        const q = supabase
            .from(getSBTable('messages'))
            .select('*')
            .contains('sentTo', [userId]);

        try {
            const { data, error, status } = await this._withTimeout(q, 15000, 'getUserMessages');
            if (error || status === 404) return [];
            return (data || []).map((row) => ({
                ...row,
                recipientLabel: row.recipientLabel ?? row.recipient_label ?? null,
                visible: row.visible !== false,
                title: row.title || null,
            }));
        } catch (e) {
            console.warn('[DB] getUserMessages timed out', e.message);
            return [];
        }
    },

    /**
     * Deliver one broadcast message to many students (single row, union sentTo).
     * If dateSent is in the future, status becomes Scheduled (no push until cron releases).
     * @param {{ id: string, text?: string, title?: string, dateHide?: string, visible?: boolean, recipientIds: string[], dateSent?: string, recipientLabel?: string }}
     */
    async deliverMessage({
        id,
        text,
        title,
        dateHide,
        visible = true,
        recipientIds = [],
        dateSent,
        recipientLabel,
    } = {}) {
        if (!id) throw new Error('message id is required');
        const ids = [...new Set((recipientIds || []).map(String).filter(Boolean))];
        if (ids.length === 0) throw new Error('At least one recipient is required');

        const today = new Date().toISOString().split('T')[0];
        const planned = (dateSent && String(dateSent).slice(0, 10)) || today;
        const isFuture = planned > today;

        const { data: existing, error: fetchErr } = await supabase
            .from(getSBTable('messages'))
            .select('*')
            .eq('id', id)
            .maybeSingle();
        if (fetchErr) throw fetchErr;
        if (!existing) throw new Error('Message not found');

        const prevSent = Array.isArray(existing.sentTo) ? existing.sentTo.map(String) : [];
        const sentTo = [...new Set([...prevSent, ...ids])];

        const patch = {
            sentTo,
            dateSent: planned,
            status: isFuture ? 'Scheduled' : 'Sent',
            recipientLabel: recipientLabel || existing.recipientLabel || null,
            visible: visible !== false,
            updated_at: new Date().toISOString(),
        };
        if (text !== undefined) patch.text = text;
        if (title !== undefined) patch.title = title;
        if (dateHide !== undefined) patch.dateHide = dateHide;

        const { data, error } = await supabase
            .from(getSBTable('messages'))
            .update(patch)
            .eq('id', id)
            .select('*')
            .single();
        if (error) throw error;

        this.clearCache('messages');

        // Push only when actually delivered (not delayed)
        if (!isFuture) {
            try {
                const preview = String(patch.title || existing.title || '')
                    || String(patch.text || existing.text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
                fetch('/api/push/notify-message', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        messageId: id,
                        recipientIds: ids,
                        title: patch.title || existing.title || 'New message',
                        body: preview || 'You have a new message from your teacher',
                    }),
                }).catch(() => {});
            } catch {
                /* ignore */
            }
        }

        return data;
    },

    /** @deprecated Prefer deliverMessage — kept for callers that append one recipient. */
    async sendMessageToUser(userId, msg) {
        if (!userId || !msg?.id) return false;
        await this.deliverMessage({
            id: msg.id,
            text: msg.text,
            title: msg.title,
            dateHide: msg.dateHide,
            recipientIds: [userId],
            dateSent: msg.dateSent,
            recipientLabel: msg.recipientLabel,
        });
        return true;
    },

    async markMessageAsSent(msgId, date, targetLabel) {
        if (!msgId) return false;
        const { error } = await supabase
            .from(getSBTable('messages'))
            .update({
                dateSent: date || new Date().toISOString().split('T')[0],
                status: 'Sent',
                recipientLabel: targetLabel || null,
                updated_at: new Date().toISOString(),
            })
            .eq('id', msgId);
        if (error) {
            console.warn('[DB] markMessageAsSent failed:', error.message);
            return false;
        }
        this.clearCache('messages');
        return true;
    },

    async getMessageReadsForUser(userId) {
        if (!userId) return [];
        try {
            const { data, error } = await supabase
                .from('message_reads')
                .select('message_id, read_at')
                .eq('user_id', userId);
            if (error) return [];
            return data || [];
        } catch {
            return [];
        }
    },

    async markMessageRead(messageId, userId) {
        if (!messageId || !userId) return false;
        const { error } = await supabase
            .from('message_reads')
            .upsert({
                message_id: messageId,
                user_id: userId,
                read_at: new Date().toISOString(),
            }, { onConflict: 'message_id,user_id' });
        if (error) {
            console.warn('[DB] markMessageRead failed:', error.message);
            return false;
        }
        return true;
    },

    async notifyMessagePushEnabled(userId) {
        if (!userId) return null;
        return this.createTeacherNotification({
            studentId: userId,
            type: 'push_notify_enabled',
            message: 'Enabled message notifications (Notify me)',
            payload: { source: 'messages', date: getTodayDateStr() },
        });
    },

    async savePushSubscription(userId, subscription) {
        if (!userId || !subscription?.endpoint) return false;
        const keys = subscription.keys || {};
        const { error } = await supabase
            .from('push_subscriptions')
            .upsert({
                user_id: userId,
                endpoint: subscription.endpoint,
                p256dh: keys.p256dh || '',
                auth: keys.auth || '',
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            }, { onConflict: 'endpoint' });
        if (error) {
            console.warn('[DB] savePushSubscription failed:', error.message);
            return false;
        }
        return true;
    },

    async deletePushSubscription(endpoint) {
        if (!endpoint) return false;
        const { error } = await supabase
            .from('push_subscriptions')
            .delete()
            .eq('endpoint', endpoint);
        if (error) {
            console.warn('[DB] deletePushSubscription failed:', error.message);
            return false;
        }
        return true;
    },

    async getMessageAutomations() {
        const { data, error } = await supabase
            .from('message_automations')
            .select('*')
            .order('key');
        if (error) {
            console.warn('[DB] getMessageAutomations failed:', error.message);
            return [];
        }
        return data || [];
    },

    async updateMessageAutomation(key, patch) {
        if (!key) throw new Error('automation key required');
        const payload = {
            ...patch,
            updated_at: new Date().toISOString(),
        };
        delete payload.key;
        const { data, error } = await supabase
            .from('message_automations')
            .update(payload)
            .eq('key', key)
            .select('*')
            .single();
        if (error) throw error;
        return data;
    },

    async getMessageAutomationLogSummary() {
        const { data, error } = await supabase
            .from('message_automation_log')
            .select('rule_key, sent_at')
            .order('sent_at', { ascending: false })
            .limit(500);
        if (error) return {};
        const byRule = {};
        (data || []).forEach((row) => {
            if (!byRule[row.rule_key]) {
                byRule[row.rule_key] = { lastSentAt: row.sent_at, count: 0 };
            }
            byRule[row.rule_key].count += 1;
        });
        return byRule;
    },

    async prependItem(tableName, item) { return this.addItem(tableName, item); },

    async getAutomationRules() {
        const q = supabase
            .from(getSBTable('automationRules'))
            .select('*');

        const defaults = {
            testToRevisionStars: 1,
            testToRevisionCoins: 0,
            revisionToLearntStars: 1,
            revisionToLearntCoins: 0,
            lookingBackCardsStars: 0,
            lookingBackCardsCoins: 1,
            lookingBackSpellStars: 0,
            lookingBackSpellCoins: 1,
            meetNewWordsStars: 1,
            meetNewWordsCoins: 0,
        paidSurfEntryCoins: 10,
        paidChallengeEntryCoins: 10,
        paidChallengeRefundCoins: 10,
        paidChallengeBonusCoins: 5,
        paidDictationEntryCoins: 10,
        paidDictationPerfectCoins: 15,
        paidDictationOneMistakeCoins: 10,
        paidDictationPenaltyCoins: 10,
        paidSentencesEntryCoins: 10,
        paidSentencesPerfectCoins: 15,
        paidSentencesOneMistakeCoins: 10,
        paidSentencesPenaltyCoins: 10,
        streakPrize7Coins: 10,
            streakPrize14Coins: 25,
            streakPrize28Coins: 50,
            dailyQuestTrainingCoins: 1,
            dailyQuestLookingBackCoins: 1,
            dailyQuestPaidSurfCoins: 1,
            dailyQuestPaidChallengeCoins: 1,
            dailyQuestPaidDictationCoins: 1,
            dailyQuestPaidSentencesCoins: 1,
            dailyQuestExerciseCoins: 1,
            dailyQuestListenSongCoins: 1,
            dailyQuestWatchMediaCoins: 1,
            dailyQuestReadNewsCoins: 1,
        };

        try {
            const { data, error } = await this._withTimeout(q, 10000, 'getAutomationRules');
            if (error || !data || data.length === 0) return defaults;

            const result = { ...defaults };
            data.forEach(rule => {
                if (rule.key) {
                    const val = parseInt(rule.value, 10);
                    result[rule.key] = isNaN(val) ? 0 : val;
                }
            });
            return result;
        } catch (e) {
            console.warn('[DB] getAutomationRules timed out', e.message);
            return defaults;
        }
    },

    async updateAutomationRule(key, value) {
        const { error } = await supabase
            .from(getSBTable('automationRules'))
            .upsert({ key, value }, { onConflict: 'key' });
        return !error;
    },

    getLocalData() {
        return JSON.parse(localStorage.getItem('english_learning_db')) || {};
    },

    // в”Ђв”Ђв”Ђ Exercises в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    // Legacy slot: some mutators still clear `this._cache.getExercises` / `getBlocks`.
    // `_withTimeout` no longer writes here (label-only memoization was incorrect for
    // parameterized parallel gets such as getFullLessonSections).
    _cache: {},

    // Race a promise against a timeout so Supabase calls never hang forever.
    // Do NOT memoize by label alone: Study Plan loads many Full Lessons in parallel
    // via getFullLessonSections(lessonId); a shared 'getFullLessonSections' cache made
    // every card reuse the first lesson’s sections (all showed 100% Done).
    async _withTimeout(promise, ms = 20000, label = 'operation') {
        let timeoutId;
        const timer = new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
                reject(new Error(`[DB] ${label} timed out after ${ms}ms.`));
            }, ms);
        });

        try {
            const result = await Promise.race([promise, timer]);
            if (timeoutId) clearTimeout(timeoutId);
            return result;
        } catch (e) {
            if (timeoutId) clearTimeout(timeoutId);
            throw e;
        }
    },

    /**
     * Page through a PostgREST select until exhausted (self-hosted max_rows ~1000).
     * @param {() => import('@supabase/supabase-js').PostgrestFilterBuilder} buildQuery
     *   Fresh query each call (range is applied here).
     */
    async _paginateSelect(buildQuery, {
        pageSize = 1000,
        label = 'paginate',
        timeoutMs = 45000,
        orderColumn = 'id',
    } = {}) {
        const data = [];
        let from = 0;
        for (;;) {
            const pageQuery = buildQuery()
                .order(orderColumn, { ascending: true })
                .range(from, from + pageSize - 1);
            const result = await this._withTimeout(pageQuery, timeoutMs, `${label}:${from}`);
            if (result.error) return { data, error: result.error };
            const batch = result.data || [];
            data.push(...batch);
            if (batch.length < pageSize) break;
            from += pageSize;
        }
        return { data, error: null };
    },

    async getExercises() {
        console.log('[DB] getExercises: fetching full bank...');
        const q = supabase.from('exercises').select('*').order('created_at', { ascending: false });
        // Increased timeout to 60s for the full bank (used by teachers)
        const { data, error } = await this._withTimeout(q, 15000, 'getExercises');
        if (error) {
            console.error('[DB] getExercises ERROR:', error);
            throw error;
        }
        console.log('[DB] getExercises: got', data?.length ?? 0, 'rows');
        return data || [];
    },

    async getExercisesByIds(ids) {
        const idList = [...new Set((ids || []).map((id) => String(id || '').trim()).filter(Boolean))];
        if (idList.length === 0) return [];
        console.log('[DB] getExercisesByIds: fetching', idList.length, 'specific exercises...');
        // Kong/NPM 502 + CORS when one GET .in() URL is too long (~100+ UUIDs). 40 IDs already works on Student Page.
        const CHUNK = 40;
        const rows = [];
        for (let i = 0; i < idList.length; i += CHUNK) {
            const batch = idList.slice(i, i + CHUNK);
            const q = supabase.from('exercises').select('*').in('id', batch);
            const { data, error } = await this._withTimeout(q, 15000, `getExercisesByIds:${batch.length}`);
            if (error) {
                console.error('[DB] getExercisesByIds ERROR:', error);
                return rows;
            }
            rows.push(...(data || []));
        }
        return rows;
    },

    /**
     * Resolve a deep-link id for Exercise Bank (?exercise=…).
     * Accepts exercise id, block id, or exercise_assignment id (→ linked exercise/block).
     * @returns {Promise<{ kind: 'exercise'|'block', item: object, via?: string }|null>}
     */
    async resolveBankDeepLinkId(rawId) {
        const id = String(rawId || '').trim();
        if (!id) return null;

        const exRows = await this.getExercisesByIds([id]);
        if (exRows?.[0]) return { kind: 'exercise', item: exRows[0] };

        const blockRows = await this.getBlocksByIds([id]);
        if (blockRows?.[0]) return { kind: 'block', item: blockRows[0] };

        try {
            const q = supabase
                .from('exercise_assignments')
                .select('id, exercise_id, block_id, student_id')
                .eq('id', id)
                .maybeSingle();
            const { data: asg, error } = await this._withTimeout(q, 15000, 'resolveBankDeepLinkId:assignment');
            if (error) {
                console.warn('[DB] resolveBankDeepLinkId assignment:', error.message);
                return null;
            }
            if (!asg) return null;
            if (asg.exercise_id) {
                const linked = await this.getExercisesByIds([asg.exercise_id]);
                if (linked?.[0]) {
                    return { kind: 'exercise', item: linked[0], via: 'assignment' };
                }
            }
            if (asg.block_id) {
                const linked = await this.getBlocksByIds([asg.block_id]);
                if (linked?.[0]) {
                    return { kind: 'block', item: linked[0], via: 'assignment' };
                }
            }
        } catch (err) {
            console.warn('[DB] resolveBankDeepLinkId failed:', err);
        }
        return null;
    },

    async addExercise(exercise) {
        const id = exercise.id || crypto.randomUUID();
        const attributions = Array.isArray(exercise.attributions) && exercise.attributions.length > 0
            ? exercise.attributions
            : [{ course: exercise.course || '', module: exercise.module ?? '', lesson: exercise.lesson ?? '' }];
        const primary = attributions[0] || {};
        const payload = {
            id,
            type: exercise.type || 'fill_gaps',
            title: exercise.title || '',
            instruction: exercise.instruction || '',
            notes: exercise.notes || null,
            body: exercise.body || '',
            course: primary.course || '',
            module: primary.module ?? '',
            lesson: primary.lesson ?? '',
            attributions,
            skill: exercise.skill || '',
            theme: exercise.theme || '',
            wordlist_ids: exercise.wordlist_ids || null,
            wordlist_data: exercise.wordlist_data || null,
            assessment: exercise.assessment || null,
            teacher_comment: exercise.teacher_comment || null,
            teacher_notes: exercise.teacher_notes || null,
            answers_html: exercise.answers_html || null,
            is_info: exercise.is_info || false,
            created_at: exercise.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        console.log('[DB] addExercise: inserting', payload.id, payload.type, payload.title);
        this.clearCache('exercises');
        if (this._cache) delete this._cache.getExercises;
        const q = supabase.from('exercises').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 60000, 'addExercise');
        console.log('[DB] addExercise result:', { data: data?.length, error, title: data?.[0]?.title });
        if (error) throw error;
        return data?.[0] || null;
    },

    async updateExercise(id, delta) {
        const updatePayload = { ...delta, updated_at: new Date().toISOString() };
        Object.keys(updatePayload).forEach(k => updatePayload[k] === undefined && delete updatePayload[k]);
        // Keep attributions in sync when primary course/module/lesson are written together without attributions
        if (
            updatePayload.attributions === undefined
            && updatePayload.course !== undefined
            && updatePayload.module !== undefined
            && updatePayload.lesson !== undefined
        ) {
            updatePayload.attributions = [{
                course: updatePayload.course || '',
                module: updatePayload.module ?? '',
                lesson: updatePayload.lesson ?? '',
            }];
        }
        console.log('[DB] updateExercise:', id.slice(0, 8), Object.keys(updatePayload));
        this.clearCache('exercises');
        if (this._cache) delete this._cache.getExercises;
        const q = supabase.from('exercises').update(updatePayload).eq('id', id).select();
        const { data, error } = await this._withTimeout(q, 25000, 'updateExercise');
        console.log('[DB] updateExercise result:', { updated: data?.length, error, title: data?.[0]?.title });
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteExercise(id) {
        this.clearCache('exercises');
        console.log('[DB] deleteExercise:', id.slice(0, 8));
        const q = supabase.from('exercises').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteExercise');
        if (error) throw error;
    },

    // в”Ђв”Ђв”Ђ Public Exercises (Open Exercises Bank) в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async getPublicExercises() {
        console.log('[DB] getPublicExercises: fetching...');
        const q = supabase.from('public_exercises').select('*').order('created_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 15000, 'getPublicExercises');
        if (error) {
            console.error('[DB] getPublicExercises ERROR:', error);
            throw error;
        }
        return data || [];
    },

    async getPublicExerciseBySlug(slug) {
        console.log('[DB] getPublicExerciseBySlug:', slug);
        const q = supabase.from('public_exercises').select('*').eq('slug', slug).eq('active', true).single();
        const { data, error } = await this._withTimeout(q, 15000, 'getPublicExerciseBySlug');
        if (error) {
            console.error('[DB] getPublicExerciseBySlug ERROR:', error);
            return null;
        }
        return data;
    },

    async addPublicExercise(item) {
        const payload = {
            id: item.id || crypto.randomUUID(),
            slug: item.slug || null, // null triggers auto-generation
            type: item.type || 'exercise',
            exercise_id: item.exercise_id || null,
            block_id: item.block_id || null,
            title: item.title || '',
            description: item.description || '',
            banner_config: item.banner_config || {},
            body: item.body ?? null,
            child_snapshots: item.child_snapshots || {},
            active: item.active !== undefined ? item.active : true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        console.log('[DB] addPublicExercise:', payload.type, payload.slug || '(auto-generate)');
        const q = supabase.from('public_exercises').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addPublicExercise');
        if (error) throw error;
        return data?.[0] || null;
    },

    async updatePublicExercise(id, delta) {
        const updatePayload = { ...delta, updated_at: new Date().toISOString() };
        Object.keys(updatePayload).forEach(k => updatePayload[k] === undefined && delete updatePayload[k]);
        console.log('[DB] updatePublicExercise:', id.slice(0, 8));
        const q = supabase.from('public_exercises').update(updatePayload).eq('id', id).select();
        const { data, error } = await this._withTimeout(q, 25000, 'updatePublicExercise');
        if (error) throw error;
        return data?.[0] || null;
    },

    async deletePublicExercise(id) {
        console.log('[DB] deletePublicExercise:', id.slice(0, 8));
        const q = supabase.from('public_exercises').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deletePublicExercise');
        if (error) throw error;
    },

    /** Re-copy exercise/block content and images into public_exercises snapshot fields. */
    async refreshPublicExerciseSnapshot(publicExerciseId) {
        const q = supabase.from('public_exercises').select('*').eq('id', publicExerciseId).single();
        const { data: pub, error } = await this._withTimeout(q, 15000, 'refreshPublicExerciseSnapshot:load');
        if (error || !pub) throw new Error('Public exercise not found');

        if (pub.type === 'exercise' && pub.exercise_id) {
            const exList = await this.getExercisesByIds([pub.exercise_id]);
            const ex = exList?.[0];
            if (!ex) throw new Error('Linked exercise not found');
            const body = await this.copyImagesInContent(ex.body || '');
            return this.updatePublicExercise(publicExerciseId, { body });
        }

        if (pub.type === 'block' && pub.block_id) {
            const blockList = await this.getBlocksByIds([pub.block_id]);
            const block = blockList?.[0];
            if (!block) throw new Error('Linked block not found');
            const body = await this.copyImagesInContent(block.body || '');
            const childSnapshots = {};
            const ids = block.exercise_ids || [];
            if (ids.length > 0) {
                const exList = await this.getExercisesByIds(ids);
                for (const ex of exList) {
                    childSnapshots[ex.id] = await this.copyImagesInContent(ex.body || '');
                }
            }
            return this.updatePublicExercise(publicExerciseId, { body, child_snapshots: childSnapshots });
        }

        return pub;
    },

    // в”Ђв”Ђв”Ђ Storage: Copy images to public-exercises bucket в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async copyImageToPublicBucket(sourceUrl) {
        if (!sourceUrl || typeof sourceUrl !== 'string') return sourceUrl;
        if (sourceUrl.includes('/public-exercises/')) {
            return sourceUrl;
        }
        if (!sourceUrl.includes('/media/')) {
            return sourceUrl;
        }

        try {
            // Extract file path from URL
            const urlObj = new URL(sourceUrl);
            const pathMatch = urlObj.pathname.match(/\/storage\/v1\/object\/public\/media\/(.+)/);
            if (!pathMatch) return sourceUrl;
            
            const sourcePath = pathMatch[1];
            const fileName = sourcePath.split('/').pop();
            const destPath = `public-exercises/${fileName}`;

            // Download from source
            const { data: downloadData, error: downloadError } = await supabase.storage
                .from('media')
                .download(sourcePath);
            
            if (downloadError) throw downloadError;

            // Upload to destination
            const { error: uploadError } = await supabase.storage
                .from('public-exercises')
                .upload(destPath, downloadData, {
                    upsert: true,
                    contentType: downloadData.type
                });
            
            if (uploadError) throw uploadError;

            // Get public URL from new location
            const { data: { publicUrl } } = supabase.storage
                .from('public-exercises')
                .getPublicUrl(destPath);

            console.log('[DB] Copied image to public bucket:', sourcePath, '->', destPath);
            return publicUrl;
        } catch (error) {
            console.error('[DB] Failed to copy image to public bucket:', error);
            // Return original URL on failure
            return sourceUrl;
        }
    },

    // в”Ђв”Ђв”Ђ Storage: Convert public media URL to signed URL в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async getSignedMediaUrl(publicUrl, options = {}) {
        if (!publicUrl || typeof publicUrl !== 'string') return publicUrl;

        const filePath = extractMediaStoragePath(publicUrl);
        if (!filePath) return publicUrl;

        try {
            return await getCachedSignedMediaUrl(
                filePath,
                async (path) => {
                    const { data, error } = await supabase.storage
                        .from('media')
                        .createSignedUrl(path, 31536000);
                    if (error) throw error;
                    return data.signedUrl || publicUrl;
                },
                options
            );
        } catch (error) {
            console.error('[DB] Failed to create signed URL:', error);
            return publicUrl;
        }
    },

    // в”Ђв”Ђв”Ђ Storage: List files in media bucket в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async listMediaFiles(prefix = '', searchTerm = '') {
        try {
            console.log('[DB] Listing files in media bucket with prefix:', prefix);
            const { data, error } = await supabase.storage
                .from('media')
                .list(prefix, {
                    limit: 1000,
                    sortBy: { column: 'name', order: 'asc' }
                });

            if (error) throw error;

            const files = data || [];
            console.log('[DB] Total files found:', files.length);
            console.log('[DB] File names:', files.map(f => f.name));

            if (!searchTerm) return files;

            // Filter client-side for substring match (case-insensitive)
            const lowerSearch = searchTerm.toLowerCase();
            const filtered = files.filter(file => file.name.toLowerCase().includes(lowerSearch));
            console.log('[DB] Files after search filter:', filtered.length);
            return filtered;
        } catch (error) {
            console.error('[DB] Failed to list media files:', error);
            return [];
        }
    },

    /** Signed URL for a path inside the private `media` bucket. */
    async getMediaSignedUrlForPath(filePath, expiresInSec = 3600, options = {}) {
        if (!filePath || typeof filePath !== 'string') return null;
        const path = filePath.replace(/^\/+/, '');
        try {
            return await getCachedSignedMediaUrl(
                path,
                async (p) => {
                    const { data, error } = await supabase.storage
                        .from('media')
                        .createSignedUrl(p, expiresInSec);
                    if (error) throw error;
                    return data?.signedUrl || null;
                },
                options
            );
        } catch (error) {
            console.error('[DB] getMediaSignedUrlForPath failed:', error);
            return null;
        }
    },

    /** Permanently delete one object from the private `media` bucket. */
    async deleteMediaResource(filePath) {
        if (!filePath || typeof filePath !== 'string') {
            throw new Error('Missing media path');
        }
        const path = filePath.replace(/^\/+/, '');
        const { error } = await supabase.storage.from('media').remove([path]);
        if (error) throw error;
        return true;
    },

    /**
     * Recursively list image/audio files in the `media` bucket.
     * Folders (no id) are walked up to maxDepth; files are tagged with kind + path.
     */
    async listMediaResources({
        prefix = '',
        searchTerm = '',
        kinds = ['image', 'audio'],
        maxDepth = 5,
        maxFiles = 2500,
    } = {}) {
        const IMAGE_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;
        const AUDIO_RE = /\.(mp3|wav|ogg|m4a|aac|flac|webm)$/i;
        const kindSet = new Set(kinds);
        const lowerSearch = (searchTerm || '').trim().toLowerCase();
        const out = [];

        const walk = async (folder, depth) => {
            if (out.length >= maxFiles || depth > maxDepth) return;
            const { data, error } = await supabase.storage.from('media').list(folder, {
                limit: 1000,
                sortBy: { column: 'name', order: 'asc' },
            });
            if (error) throw error;

            const items = data || [];
            const subfolders = [];
            for (const item of items) {
                if (out.length >= maxFiles) break;
                const path = folder ? `${folder}/${item.name}` : item.name;
                const isFolder = item.id == null;
                if (isFolder) {
                    subfolders.push(path);
                    continue;
                }
                const name = item.name || '';
                let kind = null;
                if (IMAGE_RE.test(name)) kind = 'image';
                else if (AUDIO_RE.test(name)) kind = 'audio';
                if (!kind || !kindSet.has(kind)) continue;
                if (lowerSearch && !path.toLowerCase().includes(lowerSearch)) continue;
                out.push({
                    name,
                    path,
                    folder: folder || '',
                    kind,
                    size: item.metadata?.size ?? null,
                    mimeType: item.metadata?.mimetype || null,
                    updatedAt: item.updated_at || item.created_at || null,
                });
            }
            for (const sub of subfolders) {
                if (out.length >= maxFiles) break;
                await walk(sub, depth + 1);
            }
        };

        try {
            await walk((prefix || '').replace(/\/+$/, ''), 0);
            out.sort((a, b) => a.path.localeCompare(b.path));
            return out;
        } catch (error) {
            console.error('[DB] listMediaResources failed:', error);
            return [];
        }
    },

    /**
     * Find DB rows that reference a media object path (or its filename).
     * Covers exercises, blocks, word image tables, and writing-checker checks.
     */
    async findMediaUsages(storagePath) {
        if (!storagePath || typeof storagePath !== 'string') return [];
        const path = storagePath.replace(/^\/+/, '');
        const fileName = path.split('/').pop() || path;
        const needles = [path];
        if (fileName && fileName !== path) needles.push(fileName);

        const escapeIlike = (s) => String(s).replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
        const usages = [];
        const seen = new Set();

        const add = (row) => {
            const key = `${row.source}:${row.id}:${row.field || ''}`;
            if (seen.has(key)) return;
            seen.add(key);
            usages.push(row);
        };

        const textContains = (value, needle) => {
            if (value == null) return false;
            const text = typeof value === 'string' ? value : JSON.stringify(value);
            return text.includes(needle);
        };

        for (const needle of needles) {
            const pattern = `%${escapeIlike(needle)}%`;
            const q = (col) => `${col}.ilike."${pattern.replace(/"/g, '')}"`;

            try {
                const { data, error } = await supabase
                    .from('exercises')
                    .select('id, title, type, course, module, lesson')
                    .or([q('body'), q('instruction'), q('answers_html'), q('teacher_notes')].join(','))
                    .limit(200);
                if (error) throw error;
                for (const ex of data || []) {
                    add({
                        source: 'exercise',
                        id: ex.id,
                        title: ex.title || 'Untitled exercise',
                        detail: [ex.type, ex.course, ex.module, ex.lesson].filter(Boolean).join(' · '),
                        field: 'content',
                    });
                }
            } catch (err) {
                console.warn('[DB] findMediaUsages exercises:', err);
            }

            try {
                const { data, error } = await supabase
                    .from('blocks')
                    .select('id, title, course, module, lesson')
                    .ilike('instruction', pattern)
                    .limit(100);
                if (error) throw error;
                for (const b of data || []) {
                    add({
                        source: 'block',
                        id: b.id,
                        title: b.title || 'Untitled block',
                        detail: [b.course, b.module, b.lesson].filter(Boolean).join(' · '),
                        field: 'instruction',
                    });
                }
            } catch (err) {
                console.warn('[DB] findMediaUsages blocks:', err);
            }

            for (const table of [
                { table: 'global_words', source: 'global_word', label: (r) => r.english || r.id },
                { table: 'group_words', source: 'group_word', label: (r) => r.english || r.id },
                { table: 'show_games', source: 'show_game', label: (r) => r.english || r.id },
            ]) {
                try {
                    const { data, error } = await supabase
                        .from(table.table)
                        .select('id, english, course, module, lesson')
                        .ilike('image_url', pattern)
                        .limit(100);
                    if (error) throw error;
                    for (const row of data || []) {
                        add({
                            source: table.source,
                            id: row.id,
                            title: table.label(row),
                            detail: [row.course, row.module, row.lesson].filter(Boolean).join(' · '),
                            field: 'image_url',
                        });
                    }
                } catch (err) {
                    console.warn(`[DB] findMediaUsages ${table.table}:`, err);
                }
            }

            try {
                const { data, error } = await supabase
                    .from('writing_checker_checks')
                    .select('id, user_id, created_at, task_image_urls, answer_image_urls, answer_audio_urls')
                    .limit(500);
                if (error) throw error;
                for (const row of data || []) {
                    const fields = [];
                    if (textContains(row.task_image_urls, needle)) fields.push('task_image_urls');
                    if (textContains(row.answer_image_urls, needle)) fields.push('answer_image_urls');
                    if (textContains(row.answer_audio_urls, needle)) fields.push('answer_audio_urls');
                    if (!fields.length) continue;
                    add({
                        source: 'writing_checker',
                        id: row.id,
                        title: `Check ${String(row.id).slice(0, 8)}…`,
                        detail: row.user_id ? `user ${String(row.user_id).slice(0, 8)}…` : '',
                        field: fields.join(', '),
                    });
                }
            } catch (err) {
                console.warn('[DB] findMediaUsages writing_checker:', err);
            }
        }

        return usages;
    },

    /**
     * Scan DB content once and return a Set of referenced media paths + filenames.
     * Used by Site Admin → Resources for Used / Not used filtering.
     */
    async collectReferencedMediaKeys() {
        const keys = new Set();
        const MEDIA_FOLDER_RE =
            /\b((?:exercises|audio|slides|word-images|writing-checker|presentations)\/[^\s"'<>?)\]}]+)/gi;
        const STORAGE_URL_RE =
            /\/storage\/v1\/object\/(?:sign|public|authenticated)\/media\/([^?"'\s)\]}]+)/gi;

        const addKey = (raw) => {
            if (!raw || typeof raw !== 'string') return;
            let path = raw.trim();
            if (!path) return;
            try {
                path = decodeURIComponent(path);
            } catch {
                /* keep raw */
            }
            path = path.replace(/^\/+/, '').split('?')[0];
            if (!path) return;
            keys.add(path);
            const name = path.split('/').pop();
            if (name) keys.add(name);
        };

        const harvest = (value) => {
            if (value == null) return;
            const text = typeof value === 'string' ? value : JSON.stringify(value);
            if (!text) return;
            let m;
            STORAGE_URL_RE.lastIndex = 0;
            while ((m = STORAGE_URL_RE.exec(text)) !== null) addKey(m[1]);
            MEDIA_FOLDER_RE.lastIndex = 0;
            while ((m = MEDIA_FOLDER_RE.exec(text)) !== null) addKey(m[1]);
            const direct = extractMediaStoragePath(text);
            if (direct) addKey(direct);
        };

        const scanTable = async (table, columns, { filter } = {}) => {
            try {
                let q = supabase.from(table).select(columns.join(', '));
                if (typeof filter === 'function') q = filter(q);
                const { data, error } = await q;
                if (error) throw error;
                for (const row of data || []) {
                    for (const col of columns) harvest(row[col]);
                }
            } catch (err) {
                console.warn(`[DB] collectReferencedMediaKeys ${table}:`, err);
            }
        };

        await Promise.all([
            scanTable('exercises', ['body', 'instruction', 'answers_html', 'teacher_notes']),
            scanTable('blocks', ['instruction']),
            scanTable('global_words', ['image_url'], {
                filter: (q) => q.not('image_url', 'is', null),
            }),
            scanTable('group_words', ['image_url'], {
                filter: (q) => q.not('image_url', 'is', null),
            }),
            scanTable('show_games', ['image_url'], {
                filter: (q) => q.not('image_url', 'is', null),
            }),
            scanTable('writing_checker_checks', [
                'task_image_urls',
                'answer_image_urls',
                'answer_audio_urls',
            ]),
        ]);

        return keys;
    },

    async copyImagesInContent(content) {
        if (!content || typeof content !== 'string') return content;

        // Find all image URLs in markdown syntax: ![alt](url)
        const imageRegex = /!\[([^\]]*)\]\((https?:\/\/[^\)]+)\)/g;
        let updatedContent = content;
        let match;

        while ((match = imageRegex.exec(content)) !== null) {
            const [fullMatch, alt, url] = match;
            const newUrl = await this.copyImageToPublicBucket(url);
            if (newUrl !== url) {
                updatedContent = updatedContent.replace(url, newUrl);
            }
        }

        return updatedContent;
    },

    // в”Ђв”Ђв”Ђ Exercise Assignments в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async getExerciseAssignments() {
        console.log('[DB] getExerciseAssignments: fetching...');
        const q = supabase.from('exercise_assignments').select('*').order('assigned_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getExerciseAssignments');
        if (error) {
            console.error('[DB] getExerciseAssignments ERROR:', error);
            throw error;
        }
        console.log('[DB] getExerciseAssignments: got', data?.length ?? 0, 'rows');
        return data || [];
    },

    /**
     * Assignments for Exercise/Block Stats: direct exercise rows and/or block rows.
     * Prefer this over getExerciseAssignments() when opening Stats for one item.
     */
    async getAssignmentsForExerciseStats({ exerciseId = null, blockId = null, blockIds = [] } = {}) {
        const filters = [];
        if (exerciseId) filters.push(`exercise_id.eq.${exerciseId}`);
        if (blockId) filters.push(`block_id.eq.${blockId}`);
        const ids = (blockIds || []).filter(Boolean).map(String);
        if (ids.length === 1) filters.push(`block_id.eq.${ids[0]}`);
        else if (ids.length > 1) filters.push(`block_id.in.(${ids.join(',')})`);
        if (!filters.length) return [];

        console.log('[DB] getAssignmentsForExerciseStats:', { exerciseId, blockId, blockIds: ids.length });
        const q = supabase
            .from('exercise_assignments')
            .select('*')
            .or(filters.join(','))
            .order('assigned_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getAssignmentsForExerciseStats');
        if (error) {
            console.error('[DB] getAssignmentsForExerciseStats ERROR:', error);
            throw error;
        }
        return data || [];
    },

    // в”Ђв”Ђв”Ђ Blocks в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async getBlocks() {
        console.log('[DB] getBlocks: fetching...');
        const q = supabase.from('blocks').select('*').order('created_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getBlocks');
        if (error) {
            console.error('[DB] getBlocks ERROR:', error);
            throw error;
        }
        return data || [];
    },

    async getBlocksByIds(ids) {
        const idList = [...new Set((ids || []).map((id) => String(id || '').trim()).filter(Boolean))];
        if (idList.length === 0) return [];
        console.log('[DB] getBlocksByIds:', idList.length);
        const CHUNK = 40;
        const rows = [];
        for (let i = 0; i < idList.length; i += CHUNK) {
            const batch = idList.slice(i, i + CHUNK);
            const q = supabase.from('blocks').select('*').in('id', batch);
            const { data, error } = await this._withTimeout(q, 30000, `getBlocksByIds:${batch.length}`);
            if (error) {
                console.error('[DB] getBlocksByIds ERROR:', error);
                return rows;
            }
            rows.push(...(data || []));
        }
        return rows;
    },

    async addBlock(block) {
        this.clearCache('blocks');
        if (this._cache) delete this._cache.getBlocks;

        const payload = {
            id: block.id || crypto.randomUUID(),
            title: String(block.title || '').trim() || 'New Block',
            instruction: block.instruction == null ? '' : String(block.instruction),
            exercise_ids: Array.isArray(block.exercise_ids)
                ? block.exercise_ids.map((x) => String(x)).filter(Boolean)
                : [],
            course: block.course == null ? '' : String(block.course),
            module: block.module == null || block.module === '' ? '' : String(block.module),
            lesson: block.lesson == null || block.lesson === '' ? '' : String(block.lesson),
            is_info: !!block.is_info,
            is_test: !!block.is_test,
            enable_timer: !!block.enable_timer,
            show_timer: block.show_timer !== false,
            completion_course: block.completion_course == null ? null : String(block.completion_course),
            completion_module: block.completion_module == null ? null : String(block.completion_module),
            completion_lesson: block.completion_lesson == null ? null : String(block.completion_lesson),
            completion_full_lesson_id: block.completion_full_lesson_id == null
                ? null
                : String(block.completion_full_lesson_id),
            test_time_seconds: Number.isFinite(Number(block.test_time_seconds))
                ? Number(block.test_time_seconds)
                : 0,
            created_at: block.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        console.log('[DB] addBlock:', payload.id, 'exercises:', payload.exercise_ids.length);

        // Prefer server path: blocks RLS uses is_teacher(); self-hosted client inserts can hang.
        if (typeof window !== 'undefined') {
            try {
                const session = await ensureAuthSession();
                const res = await fetch('/api/blocks/create', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify(payload),
                });
                const json = await res.json().catch(() => ({}));
                if (res.ok && json?.block) {
                    return json.block;
                }
                console.warn('[DB] addBlock API failed, trying direct insert:', res.status, json?.error);
                if (res.status === 401 || res.status === 403) {
                    throw new Error(json?.error || 'Not allowed to create blocks');
                }
                // 404 = API route not deployed yet; keep a short direct fallback.
                // Other server errors: still try direct, but don't wait a full minute.
            } catch (e) {
                if (String(e?.message || '').includes(SESSION_EXPIRED) || /Not allowed to create blocks/i.test(String(e?.message || ''))) {
                    throw e;
                }
                console.warn('[DB] addBlock API error, trying direct insert:', e?.message || e);
            }
        }

        const q = supabase.from('blocks').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 15000, 'addBlock');
        if (error) throw error;
        return data?.[0] || null;
    },

    async updateBlock(id, delta) {
        this.clearCache('blocks');
        const blockId = String(id || '').trim();
        console.log('[DB] updateBlock:', blockId.slice(0, 8));
        if (!blockId) throw new Error('Block id is required');

        const patch = { ...(delta && typeof delta === 'object' ? delta : {}) };
        delete patch.id;

        // Prefer server path: blocks RLS uses is_teacher(); self-hosted client updates can hang
        // the same way inserts did (see /api/blocks/create).
        if (typeof window !== 'undefined') {
            try {
                const session = await ensureAuthSession();
                const res = await fetch('/api/blocks/update', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({ id: blockId, ...patch }),
                });
                const json = await res.json().catch(() => ({}));
                if (res.ok && json?.block) {
                    return json.block;
                }
                console.warn('[DB] updateBlock API failed, trying direct update:', res.status, json?.error);
                if (res.status === 401 || res.status === 403) {
                    throw new Error(json?.error || 'Not allowed to update blocks');
                }
            } catch (e) {
                if (String(e?.message || '').includes(SESSION_EXPIRED) || /Not allowed to update blocks/i.test(String(e?.message || ''))) {
                    throw e;
                }
                console.warn('[DB] updateBlock API error, trying direct update:', e?.message || e);
            }
        }

        const q = supabase.from('blocks').update(patch).eq('id', blockId).select();
        const { data, error } = await this._withTimeout(q, 15000, 'updateBlock');
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteBlock(id) {
        this.clearCache('blocks');
        console.log('[DB] deleteBlock:', String(id).slice(0, 8));
        const q = supabase.from('blocks').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteBlock');
        if (error) throw error;
    },

    async assignExercise(assignment) {
        const id = assignment.id || crypto.randomUUID();
        const payload = {
            id,
            exercise_id: assignment.exercise_id || null,
            block_id: assignment.block_id || null,
            student_id: assignment.student_id,
            student_name: assignment.student_name || '',
            group_id: assignment.group_id || null,
            status: assignment.status || 'assigned',
            assigned_at: new Date().toISOString(),
            sub_statuses: assignment.sub_statuses || {},
            target_course: assignment.target_course || null,
            target_module: assignment.target_module || null,
            target_lesson: assignment.target_lesson || null,
        };
        if (assignment.completed_at != null) payload.completed_at = assignment.completed_at;
        if (assignment.checked_at != null) payload.checked_at = assignment.checked_at;
        if (assignment.awarded_stars != null) payload.awarded_stars = assignment.awarded_stars;
        if (assignment.awarded_coins != null) payload.awarded_coins = assignment.awarded_coins;
        console.log('[DB] assignExercise:', (payload.exercise_id||payload.block_id).slice(0, 8), '->', String(payload.student_id).slice(0, 8));
        const q = supabase.from('exercise_assignments').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 60000, 'assignExercise');
        console.log('[DB] assignExercise result:', { inserted: data?.length, error });
        if (error) throw error;
        return data?.[0] || null;
    },

    async updateAssignment(id, delta) {
        console.log('[DB] updateAssignment:', String(id).slice(0, 8), 'keys:', Object.keys(delta || {}));
        let normalizedDelta = prepareAssignmentDelta(delta);
        try {
            // Nesting meta into answers must not wipe the student's answer map.
            if (
                normalizedDelta
                && normalizedDelta.answers
                && typeof normalizedDelta.answers === 'object'
                && answersPatchIsMetaOnly(normalizedDelta.answers)
            ) {
                try {
                    const q0 = supabase
                        .from('exercise_assignments')
                        .select('answers')
                        .eq('id', id)
                        .maybeSingle();
                    const { data: cur } = await this._withTimeout(q0, 10000, 'updateAssignment:answersMerge');
                    const prev = (cur?.answers && typeof cur.answers === 'object') ? cur.answers : {};
                    normalizedDelta = {
                        ...normalizedDelta,
                        answers: { ...prev, ...normalizedDelta.answers },
                    };
                } catch (mergeErr) {
                    console.warn('[DB] updateAssignment: answers merge skipped', mergeErr?.message || mergeErr);
                }
            }

            const q = supabase.from('exercise_assignments').update(normalizedDelta).eq('id', id).select();
            const { data, error } = await this._withTimeout(q, 25000, 'updateAssignment');

            if (error) {
                // If column error, strip unknown / reward columns and retry
                if (error.message?.includes('schema cache') || error.code === '42703' || error.code === 'PGRST204') {
                    console.warn("[DB] updateAssignment: Missing columns! Using stripped fallback.", error.message);
                    const missingCol = error.message?.match(/'([^']+)' column/)?.[1];
                    const { awarded_coins, awarded_stars, total, ...rest } = delta || {};
                    const safeDelta = { ...rest };
                    if (missingCol) delete safeDelta[missingCol];
                    // Always nest known inline keys (covers test_completed / gap_rewards)
                    let fallbackDelta = prepareAssignmentDelta(safeDelta);
                    if (missingCol && missingCol in (fallbackDelta || {})) {
                        delete fallbackDelta[missingCol];
                    }
                    if (
                        fallbackDelta?.answers
                        && typeof fallbackDelta.answers === 'object'
                        && answersPatchIsMetaOnly(fallbackDelta.answers)
                    ) {
                        try {
                            const q0 = supabase
                                .from('exercise_assignments')
                                .select('answers')
                                .eq('id', id)
                                .maybeSingle();
                            const { data: cur } = await this._withTimeout(q0, 10000, 'updateAssignment:fallbackMerge');
                            const prev = (cur?.answers && typeof cur.answers === 'object') ? cur.answers : {};
                            fallbackDelta = {
                                ...fallbackDelta,
                                answers: { ...prev, ...fallbackDelta.answers },
                            };
                        } catch { /* ignore */ }
                    }
                    const q2 = supabase.from('exercise_assignments').update(fallbackDelta).eq('id', id).select();
                    const { data: data2, error: err2 } = await this._withTimeout(q2, 25000, 'updateAssignment:fallback');
                    if (err2) throw err2;
                    if (!data2 || data2.length === 0) {
                        console.warn('[DB] updateAssignment fallback: No rows updated for', String(id).slice(0, 8));
                    } else {
                        console.log('[DB] updateAssignment fallback success:', data2[0]?.status, data2[0]?.score);
                    }
                    const rows = (data2 || []).map(applyAssignmentMeta);
                    const fallbackRow = rows?.[0] || null;
                    this._notifyExerciseCompleted(fallbackRow, fallbackDelta);
                    return fallbackRow;
                } else {
                    throw error;
                }
            }

            if (!data || data.length === 0) {
                console.warn('[DB] updateAssignment: No rows updated for', String(id).slice(0, 8));
                return null;
            }

            const rows = data.map(applyAssignmentMeta);
            console.log('[DB] updateAssignment success:', rows[0]?.status, 'score:', rows[0]?.score);
            const updated = rows[0] || null;
            this._notifyExerciseCompleted(updated, normalizedDelta);
            return updated;
        } catch (err) {
            console.error('[DB] updateAssignment critical error:', err);
            throw err;
        }
    },

    async deleteAssignment(id) {
        this.clearCache('exerciseAssignments');
        console.log('[DB] deleteAssignment:', String(id).slice(0, 8));
        const q = supabase.from('exercise_assignments').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteAssignment');
        if (error) throw error;
    },

    async batchUpdateAssignments(ids, delta) {
        if (!ids || ids.length === 0) return;
        console.log('[DB] batchUpdateAssignments:', ids.length, 'records');
        const q = supabase.from('exercise_assignments').update(delta).in('id', ids);
        const { error } = await this._withTimeout(q, 30000, 'batchUpdateAssignments');
        if (error) throw error;
    },

    async batchDeleteAssignments(ids) {
        if (!ids || ids.length === 0) return;
        console.log('[DB] batchDeleteAssignments:', ids.length, 'records');
        const q = supabase.from('exercise_assignments').delete().in('id', ids);
        const { error } = await this._withTimeout(q, 30000, 'batchDeleteAssignments');
        if (error) throw error;
    },


    async getAssignmentsForStudent(studentId) {
        const q = supabase.from('exercise_assignments').select('*').eq('student_id', studentId).order('assigned_at', { ascending: false });
        try {
            const { data, error } = await this._withTimeout(q, 20000, 'getAssignmentsForStudent');
            if (error) { console.error('[DB] getAssignmentsForStudent error:', error); return []; }
            return (data || []).map(applyAssignmentMeta);
        } catch (e) {
            console.warn('[DB] getAssignmentsForStudent timed out', e.message);
            return [];
        }
    },

    async getGroupsForStudent(userId, fullName) {
        if (!userId) return [];
        console.log('[DB] getGroupsForStudent (optimized):', userId);
        
        let q = supabase.from('groups').select('*');
        const idSearch = `enrollments.cs.[{"userId":"${userId}"}]`;
        
        if (fullName) {
            // Escape double-quotes inside the name, then wrap in quotes if it has reserved chars
            const safeName = fullName.replace(/"/g, '\\"');
            const nameSearch = `enrollments.cs.[{"userName":"${safeName}"}]`;
            q = q.or(`${idSearch},${nameSearch}`);
        } else {
            q = q.or(idSearch);
        }

        try {
            const { data, error } = await this._withTimeout(q, 20000, 'getGroupsForStudent');
            if (error) { 
                console.warn('[DB] getGroupsForStudent filtered fetch failed:', error.message);
                return []; 
            }
            return data || [];
        } catch (e) {
            console.warn('[DB] getGroupsForStudent TIMED OUT:', e.message);
            return [];
        }
    },

    async getLessonsForGroups(groupIdents) {
        if (!groupIdents || groupIdents.length === 0) return [];
        const validIdents = Array.isArray(groupIdents) ? groupIdents.map(String).filter(n => n && n !== 'null' && n !== 'undefined') : [];
        if (validIdents.length === 0) return [];

        console.log('[DB] getLessonsForGroups:', validIdents.length);

        // Separate UUIDs from names to build a safe PostgREST filter.
        // Values with reserved chars (commas, parentheses) must be double-quoted.
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const uuids = validIdents.filter(v => uuidPattern.test(v));
        const names = validIdents.filter(v => !uuidPattern.test(v));

        // Quote a value for PostgREST if it contains reserved characters
        const pgrstQuote = (v) => /[,().]/.test(v) ? `"${v}"` : v;

        // Build OR filter parts
        const parts = [];
        if (uuids.length > 0) {
            parts.push(`group.in.(${uuids.join(',')})`);
        }
        if (names.length > 0) {
            parts.push(`group.in.(${names.map(pgrstQuote).join(',')})`);
        }
        if (parts.length === 0) return [];

        const q = supabase.from('lessons')
            .select('id, group, datetime, attendance, homework, created_at, day_of_week, m_sum, next_datetime, homework_to_check')
            .or(parts.join(','));
            
        try {
            const { data, error } = await this._withTimeout(q, 15000, `getLessonsForGroups:${validIdents.length}`);
            if (error) { console.error('[DB] getLessonsForGroups error:', error); return []; }
            
            return (data || []).map(row => ({
                ...row,
                dateAdded: row.created_at?.split('T')[0],
                dayOfWeek: row.day_of_week,
                mSum: row.m_sum,
                nextDatetime: row.next_datetime,
                homeworkToCheck: row.homework_to_check
            }));
        } catch (e) {
            console.warn('[DB] getLessonsForGroups timed out', e.message);
            return [];
        }
    },

    async getUsers() {
        console.log('[DB] getUsers starting...');
        const q = supabase.from('profiles').select('*');
        const { data, error } = await this._withTimeout(q, 15000, 'getUsers');
        if (error) {
            console.error('[DB] getUsers error:', error);
            return [];
        }
        console.log('[DB] getUsers completed:', data?.length || 0);
        return data || [];
    },

    async getGroups() {
        console.log('[DB] getGroups starting...');
        const q = supabase.from('groups').select('*');
        const { data, error } = await this._withTimeout(q, 15000, 'getGroups');
        if (error) {
            console.error('[DB] getGroups error:', error);
            return [];
        }
        console.log('[DB] getGroups completed:', data?.length || 0);
        return data || [];
    },

    async getUsersByIds(ids) {
        if (!ids || ids.length === 0) return [];
        const validIds = Array.isArray(ids) ? ids.map(String).filter(id => id && id !== 'null' && id !== 'undefined') : [];
        if (validIds.length === 0) return [];
        
        console.log('[DB] getUsersByIds:', validIds.length);
        // Only need names for leaderboards
        const q = supabase.from('profiles').select('id, name, surname').in('id', validIds);
        const { data, error } = await this._withTimeout(q, 15000, `getUsersByIds:${ids.length}`);
        if (error) return [];
        return data || [];
    },

    async getUserProgressList(ids) {
        if (!ids || ids.length === 0) return [];
        // Filter to ensure only non-empty strings are passed
        const validIds = Array.isArray(ids) ? ids.map(String).filter(id => id && id !== 'null' && id !== 'undefined') : [];
        if (validIds.length === 0) return [];

        console.log('[DB] getUserProgressList:', validIds.length);
        // CRITICAL OPTIMIZATION: Do not load full 'progress' blob because it contains megabytes of 'user_words'.
        // Load just the required columns.
        const q = supabase.from('user_progress').select('user_id, coins, course_stats').in('user_id', validIds);
        
        try {
            const { data, error } = await this._withTimeout(q, 30000, `getUserProgressList:${validIds.length}`);
            if (error) {
                console.error('[DB] getUserProgressList ERROR:', error);
                return [];
            }
            return (data || []).map(row => {
            const blob = row.progress || {};
            const course_stats = (row.course_stats && typeof row.course_stats === 'object' && Object.keys(row.course_stats).length > 0)
                ? row.course_stats : (blob.course_stats || {});

            return {
                ...row,
                // currency removed
                coins: row.coins !== undefined && row.coins !== null ? row.coins : (blob.coins ?? 0),
                course_stats: course_stats
            };
        });
    } catch (e) {
        console.warn('[DB] getUserProgressList timed out/failed', e.message);
        return [];
    }
},

    /** Lesson Details overlay: progress + words for enrolled students only (not full-table HOME fetch). */
    async getUserProgressForStudents(userIds) {
        if (!userIds || userIds.length === 0) return [];
        const validIds = [...new Set(
            (Array.isArray(userIds) ? userIds : [userIds])
                .map(String)
                .filter(id => id && id !== 'null' && id !== 'undefined')
        )];
        if (validIds.length === 0) return [];

        console.log('[DB] getUserProgressForStudents:', validIds.length);
        return this.getTable(
            'userProgress',
            null,
            'user_id, user_words, course_stats, coins, rewards, progress',
            { user_id: validIds }
        );
    },

    // в”Ђв”Ђв”Ђ Payments & Financial Ledger в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

    async logPayment(studentId, amount, type, note = '', lessonId = null, options = {}) {
        if (!studentId || amount === undefined) throw new Error('[DB] Missing required fields for payment log');
        const skipTax = Boolean(options && options.skipTax);
        
        let finalNote = note;
        if (lessonId) {
            finalNote = `${note} [LID:${lessonId}]`;

            // Duplicate guard is lesson_cost-only. Tax (and other negatives) share the same
            // [LID:…] tag; blocking any negative would skip markTaxPaid after ADD NEW.
            if (type === 'lesson_cost' && amount < 0) {
                const { data: existing, error: checkError } = await supabase
                    .from('payment_ledger')
                    .select('id')
                    .eq('student_id', studentId)
                    .eq('type', 'lesson_cost')
                    .ilike('note', `%[LID:${lessonId}]%`)
                    .lt('amount', 0)
                    .limit(1);

                if (!checkError && existing && existing.length > 0) {
                    console.log(`[DB] logPayment skipped duplicate for lesson ${lessonId}`);
                    return null;
                }
            }
        }

        console.log('[DB] logPayment:', String(studentId).slice(0, 8), type, amount, skipTax ? '(no tax)' : '');
        const payload = {
            student_id: studentId,
            amount: parseFloat(amount),
            type,
            note: finalNote,
            created_at: new Date().toISOString()
        };
        const q = supabase.from('payment_ledger').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'logPayment');
        if (error) throw error;
        const entry = data?.[0] || null;

        // For top_up payments, immediately calculate and record tax (unless skipTax)
        if (entry && type === 'top_up' && amount > 0 && !skipTax) {
            const afterTax = Math.round(amount * 0.96);
            const taxAmount = amount - afterTax;

            await supabase
                .from('payment_ledger')
                .update({ taxed: true, after_tax: afterTax })
                .eq('id', entry.id);

            // Insert tax deduction entry with lesson ID if available for grouping
            await this.logPayment(studentId, -taxAmount, 'tax', 'Tax (4%)', lessonId);
        }

        return entry;
    },

    async markTaxPaid(studentId, lessonId, skipTaxEntry = false) {
        if (!studentId || !lessonId) return null;
        const lidSuffix = `${lessonId}_cash`;
        console.log('[DB] markTaxPaid:', String(studentId).slice(0, 8), lidSuffix, 'skipTaxEntry:', skipTaxEntry);
        
        // Find the top_up entry for this lesson
        const { data: entries } = await supabase
            .from('payment_ledger')
            .select('id, amount, taxed')
            .eq('student_id', studentId)
            .eq('type', 'top_up')
            .ilike('note', `%[LID:${lidSuffix}]%`)
            .limit(1);
        
        if (!entries || entries.length === 0) {
            console.log('[DB] markTaxPaid: no top_up entry found for', lidSuffix);
            return null;
        }
        
        const entry = entries[0];
        const amount = parseFloat(entry.amount) || 0;
        const afterTax = Math.round(amount * 0.96);
        
        // Update the top_up entry
        const { error: updateError } = await supabase
            .from('payment_ledger')
            .update({ taxed: true, after_tax: afterTax })
            .eq('id', entry.id);
        
        if (updateError) {
            console.error('[DB] markTaxPaid update error:', updateError);
            throw updateError;
        }
        
        // Avoid a second tax row if one already exists for this lesson (or caller asks to skip)
        if (!skipTaxEntry) {
            const { data: existingTax } = await supabase
                .from('payment_ledger')
                .select('id')
                .eq('student_id', studentId)
                .eq('type', 'tax')
                .or(`note.ilike.%[LID:${lessonId}]%,note.ilike.%[LID:${lidSuffix}]%`)
                .limit(1);
            if (existingTax && existingTax.length > 0) {
                console.log('[DB] markTaxPaid: tax row already exists, skipping insert');
                return { topUpId: entry.id, afterTax, taxAmount: amount - afterTax, skippedDuplicate: true };
            }
            const taxAmount = -(amount - afterTax); // negative = deduction
            await this.logPayment(studentId, taxAmount, 'tax', `Tax (4%) for lesson`, lessonId);
        }
        
        return { topUpId: entry.id, afterTax, taxAmount: skipTaxEntry ? (amount - afterTax) : -(amount - afterTax) };
    },

    async getPaymentHistory(studentId) {
        if (!studentId) return [];
        await this._assertSelfOrTeacher(studentId);
        console.log('[DB] getPaymentHistory:', String(studentId).slice(0, 8));
        const q = supabase.from('payment_ledger').select('*').eq('student_id', studentId).order('created_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getPaymentHistory');
        if (error) throw error;
        return data || [];
    },

    /** True if this student already has a lesson_cost debit for the lesson UUID. */
    async hasLessonCostCharge(studentId, lessonId) {
        if (!studentId || !lessonId) return false;
        const { data, error } = await supabase
            .from('payment_ledger')
            .select('id')
            .eq('student_id', studentId)
            .eq('type', 'lesson_cost')
            .lt('amount', 0)
            .ilike('note', `%[LID:${lessonId}]%`)
            .limit(1);
        if (error) {
            console.warn('[DB] hasLessonCostCharge:', error.message);
            return false;
        }
        return !!(data && data.length);
    },

    async recalculateBalance(studentId) {
        if (!studentId) return 0;
        await this._assertSelfOrTeacher(studentId);
        console.log('[DB] recalculateBalance:', String(studentId).slice(0, 8));
        const q = supabase.from('payment_ledger').select('amount, type').eq('student_id', studentId).neq('type', 'tax');
        const { data, error } = await this._withTimeout(q, 25000, 'recalculateBalance');
        if (error) throw error;
        const balance = (data || []).reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
        return balance;
    },

    // в”Ђв”Ђв”Ђ Online Lessons в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async getOnlineLessons() {
        console.log('[DB] getOnlineLessons...');
        const q = supabase.from('online_lessons').select('*').order('created_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getOnlineLessons');
        if (error && error.code !== '42P01') console.error('Error fetching online lessons:', error);
        return data || [];
    },

    async addOnlineLesson(lesson) {
        console.log('[DB] addOnlineLesson...');
        const q = supabase.from('online_lessons').insert([lesson]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addOnlineLesson');
        if (error) throw error;
        return data?.[0] || null;
    },

    /** Return the single persistent online-lesson container owned by a group (or null). */
    async getGroupContainer(groupId) {
        if (!groupId) return null;
        const q = supabase.from('online_lessons').select('*').eq('group_id', groupId).maybeSingle();
        const { data, error } = await this._withTimeout(q, 25000, 'getGroupContainer');
        if (error && error.code !== 'PGRST116' && error.code !== '42703') {
            console.error('Error fetching group container:', error);
        }
        return data || null;
    },

    /**
     * Get the group's permanent online-lesson container, creating an empty one if missing.
     * `seed` may provide { title, course } for a freshly created container.
     */
    async getOrCreateGroupContainer(groupId, seed = {}) {
        if (!groupId) throw new Error('groupId required');
        const existing = await this.getGroupContainer(groupId);
        if (existing) return existing;
        const lesson = {
            group_id: groupId,
            title: seed.title || 'Online Lesson',
            exercises: [],
            course: seed.course || '',
            module: '',
            lesson: '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        return await this.addOnlineLesson(lesson);
    },

    /**
     * Append bank exercise(s) to a group's permanent Online Lesson container.
     * Skips ids already present. Returns { container, added, skipped }.
     */
    async addExercisesToGroupOnlineLesson(groupId, exerciseIds, seed = {}) {
        if (!groupId) throw new Error('groupId required');
        const ids = [...new Set((exerciseIds || []).map(String).filter(Boolean))];
        if (!ids.length) throw new Error('No exercises to add');

        const container = await this.getOrCreateGroupContainer(groupId, seed);
        const existing = new Set(
            (container.exercises || [])
                .map((e) => String(e.exercise_id || ''))
                .filter(Boolean)
        );

        const toAdd = ids.filter((id) => !existing.has(id));
        const skipped = ids.filter((id) => existing.has(id));

        if (!toAdd.length) {
            return { container, added: [], skipped };
        }

        const genUid = () => (
            (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `uid_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
        );

        const newSlides = toAdd.map((exercise_id) => ({
            uid: genUid(),
            type: 'exercise',
            exercise_id,
            mode: 'shared',
            completed: false,
        }));

        const exercises = [...(container.exercises || []), ...newSlides];
        const updated = await this.updateOnlineLesson(container.id, {
            exercises,
            updated_at: new Date().toISOString(),
        });

        return {
            container: updated || { ...container, exercises },
            added: toAdd,
            skipped,
        };
    },

    async updateOnlineLesson(id, delta) {
        console.log('[DB] updateOnlineLesson:', String(id).slice(0, 8));
        const q = supabase.from('online_lessons').update(delta).eq('id', id).select();
        const { data, error } = await this._withTimeout(q, 25000, 'updateOnlineLesson');
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteOnlineLesson(id) {
        console.log('[DB] deleteOnlineLesson:', String(id).slice(0, 8));
        const q = supabase.from('online_lessons').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteOnlineLesson');
        if (error) throw error;
    },

    /** Reset an active online lesson room to idle (teacher dashboard Action Required). */
    async stopOnlineLessonRoom(groupId) {
        if (!groupId) throw new Error('groupId required');
        const q = supabase.from('online_lesson_rooms').upsert({
            group_id: groupId,
            active_lesson_id: null,
            status: 'idle',
            active_slide_index: 0,
            temporary_diamonds: {},
            homework_check_data: {},
            slide_state: {},
            individual_states: {},
            student_labels: {},
            updated_at: new Date().toISOString(),
        }, { onConflict: 'group_id' });
        const { error } = await this._withTimeout(q, 25000, 'stopOnlineLessonRoom');
        if (error) throw error;
    },

    // в”Ђв”Ђв”Ђ Slides в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    async getSlides() {
        console.log('[DB] getSlides...');
        const q = supabase.from('slides').select('*').order('created_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getSlides');
        if (error && error.code !== '42P01') console.error('Error fetching slides:', error);
        return data || [];
    },

    async getSlidesByIds(ids) {
        if (!ids || ids.length === 0) return [];
        console.log('[DB] getSlidesByIds:', ids.length);
        const q = supabase.from('slides').select('*').in('id', ids);
        const { data, error } = await this._withTimeout(q, 15000, `getSlidesByIds:${ids.length}`);
        if (error) return [];
        return data || [];
    },

    async addSlide(slide) {
        console.log('[DB] addSlide...');
        const payload = preparePayload('slides', slide);
        const q = supabase.from('slides').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addSlide');
        if (error) throw error;
        return data?.[0] || null;
    },

    async updateSlide(id, delta) {
        console.log('[DB] updateSlide:', String(id).slice(0, 8));
        const payload = preparePayload('slides', delta);
        const q = supabase.from('slides').update(payload).eq('id', id).select();
        const { data, error } = await this._withTimeout(q, 25000, 'updateSlide');
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteSlide(id) {
        console.log('[DB] deleteSlide:', String(id).slice(0, 8));
        const q = supabase.from('slides').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteSlide');
        if (error) throw error;
    },


    // в”Ђв”Ђв”Ђ Courses Syllabus в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
    
    async getSyllabusItems() {
        console.log('[DB] getSyllabusItems...');
        const q = supabase.from('syllabus_items').select('*').order('created_at', { ascending: false });
        const { data, error } = await this._withTimeout(q, 25000, 'getSyllabusItems');
        if (error && error.code !== '42P01') console.error('Error fetching syllabus items:', error);
        return data || [];
    },

    async getSyllabusItemsByCourseModule(course, module) {
        console.log('[DB] getSyllabusItemsByCourseModule...', course, module);
        const m = (module !== undefined && module !== null && module !== '') ? Number(module) : null;
        const c = (course || '').trim();

        // Primary: case-insensitive match (still indexed if course values are clean)
        let q = supabase.from('syllabus_items').select('*');
        if (c) q = q.ilike('course', c);
        if (m !== null) q = q.eq('module', m);
        q = q.order('lesson', { ascending: true }).order('order_index', { ascending: true });

        const { data, error } = await this._withTimeout(q, 25000, 'getSyllabusItemsByCourseModule');
        if (error && error.code !== '42P01') console.error('Error fetching syllabus items (scoped):', error);
        const rows = data || [];

        // Fallback: handle dirty course strings (e.g., trailing spaces) by querying only by module
        // and filtering client-side by normalized course.
        if (rows.length === 0 && c && m !== null) {
            const q2 = supabase
                .from('syllabus_items')
                .select('*')
                .eq('module', m)
                .order('lesson', { ascending: true })
                .order('order_index', { ascending: true });
            const { data: data2, error: error2 } = await this._withTimeout(q2, 25000, 'getSyllabusItemsByCourseModule:fallback');
            if (error2 && error2.code !== '42P01') console.error('Error fetching syllabus items (fallback):', error2);
            const norm = (s) => String(s || '').trim().toLowerCase();
            return (data2 || []).filter(r => norm(r.course) === norm(c));
        }

        return rows;
    },

    async addSyllabusItem(item) {
        console.log('[DB] addSyllabusItem...');
        const q = supabase.from('syllabus_items').insert([item]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addSyllabusItem');
        if (error) throw error;
        return data?.[0] || null;
    },

    async updateSyllabusItem(id, delta) {
        console.log('[DB] updateSyllabusItem:', String(id).slice(0, 8));
        const q = supabase.from('syllabus_items').update(delta).eq('id', id).select();
        const { data, error } = await this._withTimeout(q, 25000, 'updateSyllabusItem');
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteSyllabusItem(id) {
        console.log('[DB] deleteSyllabusItem:', String(id).slice(0, 8));
        const q = supabase.from('syllabus_items').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteSyllabusItem');
        if (error) throw error;
    },

    async deleteSyllabusLesson(course, module, lesson) {
        console.log('[DB] deleteSyllabusLesson:', course, module, lesson);
        const q = supabase.from('syllabus_lessons').delete().eq('course', course).eq('module', module).eq('lesson', lesson);
        const { error } = await this._withTimeout(q, 25000, 'deleteSyllabusLesson');
        if (error) throw error;
    },

    // в”Ђв”Ђв”Ђ Full Lesson Sections в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

    async getFullLessonSections(lessonId) {
        console.log('[DB] getFullLessonSections...', String(lessonId).slice(0, 8));
        const sectionQuery = supabase
            .from('full_lesson_sections')
            .select('*')
            .eq('lesson_id', lessonId)
            .order('order_index', { ascending: true });

        const { data: sections, error: sectionError } = await this._withTimeout(sectionQuery, 25000, 'getFullLessonSections');
        if (sectionError && sectionError.code !== '42P01') console.error('Error fetching full lesson sections:', sectionError);

        const rows = sections || [];
        if (rows.length === 0) return [];

        const sectionIds = rows.map(section => section.id);
        const itemsQuery = supabase
            .from('full_lesson_section_items')
            .select('*')
            .in('section_id', sectionIds)
            .order('order_index', { ascending: true });

        const { data: items, error: itemsError } = await this._withTimeout(itemsQuery, 25000, 'getFullLessonSections:items');
        if (itemsError && itemsError.code !== '42P01') console.error('Error fetching full lesson section items:', itemsError);

        const itemsBySection = (items || []).reduce((acc, row) => {
            if (!acc[row.section_id]) acc[row.section_id] = [];
            acc[row.section_id].push(row);
            return acc;
        }, {});

        return rows.map(section => ({
            ...section,
            items: itemsBySection[section.id] || [],
        }));
    },

    /** Lightweight: section titles only for many Full Lessons (Syllabus cards). */
    async getFullLessonSectionTitlesByLessonIds(lessonIds = []) {
        const ids = [...new Set((lessonIds || []).map(String).filter(Boolean))];
        if (ids.length === 0) return {};
        console.log('[DB] getFullLessonSectionTitlesByLessonIds:', ids.length);
        const q = supabase
            .from('full_lesson_sections')
            .select('id, lesson_id, title, order_index')
            .in('lesson_id', ids)
            .order('order_index', { ascending: true });
        const { data, error } = await this._withTimeout(q, 25000, 'getFullLessonSectionTitlesByLessonIds');
        if (error && error.code !== '42P01') {
            console.error('Error fetching full lesson section titles:', error);
            return {};
        }
        const byLesson = {};
        (data || []).forEach((row) => {
            const key = String(row.lesson_id);
            if (!byLesson[key]) byLesson[key] = [];
            byLesson[key].push({
                id: row.id,
                title: row.title || 'Section',
                order_index: row.order_index || 0,
            });
        });
        Object.values(byLesson).forEach((list) => list.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
        return byLesson;
    },

    async addFullLessonSection(section) {
        console.log('[DB] addFullLessonSection...');
        const q = supabase.from('full_lesson_sections').insert([section]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addFullLessonSection');
        if (error) throw error;
        return data?.[0] || null;
    },

    async updateFullLessonSection(id, delta) {
        console.log('[DB] updateFullLessonSection:', String(id).slice(0, 8));
        const q = supabase.from('full_lesson_sections').update(delta).eq('id', id).select();
        const { data, error } = await this._withTimeout(q, 25000, 'updateFullLessonSection');
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteFullLessonSection(id) {
        console.log('[DB] deleteFullLessonSection:', String(id).slice(0, 8));
        const q = supabase.from('full_lesson_sections').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteFullLessonSection');
        if (error) throw error;
    },

    async reorderFullLessonSections(idOrder = []) {
        // idOrder: array of section ids in the desired order → order_index 0..n
        const ids = (idOrder || []).filter(Boolean);
        if (ids.length === 0) return [];
        console.log('[DB] reorderFullLessonSections:', ids.length);
        const results = await Promise.all(
            ids.map((sectionId, idx) => this.updateFullLessonSection(sectionId, { order_index: idx }))
        );
        return results;
    },

    async replaceFullLessonSectionItems(sectionId, items = []) {
        console.log('[DB] replaceFullLessonSectionItems...', String(sectionId).slice(0, 8), items.length);

        const deleteQuery = supabase.from('full_lesson_section_items').delete().eq('section_id', sectionId);
        const { error: deleteError } = await this._withTimeout(deleteQuery, 25000, 'replaceFullLessonSectionItems:delete');
        if (deleteError) throw deleteError;

        const cleanedItems = (Array.isArray(items) ? items : [])
            .filter(item => item && item.content_type && item.content_id)
            .map((item, index) => ({
                section_id: sectionId,
                content_type: item.content_type,
                content_id: String(item.content_id),
                order_index: Number.isFinite(Number(item.order_index)) ? Number(item.order_index) : index,
                payload: (item.payload && typeof item.payload === 'object' && !Array.isArray(item.payload))
                    ? item.payload
                    : {},
            }));

        if (cleanedItems.length === 0) return [];

        const insertQuery = supabase.from('full_lesson_section_items').insert(cleanedItems).select();
        const { data, error } = await this._withTimeout(insertQuery, 25000, 'replaceFullLessonSectionItems:insert');
        if (error) throw error;
        return data || [];
    },

    // в”Ђв”Ђв”Ђ Self-Study Lesson Progress в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

    async getSelfStudyLessonProgress(userId, groupId) {
        console.log('[DB] getSelfStudyLessonProgress:', userId, groupId);
        const q = supabase
            .from('self_study_lesson_progress')
            .select('*')
            .eq('user_id', String(userId))
            .eq('group_id', String(groupId));
        const { data, error } = await this._withTimeout(q, 25000, 'getSelfStudyLessonProgress');
        if (error && error.code !== '42P01') console.error('Error fetching self-study lesson progress:', error);
        return data || [];
    },

    async unlockLesson(userId, lessonId, groupId) {
        console.log('[DB] unlockLesson:', userId, lessonId, groupId);
        const { data, error } = await supabase
            .from('self_study_lesson_progress')
            .upsert({
                user_id: String(userId),
                lesson_id: lessonId,
                group_id: String(groupId),
                is_unlocked: true,
                unlocked_at: new Date().toISOString(),
            }, {
                onConflict: 'user_id,lesson_id',
                ignoreDuplicates: false,
            })
            .select();
        if (error) throw error;
        return data?.[0] || null;
    },

    async completeLesson(userId, lessonId, groupId) {
        console.log('[DB] completeLesson:', userId, lessonId, groupId);
        const { data, error } = await supabase
            .from('self_study_lesson_progress')
            .update({
                is_completed: true,
                completed_at: new Date().toISOString(),
            })
            .eq('user_id', String(userId))
            .eq('lesson_id', lessonId)
            .eq('group_id', String(groupId))
            .select();
        if (error) throw error;
        return data?.[0] || null;
    },

    async getNextLesson(course, module, lesson) {
        console.log('[DB] getNextLesson:', course, module, lesson);
        const q = supabase
            .from('syllabus_lessons')
            .select('*')
            .eq('course', course)
            .gt('module', module)
            .order('module', { ascending: true })
            .order('lesson', { ascending: true })
            .limit(1);
        const { data, error } = await this._withTimeout(q, 25000, 'getNextLesson');
        if (error && error.code !== '42P01') console.error('Error fetching next lesson:', error);
        
        if (data && data.length > 0) return data[0];
        
        // If no lesson in next module, try next lesson in same module
        const q2 = supabase
            .from('syllabus_lessons')
            .select('*')
            .eq('course', course)
            .eq('module', module)
            .gt('lesson', lesson)
            .order('lesson', { ascending: true })
            .limit(1);
        const { data: data2, error: error2 } = await this._withTimeout(q2, 25000, 'getNextLesson:sameModule');
        if (error2 && error2.code !== '42P01') console.error('Error fetching next lesson in same module:', error2);
        return data2?.[0] || null;
    },

    async getFirstLesson(course) {
        console.log('[DB] getFirstLesson:', course);
        const q = supabase
            .from('syllabus_lessons')
            .select('*')
            .eq('course', course)
            .order('module', { ascending: true })
            .order('lesson', { ascending: true })
            .limit(1);
        const { data, error } = await this._withTimeout(q, 25000, 'getFirstLesson');
        if (error && error.code !== '42P01') console.error('Error fetching first lesson:', error);
        return data?.[0] || null;
    },

    async assignExercisesFromFullLessonSections(lessonId, userId, groupId, assignedExerciseIds, assignedBlockIds) {
        console.log('[DB] assignExercisesFromFullLessonSections:', String(lessonId).slice(0, 8));
        try {
            const sections = await this.getFullLessonSections(lessonId);
            const exerciseIds = new Set();
            const blockIds = new Set();

            for (const section of sections) {
                for (const item of section.items || []) {
                    if (item.content_type === 'exercise' && item.content_id) {
                        exerciseIds.add(String(item.content_id));
                    }
                    if (item.content_type === 'block' && item.content_id) {
                        blockIds.add(String(item.content_id));
                    }
                }
            }

            // Also need exercises and blocks data for assignment
            const [exercises, blocks] = await Promise.all([
                this.getExercises(),
                this.getBlocks(),
            ]);

            for (const exerciseId of exerciseIds) {
                if (assignedExerciseIds.has(String(exerciseId))) continue;
                const exercise = exercises.find(e => String(e.id) === String(exerciseId));
                if (exercise) {
                    await this.assignExercise({
                        exercise_id: exercise.id,
                        student_id: userId,
                        group_id: groupId,
                        status: 'assigned',
                    });
                    assignedExerciseIds.add(String(exerciseId));
                }
            }

            for (const blockId of blockIds) {
                if (assignedBlockIds.has(String(blockId))) continue;
                const block = blocks.find(b => String(b.id) === String(blockId));
                if (block) {
                    await this.assignExercise({
                        block_id: block.id,
                        student_id: userId,
                        group_id: groupId,
                        status: 'assigned',
                    });
                    assignedBlockIds.add(String(blockId));
                }
            }

            console.log('[DB] Assigned from Full Lesson sections:', { exercises: exerciseIds.size, blocks: blockIds.size });
        } catch (e) {
            console.error('[DB] Error assigning from Full Lesson sections:', e);
        }
    },

    async unlockFirstLessonForStudent(userId, groupId, courseName) {
        console.log('[DB] unlockFirstLessonForStudent:', userId, groupId, courseName);
        
        // Get first lesson
        const firstLesson = await this.getFirstLesson(courseName);
        if (!firstLesson) {
            console.log('[DB] No lessons found for course:', courseName);
            return;
        }

        // Unlock the first lesson
        await this.unlockLesson(userId, firstLesson.id, groupId);

        // Avoid duplicate assignment rows if this bootstrap is triggered more than once.
        const existingAssignments = await this.getAssignmentsForStudent(userId);
        const assignedExerciseIds = new Set(existingAssignments.map(a => String(a.exercise_id || '')).filter(Boolean));
        const assignedBlockIds = new Set(existingAssignments.map(a => String(a.block_id || '')).filter(Boolean));

        // Get syllabus items for the first lesson
        const [syllabusItems, exercises, blocks] = await Promise.all([
            this.getSyllabusItems ? this.getSyllabusItems() : Promise.resolve([]),
            this.getExercises(),
            this.getBlocks(),
        ]);

        const lessonItems = syllabusItems.filter(item =>
            item.course === courseName &&
            item.module === firstLesson.module &&
            item.lesson === firstLesson.lesson
        );

        // Assign each exercise/block from the first lesson
        for (const item of lessonItems) {
            if (item.exercise_ids && Array.isArray(item.exercise_ids)) {
                for (const exerciseId of item.exercise_ids) {
                    if (assignedExerciseIds.has(String(exerciseId))) continue;
                    const exercise = exercises.find(e => e.id === exerciseId);
                    if (exercise) {
                        await this.assignExercise({
                            exercise_id: exercise.id,
                            student_id: userId,
                            group_id: groupId,
                            status: 'assigned',
                        });
                        assignedExerciseIds.add(String(exerciseId));
                    }
                }
            }
            if (item.block_ids && Array.isArray(item.block_ids)) {
                for (const blockId of item.block_ids) {
                    if (assignedBlockIds.has(String(blockId))) continue;
                    const block = blocks.find(b => b.id === blockId);
                    if (block) {
                        await this.assignExercise({
                            block_id: block.id,
                            student_id: userId,
                            group_id: groupId,
                            status: 'assigned',
                        });
                        assignedBlockIds.add(String(blockId));
                    }
                }
            }
        }

        // Also assign exercises from Full Lesson sections
        await this.assignExercisesFromFullLessonSections(firstLesson.id, userId, groupId, assignedExerciseIds, assignedBlockIds);

        console.log('[DB] First lesson unlocked and exercises assigned for:', userId);
    },

    async getSyllabusConfig() {
        console.log('[DB] getSyllabusConfig...');
        const q = supabase.from('syllabus_config').select('*');
        const { data, error } = await this._withTimeout(q, 25000, 'getSyllabusConfig');
        if (error && error.code !== '42P01') console.error('Error fetching syllabus config:', error);
        return data || [];
    },

    async getSyllabusConfigByCourse(course) {
        console.log('[DB] getSyllabusConfigByCourse...', course);
        let q = supabase.from('syllabus_config').select('*');
        if (course) q = q.eq('course', course);
        const { data, error } = await this._withTimeout(q, 25000, 'getSyllabusConfigByCourse');
        if (error && error.code !== '42P01') console.error('Error fetching syllabus config (scoped):', error);
        return data || [];
    },

    async updateSyllabusConfig(payload) {
        console.log('[DB] updateSyllabusConfig:', payload.course, payload.module);
        const { data, error } = await supabase
            .from('syllabus_config')
            .upsert(payload, { onConflict: 'course, module' })
            .select();
        if (error) throw error;
        return data?.[0] || null;
    },

    async getSyllabusLessons() {
        console.log('[DB] getSyllabusLessons...');
        const q = supabase.from('syllabus_lessons').select('*');
        const { data, error } = await this._withTimeout(q, 60000, 'getSyllabusLessons');
        if (error && error.code !== '42P01') console.error('Error fetching syllabus lessons:', error);
        return data || [];
    },

    async getSyllabusLessonsByCourse(course) {
        console.log('[DB] getSyllabusLessonsByCourse:', course);
        const q = supabase.from('syllabus_lessons').select('*').eq('course', course);
        const { data, error } = await this._withTimeout(q, 60000, 'getSyllabusLessonsByCourse');
        if (error && error.code !== '42P01') console.error('Error fetching syllabus lessons by course:', error);
        return data || [];
    },

    async getSyllabusLessonsByCourseModule(course, module) {
        const m = (module !== undefined && module !== null && module !== '') ? Number(module) : null;
        const c = (course || '').trim();

        // Try primary query with retry mechanism to handle Supabase connection issues
        let rows = [];
        for (let attempt = 1; attempt <= 5; attempt++) {
            let q = supabase.from('syllabus_lessons').select('*');
            if (c) q = q.ilike('course', c);
            if (m !== null) q = q.eq('module', m);
            // Increased timeout to 60 seconds to handle Supabase connection warm-up
            const { data, error } = await this._withTimeout(q, 60000, 'getSyllabusLessonsByCourseModule');
            if (error && error.code !== '42P01') console.error('Error fetching syllabus lessons (scoped):', error);
            rows = data || [];

            if (rows.length > 0) {
                return rows;
            }

            // If retry is needed, wait a bit before next attempt
            if (attempt < 5) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }

        // If all primary query attempts return 0, try fallback without course filter
        if (c && m !== null) {
            const q2 = supabase.from('syllabus_lessons').select('*').eq('module', m);
            const { data: data2, error: error2 } = await this._withTimeout(q2, 25000, 'getSyllabusLessonsByCourseModule:fallback');
            if (error2 && error2.code !== '42P01') console.error('Error fetching syllabus lessons (fallback):', error2);
            const norm = (s) => String(s || '').trim().toLowerCase();
            const filtered = (data2 || []).filter(r => norm(r.course) === norm(c));
            return filtered;
        }

        return rows;
    },

    async upsertSyllabusLesson(payload) {
        console.log('[DB] upsertSyllabusLesson:', payload.course, payload.module, payload.lesson);
        const { data, error } = await supabase
            .from('syllabus_lessons')
            .upsert(payload, { onConflict: 'course, module, lesson' })
            .select();
        if (error) throw error;
        return data?.[0] || null;
    },

    /**
     * Move a syllabus lesson slot (module / lesson number) and cascade attributions.
     * Exercises with multiple attributions: only the matching slot is rewritten.
     * If the destination slot is occupied, the two lessons are swapped.
     */
    async remapLessonSlot(course, fromModule, fromLesson, toModule, toLesson) {
        const c = String(course || '').trim();
        const fromM = Number(fromModule);
        const fromL = Number(fromLesson);
        const toM = Number(toModule);
        const toL = Number(toLesson);
        const normCourseMatch = (a, b) =>
            String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

        if (!c || !Number.isFinite(fromM) || !Number.isFinite(fromL) || !Number.isFinite(toM) || !Number.isFinite(toL)) {
            throw new Error('Invalid lesson coordinates');
        }
        if (fromM === toM && fromL === toL) {
            return { swapped: false, counts: {} };
        }
        if (toM < 1 || toL < 1) {
            throw new Error('Module and lesson must be ≥ 1');
        }

        console.log('[DB] remapLessonSlot:', c, `${fromM}.${fromL}`, '→', `${toM}.${toL}`);

        const findLessonRow = async (module, lesson) => {
            const q = supabase
                .from('syllabus_lessons')
                .select('*')
                .eq('course', c)
                .eq('module', module)
                .eq('lesson', lesson)
                .maybeSingle();
            const { data, error } = await this._withTimeout(q, 25000, 'remapLessonSlot:find');
            if (error && error.code !== 'PGRST116') throw error;
            return data || null;
        };

        const updateLessonRowById = async (id, module, lesson) => {
            const q = supabase
                .from('syllabus_lessons')
                .update({ module, lesson })
                .eq('id', id)
                .select();
            const { data, error } = await this._withTimeout(q, 25000, 'remapLessonSlot:updateLesson');
            if (error) throw error;
            return data?.[0] || null;
        };

        const ensureLessonCount = async (module, lesson) => {
            const configs = await this.getSyllabusConfigByCourse(c);
            const config = (configs || []).find(
                (row) => normCourseMatch(row.course, c) && Number(row.module) === Number(module)
            );
            const current = config?.lesson_count ? Number(config.lesson_count) : 0;
            if (current >= lesson) return;
            await this.updateSyllabusConfig({
                course: c,
                module: Number(module),
                lesson_count: Number(lesson),
                title: config?.title || `Module ${module}`,
            });
        };

        const cascadeContent = async (fM, fL, tM, tL) => {
            const counts = {
                exercises: 0,
                blocks: 0,
                slides: 0,
                onlineLessons: 0,
                syllabusItems: 0,
                words: 0,
            };

            const [exercises, blocks, slides, onlineLessons, syllabusItems, words] = await Promise.all([
                this.getExercises(),
                this.getBlocks(),
                this.getSlides ? this.getSlides() : Promise.resolve([]),
                this.getOnlineLessons(),
                this.getSyllabusItems(),
                this.getGlobalWordsByCourse ? this.getGlobalWordsByCourse(c) : Promise.resolve([]),
            ]);

            const exerciseUpdates = [];
            for (const ex of exercises || []) {
                const delta = remapExerciseAttributionDelta(ex, c, fM, fL, tM, tL);
                if (!delta) continue;
                exerciseUpdates.push(this.updateExercise(ex.id, delta));
                counts.exercises += 1;
            }

            const simpleRemap = (rows, updateFn, key, opts = {}) => {
                const jobs = [];
                for (const row of rows || []) {
                    if (!normCourseMatch(row.course, c)) continue;
                    const delta = remapSimpleAttributionDelta(row, c, fM, fL, tM, tL, opts);
                    if (!delta) continue;
                    jobs.push(updateFn(row.id, delta));
                    counts[key] += 1;
                }
                return jobs;
            };

            const jobs = [
                ...exerciseUpdates,
                ...simpleRemap(blocks, (id, d) => this.updateBlock(id, d), 'blocks'),
                ...simpleRemap(slides, (id, d) => this.updateSlide(id, d), 'slides'),
                ...simpleRemap(onlineLessons, (id, d) => this.updateOnlineLesson(id, d), 'onlineLessons'),
                ...simpleRemap(syllabusItems, (id, d) => this.updateSyllabusItem(id, d), 'syllabusItems', { numeric: true }),
                ...simpleRemap(words, (id, d) => this.updateItem('globalWords', id, d), 'words'),
            ];

            if (jobs.length) await Promise.all(jobs);
            return counts;
        };

        const fromRow = await findLessonRow(fromM, fromL);
        const toRow = await findLessonRow(toM, toL);

        // Destination occupied if another lesson row, cards, or attributed exercises sit there
        let destHasContent = false;
        if (!toRow || (fromRow && toRow.id === fromRow.id)) {
            const [items, exs] = await Promise.all([
                this.getSyllabusItems(),
                this.getExercises(),
            ]);
            destHasContent = (items || []).some(
                (i) => normCourseMatch(i.course, c) && Number(i.module) === toM && Number(i.lesson) === toL
            );
            if (!destHasContent) {
                destHasContent = (exs || []).some((e) => {
                    if (sameLessonCoord(e, c, toM, toL)) return true;
                    return Array.isArray(e.attributions)
                        && e.attributions.some((a) => sameLessonCoord(a, c, toM, toL));
                });
            }
        }
        const occupied = !!(toRow && (!fromRow || toRow.id !== fromRow.id)) || destHasContent;

        await ensureLessonCount(toM, toL);
        if (occupied) await ensureLessonCount(fromM, fromL);

        if (occupied) {
            const tempModule = -900000 - Math.abs(fromM);
            const tempLesson = -900000 - Math.abs(fromL);

            if (fromRow) await updateLessonRowById(fromRow.id, tempModule, tempLesson);
            await cascadeContent(fromM, fromL, tempModule, tempLesson);

            if (toRow) await updateLessonRowById(toRow.id, fromM, fromL);
            await cascadeContent(toM, toL, fromM, fromL);

            if (fromRow) await updateLessonRowById(fromRow.id, toM, toL);
            await cascadeContent(tempModule, tempLesson, toM, toL);

            this.clearCache?.('exercises');
            this.clearCache?.('blocks');
            return { swapped: true, counts: { note: 'swapped' } };
        }

        if (fromRow) await updateLessonRowById(fromRow.id, toM, toL);
        const counts = await cascadeContent(fromM, fromL, toM, toL);

        this.clearCache?.('exercises');
        this.clearCache?.('blocks');
        return { swapped: false, counts };
    },

    // в”Ђв”Ђв”Ђ Group Study Plan в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

    async getGroupStudyPlanModules(groupId) {
        console.log('[DB] getGroupStudyPlanModules...');
        const q = supabase
            .from('group_study_plan_modules')
            .select('*')
            .eq('group_id', String(groupId));
        const { data, error } = await this._withTimeout(q, 25000, 'getGroupStudyPlanModules');
        if (error) {
            if (error.code === '42P01' || error.code === 'PGRST205') return [];
            console.error('Error fetching group study plan modules:', error);
        }
        return data || [];
    },

    async upsertGroupStudyPlanModule(payload) {
        console.log('[DB] upsertGroupStudyPlanModule:', payload.group_id, payload.course, payload.module);
        const { data, error } = await supabase
            .from('group_study_plan_modules')
            .upsert(payload, { onConflict: 'group_id, course, module' })
            .select();
        if (error) throw error;
        return data?.[0] || null;
    },

    async getGroupStudyPlanItems(groupId, course, module) {
        console.log('[DB] getGroupStudyPlanItems...');
        const c = (course || '').trim();
        const m = (module !== undefined && module !== null && module !== '') ? Number(module) : null;

        let q = supabase
            .from('group_study_plan_items')
            .select('*')
            .eq('group_id', String(groupId));
        if (c) q = q.ilike('course', c);
        if (m !== null) q = q.eq('module', m);
        q = q.order('order_index', { ascending: true });
        const { data, error } = await this._withTimeout(q, 25000, 'getGroupStudyPlanItems');
        if (error) {
            if (error.code === '42P01' || error.code === 'PGRST205') return [];
            console.error('Error fetching group study plan items:', error);
        }
        const rows = data || [];

        // Fallback: handle dirty course strings by querying without course constraint,
        // then filtering client-side by normalized course.
        if (rows.length === 0 && c && m !== null) {
            let q2 = supabase
                .from('group_study_plan_items')
                .select('*')
                .eq('group_id', String(groupId))
                .eq('module', m)
                .order('order_index', { ascending: true });
            const { data: data2, error: error2 } = await this._withTimeout(q2, 25000, 'getGroupStudyPlanItems:fallback');
            if (error2) {
                if (error2.code === '42P01' || error2.code === 'PGRST205') return [];
                console.error('Error fetching group study plan items (fallback):', error2);
            }
            const norm = (s) => String(s || '').trim().toLowerCase();
            return (data2 || []).filter(r => norm(r.course) === norm(c));
        }

        return rows;
    },

    async upsertGroupStudyPlanItem(payload) {
        console.log('[DB] upsertGroupStudyPlanItem:', payload.group_id, payload.course, payload.module);
        const { data, error } = await supabase
            .from('group_study_plan_items')
            .upsert(payload, { onConflict: 'group_id, course, module, syllabus_item_id' })
            .select();
        if (error) throw error;
        return data?.[0] || null;
    },

    async deleteGroupStudyPlanItem(id) {
        console.log('[DB] deleteGroupStudyPlanItem:', String(id).slice(0, 8));
        const q = supabase.from('group_study_plan_items').delete().eq('id', id);
        const { error } = await this._withTimeout(q, 25000, 'deleteGroupStudyPlanItem');
        if (error) throw error;
    },

    async reorderGroupStudyPlanItems(idOrder = []) {
        // idOrder: array of ids in the desired order, assigned 0..n
        const updates = (idOrder || []).map((id, idx) => ({ id, order_index: idx }));
        if (updates.length === 0) return;
        console.log('[DB] reorderGroupStudyPlanItems:', updates.length);
        const q = supabase.from('group_study_plan_items').upsert(updates).select();
        const { data, error } = await this._withTimeout(q, 25000, 'reorderGroupStudyPlanItems');
        if (error) throw error;
        return data || [];
    },

    async getGroupWords(groupId) {
        if (!groupId) return [];
        console.log('[DB] getGroupWords:', String(groupId).slice(0, 8));
        const q = supabase.from('group_words').select('*').eq('group_id', groupId);
        const { data, error } = await this._withTimeout(q, 25000, 'getGroupWords');
        if (error) {
            console.error('[DB] getGroupWords error:', error);
            return [];
        }
        return data || [];
    },

    async getGroupWordsByIds(ids = [], groupId = null) {
        const uniqueIds = [...new Set((ids || []).filter(Boolean).map(String))];
        if (uniqueIds.length === 0) return [];
        let q = supabase.from('group_words').select('*').in('id', uniqueIds);
        if (groupId) q = q.eq('group_id', groupId);
        const { data, error } = await this._withTimeout(q, 25000, 'getGroupWordsByIds');
        if (error) {
            console.error('[DB] getGroupWordsByIds error:', error);
            return [];
        }
        return data || [];
    },

    async getGlobalWordsByCourse(courseName) {
        if (!courseName) return [];
        console.log('[DB] getGlobalWordsByCourse:', courseName);
        const q = supabase.from('global_words').select('*').eq('course', courseName);
        const { data, error } = await this._withTimeout(q, 25000, 'getGlobalWordsByCourse');
        if (error) {
            console.error('[DB] getGlobalWordsByCourse error:', error);
            return [];
        }
        return data || [];
    },

    async getGlobalWordsByFilters(filters = {}) {
        const { course, module, lesson, theme, subtheme } = filters;
        if (!course) return [];
        console.log('[DB] getGlobalWordsByFilters:', { course, module, lesson, theme, subtheme });

        const mergeById = (lists) => {
            const byId = new Map();
            for (const list of lists) {
                for (const row of list || []) {
                    if (row?.id != null) byId.set(String(row.id), row);
                }
            }
            return [...byId.values()];
        };

        const placement = {
            course: String(course).trim(),
        };
        if (module != null && module !== '') placement.module = Number(module);
        if (lesson != null && lesson !== '') placement.lesson = Number(lesson);

        const [homeRes, alsoRes] = await Promise.all([
            this._paginateSelect(
                () => {
                    let q = supabase.from('global_words').select('*').eq('course', course);
                    if (module != null && module !== '') q = q.eq('module', module);
                    if (lesson != null && lesson !== '') q = q.eq('lesson', lesson);
                    return q;
                },
                { label: 'getGlobalWordsByFilters:home', timeoutMs: 45000 },
            ),
            // used_in cs this slot (.contains() mis-encodes jsonb array filters)
            this._paginateSelect(
                () => supabase
                    .from('global_words')
                    .select('*')
                    .filter('used_in', 'cs', JSON.stringify([placement])),
                { label: 'getGlobalWordsByFilters:used_in', timeoutMs: 45000 },
            ),
        ]);
        if (homeRes.error) {
            console.error('[DB] getGlobalWordsByFilters home error:', homeRes.error);
        }
        if (alsoRes.error) {
            // Column may not exist yet before migration — fall back to home only
            console.warn('[DB] getGlobalWordsByFilters used_in error:', alsoRes.error.message || alsoRes.error);
        }
        return mergeById([homeRes.data, alsoRes.error ? [] : alsoRes.data])
            .map(normalizeWordTaxonomy)
            .filter((word) => wordMatchesTheme(word, theme, subtheme));
    },

    /**
     * Load global words for DatabaseFilter course selection.
     * Includes home course and used_in course slots (same row id). Level is not used for fetch.
     * Paginated — PostgREST max_rows would otherwise truncate large courses.
     */
    async getGlobalWordsForCourseFilter(courseNames = []) {
        const names = (courseNames || []).map((c) => String(c || '').trim()).filter(Boolean);
        if (!names.length) return [];

        const mergeById = (lists) => {
            const byId = new Map();
            for (const list of lists) {
                for (const row of list || []) {
                    if (row?.id != null) byId.set(String(row.id), row);
                }
            }
            return [...byId.values()];
        };

        const lists = [];

        // Home course IN selected (paged)
        const homeRes = await this._paginateSelect(
            () => supabase.from('global_words').select('*').in('course', names),
            { label: 'getGlobalWordsForCourseFilter:home', timeoutMs: 45000 },
        );
        if (homeRes.error) console.error('[DB] getGlobalWordsForCourseFilter home:', homeRes.error);
        else lists.push(homeRes.data);

        // used_in cs { course } for each selected course (.contains() mis-encodes)
        for (const course of names) {
            const alsoRes = await this._paginateSelect(
                () => supabase
                    .from('global_words')
                    .select('*')
                    .filter('used_in', 'cs', JSON.stringify([{ course }])),
                { label: `getGlobalWordsForCourseFilter:used_in:${course}`, timeoutMs: 45000 },
            );
            if (alsoRes.error) {
                console.warn('[DB] used_in fetch:', alsoRes.error.message || alsoRes.error);
            } else {
                lists.push(alsoRes.data);
            }
        }

        return mergeById(lists);
    },

    async getWordsByIds(table, ids) {
        if (!ids?.length) return [];
        console.log('[DB] getWordsByIds:', table, ids.length);
        const q = supabase.from(table).select('*').in('id', ids);
        const { data, error } = await this._withTimeout(q, 25000, 'getWordsByIds');
        if (error) {
            console.error('[DB] getWordsByIds error:', error);
            return [];
        }
        return data || [];
    },

    async addGlobalWord(wordData) {
        const payload = withPrepositionFromEnglish(normalizeNewWordPayload('globalWords', wordData));
        console.log('[DB] addGlobalWord:', payload?.english);
        const q = supabase.from('global_words').insert([payload]).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addGlobalWord');
        if (error) throw error;
        return data?.[0] || null;
    },

    async addGlobalWords(wordsArray) {
        if (!wordsArray?.length) return [];
        const payloads = wordsArray.map((w) => withPrepositionFromEnglish(normalizeNewWordPayload('globalWords', w)));
        console.log('[DB] addGlobalWords:', payloads.length);
        const q = supabase.from('global_words').insert(payloads).select();
        const { data, error } = await this._withTimeout(q, 25000, 'addGlobalWords');
        if (error) throw error;
        return data || [];
    },

    /**
     * Bulk upsert global words from Google Sheets (or other import sources)
     * If word has an id and it exists -> update, otherwise insert as new
     */
    async syncGlobalWordsFromImport(wordsArray, options = {}) {
        if (!wordsArray?.length) return { inserted: 0, updated: 0, total: 0 };
        console.log('[DB] syncGlobalWordsFromImport:', wordsArray.length);

        const { skipExistingCheck = false, defaultCourse = "Imported" } = options;
        
        // Separate words with IDs (potential updates) from new words
        const wordsWithId = [];
        const wordsWithoutId = [];
        
        for (const word of wordsArray) {
            // Clean up the word object
            const source = normalizeWordTaxonomy(word);
            const cleanWord = {
                english: normalizeWordApostrophe(word.english?.trim() || ''),
                russian: word.russian?.trim() || '',
                course: word.course?.trim() || defaultCourse,
                module: word.module?.trim() || null,
                lesson: word.lesson?.trim() || null,
                theme_pairs: source.theme_pairs,
                subtypes: source.subtypes,
                theme: source.theme || null,
                subtheme: source.subtheme || null,
                subtype: source.subtype || null,
                transcription: word.transcription?.trim() || null,
                example: word.example?.trim() || null,
            };

            // Skip empty words
            if (!cleanWord.english && !cleanWord.russian) continue;

            if (word.id && String(word.id).trim()) {
                wordsWithId.push({ ...cleanWord, id: word.id });
            } else {
                wordsWithoutId.push(cleanWord);
            }
        }

        let inserted = 0;
        let updated = 0;

        // Process updates for words with IDs
        if (wordsWithId.length > 0) {
            // Check which IDs actually exist
            const ids = wordsWithId.map(w => w.id);
            const { data: existingWords } = await supabase
                .from('global_words')
                .select('id')
                .in('id', ids);
            
            const existingIds = new Set((existingWords || []).map(w => w.id));
            
            // Separate into updates and new inserts (IDs that don't exist)
            const toUpdate = wordsWithId.filter(w => existingIds.has(w.id));
            const toInsertWithFakeId = wordsWithId.filter(w => !existingIds.has(w.id));

            // Batch update existing words
            for (const word of toUpdate) {
                const { id, ...updateData } = word;
                const { error } = await supabase
                    .from('global_words')
                    .update(updateData)
                    .eq('id', id);
                if (!error) updated++;
                else console.warn('[DB] Failed to update word:', id, error);
            }

            // These will be inserted as new (without the fake ID)
            wordsWithoutId.push(...toInsertWithFakeId.map(({ id, ...rest }) => rest));
        }

        // Batch insert new words
        if (wordsWithoutId.length > 0) {
            // Add timestamps
            const wordsToInsert = wordsWithoutId.map(w => ({
                ...w,
                created_at: new Date().toISOString()
            }));

            // Insert in batches of 100 to avoid overwhelming the API
            const BATCH_SIZE = 100;
            for (let i = 0; i < wordsToInsert.length; i += BATCH_SIZE) {
                const batch = wordsToInsert.slice(i, i + BATCH_SIZE);
                const { data, error } = await supabase
                    .from('global_words')
                    .insert(batch)
                    .select('id');
                
                if (error) {
                    console.error('[DB] Batch insert error:', error);
                } else {
                    inserted += data?.length || 0;
                }
            }
        }

        console.log('[DB] syncGlobalWordsFromImport complete:', { inserted, updated, total: inserted + updated });
        return { inserted, updated, total: inserted + updated };
    },

    /**
     * Delete all global words matching a filter (useful for full sync replacement)
     */
    async deleteGlobalWordsByFilter(filter = {}) {
        console.log('[DB] deleteGlobalWordsByFilter:', filter);
        
        let q = supabase.from('global_words').delete();
        
        if (filter.course) q = q.eq('course', filter.course);
        if (filter.module) q = q.eq('module', filter.module);
        if (filter.lesson) q = q.eq('lesson', filter.lesson);
        if (filter.theme) q = q.eq('theme', filter.theme);
        
        const { error, count } = await q;
        if (error) throw error;
        return count || 0;
    },

    async ensureOtherCourse() {
        const { data: existing, error: findErr } = await supabase
            .from('courses')
            .select('id, name, modules')
            .ilike('name', OTHER_COURSE)
            .limit(1);
        if (findErr) throw findErr;
        if (existing?.length) {
            const row = existing[0];
            // Keep modules at 0 so the filter treats it as module-less
            if (Number(row.modules) !== 0) {
                await supabase.from('courses').update({ modules: 0 }).eq('id', row.id);
            }
            return row;
        }
        const { data: inserted, error: insertErr } = await supabase
            .from('courses')
            .insert({
                name: OTHER_COURSE,
                description: 'Words without curriculum attribution',
                color: '#94a3b8',
                modules: 0,
            })
            .select('id, name, modules')
            .single();
        if (insertErr) throw insertErr;
        return inserted;
    },

    /**
     * One-time / idempotent: rewrite empty/N/A/Student's Words/Imported → Other (no module/lesson)
     * on global_words, group_words, and user_progress.user_words JSON.
     */
    async migrateUnattributedWordAttributions() {
        await this.ensureOtherCourse();

        const needsFix = (w) => {
            if (!w) return false;
            if (isUnattributedCourse(w.course)) return true;
            if (isOtherCourse(w.course) && (w.module != null && w.module !== '' || w.lesson != null && w.lesson !== '')) {
                return true;
            }
            return false;
        };

        const patchWordTable = async (table) => {
            const { data, error } = await this._withTimeout(
                supabase.from(table).select('id, course, module, lesson'),
                60000,
                `migrateUnattributed:${table}:select`
            );
            if (error) throw error;
            const rows = (data || []).filter(needsFix);
            if (!rows.length) return { table, updated: 0 };

            const CHUNK = 200;
            let updated = 0;
            for (let i = 0; i < rows.length; i += CHUNK) {
                const chunk = rows.slice(i, i + CHUNK);
                await Promise.all(chunk.map(async (row) => {
                    const { error: upErr } = await supabase
                        .from(table)
                        .update({ course: OTHER_COURSE, module: null, lesson: null })
                        .eq('id', row.id);
                    if (upErr) throw upErr;
                    updated += 1;
                }));
            }
            return { table, updated };
        };

        const globalResult = await patchWordTable('global_words');
        // group_words may use composite PK — update by id when unique enough; fall back to id+course match
        let groupUpdated = 0;
        {
            const { data, error } = await this._withTimeout(
                supabase.from('group_words').select('id, group_id, course, module, lesson'),
                60000,
                'migrateUnattributed:group_words:select'
            );
            if (error) throw error;
            const rows = (data || []).filter(needsFix);
            const CHUNK = 200;
            for (let i = 0; i < rows.length; i += CHUNK) {
                const chunk = rows.slice(i, i + CHUNK);
                await Promise.all(chunk.map(async (row) => {
                    let q = supabase
                        .from('group_words')
                        .update({ course: OTHER_COURSE, module: null, lesson: null })
                        .eq('id', row.id);
                    if (row.group_id != null) q = q.eq('group_id', row.group_id);
                    const { error: upErr } = await q;
                    if (upErr) throw upErr;
                    groupUpdated += 1;
                }));
            }
        }

        let userUpdated = 0;
        let userRecords = 0;
        {
            const { data: progressRows, error } = await this._withTimeout(
                supabase.from('user_progress').select('user_id, user_words, progress'),
                120000,
                'migrateUnattributed:user_progress:select'
            );
            if (error) throw error;

            for (const rec of progressRows || []) {
                const columnWords = Array.isArray(rec.user_words) ? rec.user_words : [];
                const blob = rec.progress && typeof rec.progress === 'object' ? rec.progress : {};
                const blobWords = Array.isArray(blob.user_words) ? blob.user_words : [];
                const merged = sanitizeUserWords([...columnWords, ...blobWords]);
                let changed = false;
                const next = merged.map((w) => {
                    if (!needsFix(w)) return w;
                    changed = true;
                    userUpdated += 1;
                    return normalizeWordAttribution(w);
                });
                if (!changed) continue;
                userRecords += 1;
                const nextBlob = { ...blob, user_words: next };
                const { error: upErr } = await supabase
                    .from('user_progress')
                    .update({ user_words: next, progress: nextBlob })
                    .eq('user_id', rec.user_id);
                if (upErr) throw upErr;
            }
        }

        return {
            global: globalResult.updated,
            group: groupUpdated,
            userWords: userUpdated,
            userRecords,
        };
    },

    async saveReadingCart(cartWords, theme, groupId, courseName = OTHER_COURSE) {
        if (!cartWords?.length) return;

        const attribution = {
            course: OTHER_COURSE,
            module: null,
            lesson: null,
        };
        // Always store Parallel Reading cart words under Other (no module/lesson)
        void courseName;

        // 1. GLOBAL WORDS STEP: Only add words that don't exist under this theme
        const { data: existingGlobal } = await supabase
            .from('global_words')
            .select('english')
            .eq('theme', theme || null);
        
        const existingGlobalSet = new Set((existingGlobal || []).map(w => String(w.english).toLowerCase().trim()));
        const newGlobalWords = cartWords
            .filter(w => !existingGlobalSet.has(String(w.english).toLowerCase().trim()))
            .map(w => normalizeNewWordPayload('globalWords', {
                english: w.english,
                russian: w.russian,
                theme: theme || null,
                ...attribution,
                created_at: new Date().toISOString()
            }));

        if (newGlobalWords.length > 0) {
            const { error: globalErr } = await supabase.from('global_words').insert(newGlobalWords);
            if (globalErr) console.error('[DB] saveReadingCart: Global insert failed', globalErr);
        }

        // 2. GROUP WORDS STEP
        if (groupId) {
            const groupWords = cartWords.map(w => normalizeNewWordPayload('groupWords', {
                group_id: groupId,
                english: w.english,
                russian: w.russian,
                theme: theme || null,
                ...attribution,
                created_at: new Date().toISOString()
            }));
            const { error: groupErr } = await supabase.from('group_words').insert(groupWords);
            if (groupErr) console.error('[DB] saveReadingCart: Group insert failed', groupErr);

            // 3. STUDENT PROGRESS STEP: Add to all enrolled students
            const { data: group } = await supabase.from('groups').select('enrollments').eq('id', groupId).single();
            const studentIds = (group?.enrollments || []).map(e => e.userId).filter(Boolean);

            if (studentIds.length > 0) {
                await Promise.all(studentIds.map(async (uid) => {
                    try {
                        const progress = await this.getUserProgress(uid);
                        const existingWords = progress.user_words || [];
                        const existingIds = new Set(existingWords.map(w => getStableWordId(w)));
                        
                        const newStudentWords = cartWords
                            .filter(w => !existingIds.has(getStableWordId(w)))
                            .map(w => normalizeWordTaxonomy({
                                english: w.english,
                                russian: w.russian,
                                theme: theme || null,
                                ...attribution,
                                status: 'NOT STARTED',
                                statusDate: new Date().toISOString(),
                                dateAdded: new Date().toISOString()
                            }));
                        
                        if (newStudentWords.length > 0) {
                            await this.saveProgress(uid, {
                                ...progress,
                                user_words: [...existingWords, ...newStudentWords]
                            });
                        }
                    } catch (e) {
                        console.error(`[DB] saveReadingCart: Student update failed for ${uid}`, e);
                    }
                }));
            }
        }
    },

    // ─── Exam Checker ───────────────────────────────────────────────────────
    async getWritingCheckerRubrics() {
        const { data, error } = await supabase
            .from(getSBTable('writingCheckerRubrics'))
            .select('*')
            .order('title');
        if (error) throw error;
        return (data || []).map((r) => this._mapWritingCheckerRubric(r));
    },

    _mapWritingCheckerRubric(row) {
        if (!row) return row;
        let prompts = row.prompts || {};
        if (typeof prompts === 'string') {
            try {
                prompts = JSON.parse(prompts);
            } catch {
                prompts = {};
            }
        }
        return {
            id: row.id,
            slug: row.slug,
            title: row.title,
            courseId: row.course_id,
            courseName: row.course_name,
            priceTokens: row.price_tokens ?? row.price_coins,
            maxScore: row.max_score,
            criteria: row.criteria || [],
            prompts,
            active: row.active,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
        };
    },

    async saveWritingCheckerRubric(rubric) {
        const payload = {
            slug: rubric.slug,
            title: rubric.title,
            course_id: rubric.courseId || null,
            course_name: rubric.courseName || null,
            price_tokens: parseInt(rubric.priceTokens ?? rubric.priceCoins, 10) || 300,
            max_score: parseInt(rubric.maxScore, 10) || 14,
            criteria: rubric.criteria || [],
            prompts: JSON.parse(JSON.stringify(rubric.prompts || {})),
            active: rubric.active !== false,
            updated_at: new Date().toISOString(),
        };
        if (rubric.id) {
            const { data, error } = await supabase
                .from(getSBTable('writingCheckerRubrics'))
                .update(payload)
                .eq('id', rubric.id)
                .select()
                .single();
            if (error) throw error;
            return this._mapWritingCheckerRubric(data);
        }
        const { data, error } = await supabase
            .from(getSBTable('writingCheckerRubrics'))
            .insert(payload)
            .select()
            .single();
        if (error) throw error;
        return this._mapWritingCheckerRubric(data);
    },

    async getWritingCheckerChecks({ userId, source, limit = 100 } = {}) {
        let q = supabase
            .from(getSBTable('writingCheckerChecks'))
            .select('*, writing_checker_rubrics(title, slug)')
            .order('created_at', { ascending: false })
            .limit(limit);
        if (userId) q = q.eq('user_id', userId);
        if (source) q = q.eq('source', source);
        const { data, error } = await q;
        if (error) throw error;
        return (data || []).map((row) => this._mapWritingCheckerCheck(row));
    },

    _mapWritingCheckerCheck(row) {
        const rubricJoin = row.writing_checker_rubrics;
        return {
            id: row.id,
            userId: row.user_id,
            rubricId: row.rubric_id,
            rubricTitle: rubricJoin?.title || '',
            rubricSlug: rubricJoin?.slug || '',
            status: row.status,
            taskText: row.task_text,
            taskImageUrls: row.task_image_urls || [],
            answerText: row.answer_text,
            answerImageUrls: row.answer_image_urls || [],
            answerAudioUrls: row.answer_audio_urls || [],
            recognizedText: row.recognized_text,
            result: row.result,
            scoreFinal: row.score_final,
            maxScore: row.max_score,
            coinsSpent: row.coins_spent,
            tokensSpent: row.tokens_spent,
            source: row.source,
            errorMessage: row.error_message,
            tokensEstimate: row.tokens_estimate,
            isTest: row.is_test,
            createdAt: row.created_at,
            completedAt: row.completed_at,
        };
    },

    async getWritingCheckerCheckById(id) {
        const { data, error } = await supabase
            .from(getSBTable('writingCheckerChecks'))
            .select('*, writing_checker_rubrics(title, slug, criteria)')
            .eq('id', id)
            .single();
        if (error) throw error;
        return this._mapWritingCheckerCheck(data);
    },

    async createWritingCheckerCheck(row) {
        const payload = {
            user_id: row.userId,
            rubric_id: row.rubricId,
            status: row.status || 'processing',
            task_text: row.taskText || null,
            task_image_urls: row.taskImageUrls || [],
            answer_text: row.answerText || null,
            answer_image_urls: row.answerImageUrls || [],
            answer_audio_urls: row.answerAudioUrls || [],
            coins_spent: row.coinsSpent || 0,
            tokens_spent: row.tokensSpent || 0,
            source: row.source || (row.isTest ? 'test' : 'student'),
            is_test: !!row.isTest,
        };
        const { data, error } = await supabase
            .from(getSBTable('writingCheckerChecks'))
            .insert(payload)
            .select()
            .single();
        if (error) throw error;
        return this._mapWritingCheckerCheck(data);
    },

    async updateWritingCheckerCheck(id, patch) {
        const payload = {};
        if (patch.status != null) payload.status = patch.status;
        if (patch.recognizedText != null) payload.recognized_text = patch.recognizedText;
        if (patch.taskText != null) payload.task_text = patch.taskText;
        if (patch.result != null) payload.result = patch.result;
        if (patch.scoreFinal != null) payload.score_final = patch.scoreFinal;
        if (patch.maxScore != null) payload.max_score = patch.maxScore;
        if (patch.errorMessage != null) payload.error_message = patch.errorMessage;
        if (patch.tokensEstimate != null) payload.tokens_estimate = patch.tokensEstimate;
        if (patch.completedAt != null) payload.completed_at = patch.completedAt;
        const { data, error } = await supabase
            .from(getSBTable('writingCheckerChecks'))
            .update(payload)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return this._mapWritingCheckerCheck(data);
    },

    async deductPortalTokens(amount, referenceId = null, reason = 'Exam check') {
        const { data, error } = await this.supabase.rpc('deduct_portal_tokens', {
            p_amount: parseInt(amount, 10) || 0,
            p_reference_id: referenceId,
            p_reason: reason,
        });
        if (error) throw error;
        return data;
    },

    async refundPortalTokens(amount, referenceId = null, reason = 'Refund') {
        const { data, error } = await this.supabase.rpc('refund_portal_tokens', {
            p_amount: parseInt(amount, 10) || 0,
            p_reference_id: referenceId,
            p_reason: reason,
        });
        if (error) throw error;
        return data;
    },

    async getPortalTokenLedger({ limit = 200 } = {}) {
        const q = this.supabase
            .from('portal_token_ledger')
            .select('id, amount, balance_after, reason, reference_id, created_at')
            .order('created_at', { ascending: false })
            .limit(limit);
        const { data, error } = await this._withTimeout(q, 15000, 'getPortalTokenLedger');
        if (error) throw error;
        return data || [];
    },

    /** Deduct coins for a writing check (same ledger pattern as store). */
    async chargeCoinsForWritingCheck(userId, amount, label) {
        const progress = await this.getUserProgress(userId);
        const price = parseInt(amount, 10) || 0;
        if ((progress.coins || 0) < price) {
            throw new Error('Not enough coins!');
        }
        const newCoins = progress.coins - price;
        const date = new Date().toISOString().split('T')[0];
        const historyEntry = {
            id: `wc_${Date.now()}`,
            name: label || 'Writing check',
            amount: -price,
            source: 'writing_checker',
            dateAwarded: date,
        };
        await this.logToLedger(userId, 'coins', -price, label || 'Writing check', 'writing_checker');
        await this.saveProgress(userId, {
            ...progress,
            coins: newCoins,
            coins_history: [...(progress.coins_history || []), historyEntry],
        });
        return newCoins;
    },

    async refundCoinsForWritingCheck(userId, amount, label) {
        const progress = await this.getUserProgress(userId);
        const price = parseInt(amount, 10) || 0;
        const date = new Date().toISOString().split('T')[0];
        const historyEntry = {
            id: `wc_refund_${Date.now()}`,
            name: label || 'Writing check refund',
            amount: price,
            source: 'writing_checker',
            dateAwarded: date,
        };
        await this.logToLedger(userId, 'coins', price, label || 'Writing check refund', 'writing_checker');
        await this.saveProgress(userId, {
            ...progress,
            coins: (progress.coins || 0) + price,
            coins_history: [...(progress.coins_history || []), historyEntry],
        });
    },

    /** Rubrics active for a student based on enrolled course names / ids. */
    getWritingCheckerRubricsForStudent(rubrics, enrolledCourses) {
        const courseIds = new Set(
            (enrolledCourses || []).map((c) => String(c.courseId || c.id || '')).filter(Boolean)
        );
        const courseNames = new Set(
            (enrolledCourses || []).map((c) => (c.courseName || '').trim().toLowerCase()).filter(Boolean)
        );
        return (rubrics || []).filter((r) => {
            if (!r.active) return false;
            if (!r.courseId && !r.courseName) return false;
            if (r.courseId && courseIds.has(String(r.courseId))) return true;
            const rn = (r.courseName || '').trim().toLowerCase();
            return rn && courseNames.has(rn);
        });
    },

    async uploadWritingCheckerImage(userId, file) {
        const fileExt = (file.name || 'jpg').split('.').pop();
        const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
        const filePath = `writing-checker/${fileName}`;
        const { error } = await supabase.storage.from('media').upload(filePath, file);
        if (error) throw error;
        const { data } = await supabase.storage.from('media').createSignedUrl(filePath, 31536000);
        return data?.signedUrl || null;
    },

    /** Upload any media (image/audio) and return both URL and storage path. */
    async uploadWritingCheckerMedia(userId, file) {
        const fileExt = (file.name || 'bin').split('.').pop();
        const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
        const filePath = `writing-checker/${fileName}`;
        const { error } = await supabase.storage.from('media').upload(filePath, file);
        if (error) throw error;
        const { data } = await supabase.storage.from('media').createSignedUrl(filePath, 31536000);
        return { url: data?.signedUrl || null, path: filePath };
    },

    /** Delete a previously uploaded writing-checker media file by storage path. */
    async deleteWritingCheckerMedia(path) {
        if (!path) return;
        const { error } = await supabase.storage.from('media').remove([path]);
        if (error) throw error;
    },

    /** Fresh signed URL for a stored writing-checker media link (private bucket). */
    async getFreshWritingCheckerMediaUrl(storedUrl, expiresInSec = 3600) {
        const path = extractMediaStoragePath(storedUrl);
        if (!path) return storedUrl;
        const { data, error } = await supabase.storage
            .from('media')
            .createSignedUrl(path, expiresInSec);
        if (error) throw error;
        return data?.signedUrl || storedUrl;
    },

    /** Download via authenticated Storage API — reliable playback in browser (blob URL). */
    async downloadWritingCheckerMediaAsBlobUrl(storedUrlOrPath) {
        const path =
            storedUrlOrPath?.includes('/storage/v1/object/')
                ? extractMediaStoragePath(storedUrlOrPath)
                : storedUrlOrPath;
        if (!path) throw new Error('Invalid writing-checker media URL');
        const { data, error } = await supabase.storage.from('media').download(path);
        if (error) throw error;
        return URL.createObjectURL(data);
    },

    /**
     * Download a `media` bucket object as a Blob (bypasses browser CORS on signed/public URLs).
     * Accepts a full storage URL or a bare object path.
     */
    async downloadMediaBlob(urlOrPath) {
        if (!urlOrPath) return null;
        const raw = String(urlOrPath).trim();
        const path = /\/storage\/v1\/object\//i.test(raw)
            ? extractMediaStoragePath(raw)
            : raw.replace(/^\/+/, '');
        if (!path || /https?:\/\//i.test(path)) return null;
        const { data, error } = await supabase.storage.from('media').download(path);
        if (error) {
            console.warn('[DB] downloadMediaBlob:', error.message || error);
            return null;
        }
        return data || null;
    },
};

export default db;
